import React, { useState, useEffect, useRef, useMemo } from 'react'
import {
    Box,
    Typography,
    Button,
    TextField,
    Paper,
    Link,
    InputAdornment,
    IconButton,
    AppBar,
    Toolbar,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    FormHelperText,
    Grow,
    Fade,
    Slide,
    Zoom,
    Grid,
    Chip,
    Stepper,
    Step,
    StepLabel,
    Alert,
    Snackbar,
    CircularProgress,
    Radio,
    FormControlLabel,
    RadioGroup,
    darken,
    Autocomplete,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material'
import {
    Visibility,
    VisibilityOff,
    Person,
    CameraAlt,
    Delete,
    CloudUpload,
    CheckCircle,
    Add,
    ArrowForward,
    AutoAwesome,
    PictureAsPdf,
    Security,
    Verified,
    Lock,
    Cancel,
    KeyboardArrowDown,
} from '@mui/icons-material'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { format as formatDate } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import api from '@/services/api'
import SubscriptionPlans from '@/components/SubscriptionPlans'
import PaymentModal from '@/components/contracts/PaymentModal'
import { SubscriptionPlan, MembershipPlan } from '@/types/subscription'
import { membershipApi } from '@/services/api'
import { initiateStripeCheckout } from '@/services/stripeService'
import { useDebounce } from '@/hooks/useDebounce'
import { AddressAutocomplete } from '@/components/AddressAutocomplete'
import {
    isValidEmail,
    isValidPhone,
    isValidSSN,
    isValidEIN,
    ValidationRules,
    calculatePasswordStrength,
    PasswordStrength,
} from '@/utils/validation'
import PasswordStrengthMeter from '@/components/ui/PasswordStrengthMeter'
import PhoneInput from '@/components/ui/PhoneInput'
import LocationSelector from '@/components/ui/LocationSelector'
import { getUSPhoneErrorMessage } from '@/utils/phoneUtils'
import ProgressStepper from './components/ProgressStepper'
import AboutYouStep from './steps/AboutYouStep'
import ChooseSubscriptionStep from './steps/ChooseSubscriptionStep'
import ConnectBankStep from './steps/ConnectBankStep'
import AgencyDocumentsSection from './steps/AgencyDocumentsSection'
import OnboardingNavigation from './components/OnboardingNavigation'
import { bi } from 'node_modules/@fullcalendar/core/internal-common'
import { log } from 'console'
import AgencyPaymentModal from './components/AgencyPaymentModal'

const getBillingPhoneValidationMessage = (value: string): string => {
    const trimmed = value.trim()
    if (!trimmed) {
        return 'Billing phone number is required'
    }
    return getUSPhoneErrorMessage(value)
}

const THERAPIST_CREATE_ACCOUNT_DRAFT_KEY =
    'therapist_create_account_draft_v1' as const

const THERAPIST_CREATE_ACCOUNT_DRAFT_SECRET =
    'therapist_create_account_password_v1' as const

const encodeBase64 = (bytes: Uint8Array): string => {
    let binary = ''
    bytes.forEach(b => {
        binary += String.fromCharCode(b)
    })
    return btoa(binary)
}

const decodeBase64ToArrayBuffer = (value: string): ArrayBuffer => {
    const binary = atob(value)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i)
    }
    return bytes.buffer.slice(0)
}

const getPasswordEncryptionKey = async (): Promise<CryptoKey> => {
    const cryptoApi = window.crypto
    const encoder = new TextEncoder()
    const baseKey = await cryptoApi.subtle.importKey(
        'raw',
        encoder.encode(THERAPIST_CREATE_ACCOUNT_DRAFT_SECRET),
        { name: 'PBKDF2' },
        false,
        ['deriveKey']
    )

    return cryptoApi.subtle.deriveKey(
        {
            name: 'PBKDF2',
            salt: encoder.encode('therapist-create-account'),
            iterations: 100000,
            hash: 'SHA-256',
        },
        baseKey,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt']
    )
}

const encryptDraftText = async (value: string): Promise<string> => {
    if (!value) return ''
    const cryptoApi = window.crypto
    const encoder = new TextEncoder()
    const iv = cryptoApi.getRandomValues(new Uint8Array(12))
    const key = await getPasswordEncryptionKey()
    const encrypted = await cryptoApi.subtle.encrypt(
        { name: 'AES-GCM', iv },
        key,
        encoder.encode(value)
    )
    return `${encodeBase64(iv)}:${encodeBase64(new Uint8Array(encrypted))}`
}

const decryptDraftText = async (encryptedValue: string): Promise<string> => {
    if (!encryptedValue) return ''
    const [ivB64, payloadB64] = encryptedValue.split(':')
    if (!ivB64 || !payloadB64) return ''
    const cryptoApi = window.crypto
    const key = await getPasswordEncryptionKey()
    const decrypted = await cryptoApi.subtle.decrypt(
        { name: 'AES-GCM', iv: decodeBase64ToArrayBuffer(ivB64) },
        key,
        decodeBase64ToArrayBuffer(payloadB64)
    )
    return new TextDecoder().decode(decrypted)
}

interface Specialty {
    _id: string
    name: string
    description: string
}

interface Degree {
    _id: string
    name: string
    discription: string
    isActive: boolean
}

interface LicenseType {
    _id: string
    name: string
    description: string
    isDeleted: boolean
    required?: 'mandatory' | 'non_mandatory'
    task: string
}

interface PayerAltName {
    _id: string
    alt_payer_name: string
}

interface Payer {
    _id: string
    payer_name: string
    payer_state: string
    payer_type: string
    workers_comp: string
    ub_claims: string
    eligibility: string
    auto: string
    attachment: string
    dent_claims: string
    era: string
    payer_alt_names: PayerAltName[]
    avg_era_enroll_days: number
    secondary_support: string
    payerid: string
    '1500_claims': string
    status: string
    __v: number
    createdAt: string
    updatedAt: string
}

interface BoardOption {
    _id: string
    name: string
}

interface OnboardingData {
    // Step 1: Service Information
    services: string[]
    degrees: string[]
    genderIdentity: string
    sessionFee: string
    sessionLength: string
    insuranceAccepted: string[]
    yearsOfExperience: string
    pronouns: string
    // Board certification (all optional - not all providers have board certificates)
    noOfProvider: string
    boardCertificationNo: string
    boardSpeciality: string
    boardName: string
    boardSubSpeciality: string
    // Step 2: About you
    profilePicture: string | null
    professionalBio: string
    // Step 3: NPI & License verification
    npiNumber: string
    ssn: string
    licenseNpi: string
    licenseStatus: string
    socialSecurityNumber: string
    licenseType: string
    licenseState: string
    licenseNumber: string
    licenseExpirationYear: string
    licenseExpirationDate: string
    issueDate: string
    licenseFileUrl: string
    licenseFileName?: string
    licenseFilePreview?: string
}

interface SubscriptionDetails {
    orderId: string
    client_secret: string
    customerId: string
    ephemeralKey?: string
    discountedPrice?: number
}

type TherapistCreateAccountDraftV1 = {
    v: 1
    savedAt: number
    currentStep: number
    showOnboarding: boolean
    encryptedPassword?: string
    encryptedConfirmPassword?: string
    encryptedProviderSsn?: string
    // Subscription progress (so refresh/return keeps correct state)
    therapistId?: string | null
    selectedPlan?: MembershipPlan | null
    billingCycle?: 'monthly' | 'yearly'
    discountedPrice?: number | null
    subscriptionDetails?: SubscriptionDetails | null
    subscriptionPaymentSuccess?: boolean
    formData: Record<string, any>
    onboardingData: Record<string, any>
    additionalLicenses: Array<Record<string, any>>
}

const AgencySignUp: React.FC = () => {
    const navigate = useNavigate()
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>(
        calculatePasswordStrength('')
    )
    const [formData, setFormData] = useState({
        agencyName: '',
        firstName: '',
        lastName: '',
        phoneNumber: '',
        email: '',
        password: '',
        confirmPassword: '',
        gender: '',
        dateOfBirth: null as Date | null,
        address: '',
        state: '',
        city: '',
        country: '',
        zipCode: '',
        latitude: undefined as number | undefined,
        longitude: undefined as number | undefined,
        noOfProvider: '',
        // New billing fields
        billingType: 'individual',
        billingNpi: '',
        taxId: '',
        companyName: '',
        ssn: '',
        billingPhone: '',
        billingAddressLine: '',
        billingState: '',
        billingCity: '',
        billingZipCode: '',
        billingCountry: '',
        addressLine2: '',
        licenseDocument: null as File | null,
        // New fields for redesigned "Create your account" form
        dbaName: '',
        ein: '',
        npiOrganization: '',
        agencyLicenseNumber: '',
        agencyLicenseExpiration: null as Date | null,
        stateOfOperations: [] as string[],
        businessEmail: '',
        businessPhoneNumber: '',
        website: '',
        professionalLiabilityInsurance: '',
        generalLiabilityInsurance: '',
        // Corporate documents / clinical accreditation uploads
        businessLicenseUrl: '',
        businessLicenseName: '',
        agencyLicenseUrl: '',
        agencyLicenseName: '',
        npiCertificateUrl: '',
        npiCertificateName: '',
        w9FormUrl: '',
        w9FormName: '',
        certificateOfInsuranceUrl: '',
        certificateOfInsuranceName: '',
        clinicalAccreditationUrl: '',
        clinicalAccreditationName: '',
    })
    const primaryColor = "#7A6FA1"
    // Shared styles for the redesigned "Create your account" form fields
    const accountFieldSx = {
        '& .MuiOutlinedInput-root': {
            bgcolor: 'white',
            borderRadius: 2,
            transition: 'all 0.3s ease',
            '& fieldset': { borderColor: 'grey.300' },
            '&:hover fieldset': { borderColor: 'grey.400' },
            '&.Mui-focused fieldset': { borderColor: '#574F75', borderWidth: 2 },
        },
        '& .MuiInputBase-input': { py: 1.5, fontSize: '0.875rem' },
    }
    const accountFieldLabelSx = {
        color: 'grey.700',
        mb: 1,
        fontWeight: 500,
        fontSize: '0.875rem',
    } as const
    const accountDatePickerSx = {
        '& .MuiPickersInputBase-root': {
            height: '48px',
            bgcolor: 'white',
            borderRadius: 2,
            transition: 'all 0.3s ease',
            '& fieldset': { borderColor: 'grey.300' },
            '&:hover fieldset': { borderColor: 'grey.400' },
            '&.Mui-focused fieldset': { borderColor: '#574F75 !important', borderWidth: 2 },
        },
        '& .MuiInputBase-input': { fontSize: '0.875rem' },
    }


    const [currentStep, setCurrentStep] = useState(0)
    const formContainerRef = useRef<HTMLDivElement>(null)
    const formPaperRef = useRef<HTMLDivElement>(null)
    const [showOnboarding, setShowOnboarding] = useState(false)
    const [showSuccessAlert, setShowSuccessAlert] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false)
    const [hasAutoOpenedSubscriptionDialog, setHasAutoOpenedSubscriptionDialog] =
        useState(false)
    const [noOfProviderError, setNoOfProviderError] = useState('')
    const [selectedPlan, setSelectedPlan] = useState<MembershipPlan | null>(null)
    const [isNpiVerifying, setIsNpiVerifying] = useState(false)
    const [isNpiVerified, setIsNpiVerified] = useState(false)
    const [npiVerificationError, setNpiVerificationError] = useState('')
    // Billing NPI verification state
    const [isBillingNpiVerifying, setIsBillingNpiVerifying] = useState(false)
    const [isBillingNpiVerified, setIsBillingNpiVerified] = useState(false)
    const [billingNpiVerificationError, setBillingNpiVerificationError] =
        useState('')
    // Email verification state
    const [isVerifyingEmail, setIsVerifyingEmail] = useState(false)
    const [emailVerificationError, setEmailVerificationError] = useState('')
    // Phone number validation state
    const [phoneNumberError, setPhoneNumberError] = useState('')
    const [isPhoneNumberTouched, setIsPhoneNumberTouched] = useState(false)
    const [billingPhoneError, setBillingPhoneError] = useState('')
    const [isBillingPhoneTouched, setIsBillingPhoneTouched] = useState(false)
    const [taxIdError, setTaxIdError] = useState('')
    const [companyNameError, setCompanyNameError] = useState('')
    // Tax ID (SSN/EIN) validation state
    const [ssnIdError, setSsnError] = useState('')
    // Password confirmation validation state
    const [confirmPasswordError, setConfirmPasswordError] = useState('')
    const [genderError, setGenderError] = useState('')
    const [dateOfBirthError, setDateOfBirthError] = useState('')
    const [npiStepErrors, setNpiStepErrors] = useState<Record<string, string>>({})
    // Board certification errors
    const [boardCertificationNoError, setBoardCertificationNoError] = useState('')
    const [boardSpecialityError, setBoardSpecialityError] = useState('')
    const [boardNameError, setBoardNameError] = useState('')
    const [servicesError, setServicesError] = useState('')
    const [degreesError, setDegreesError] = useState('')
    const [insuranceError, setInsuranceError] = useState('')
    const [yearsOfExperienceError, setYearsOfExperienceError] = useState('')
    // New "Create your account" redesign - validation state
    const [dbaNameError, setDbaNameError] = useState('')
    const [einError, setEinError] = useState('')
    const [businessEmailError, setBusinessEmailError] = useState('')
    const [businessPhoneError, setBusinessPhoneError] = useState('')
    const [isBusinessPhoneTouched, setIsBusinessPhoneTouched] = useState(false)
    const [stateOfOperationsError, setStateOfOperationsError] = useState('')
    const [stateOfOperationsSelectOpen, setStateOfOperationsSelectOpen] =
        useState(false)
    // Secondary administrators (optional)
    const [secondaryAdministrators, setSecondaryAdministrators] = useState<
        Array<{
            id: string
            firstName: string
            lastName: string
            role: string
            email: string
        }>
    >([])
    const [showAddAdminDialog, setShowAddAdminDialog] = useState(false)
    const [newAdminDraft, setNewAdminDraft] = useState({
        firstName: '',
        lastName: '',
        role: '',
        email: '',
    })
    const [newAdminErrors, setNewAdminErrors] = useState<Record<string, string>>(
        {}
    )
    // Corporate documents / clinical accreditation upload state
    const [uploadingDocumentField, setUploadingDocumentField] = useState<
        string | null
    >(null)
    // Track if we're deleting a chip to prevent Select from opening
    const [isDeletingChip, setIsDeletingChip] = useState(false)
    const [servicesSelectOpen, setServicesSelectOpen] = useState(false)
    const [providedServicesSelectOpen, setProvidedServicesSelectOpen] = useState(false)
    const [degreesSelectOpen, setDegreesSelectOpen] = useState(false)
    // License file upload loading states
    const [isUploadingLicense, setIsUploadingLicense] = useState(false)
    const [isUploadingAdditionalLicenses, setIsUploadingAdditionalLicenses] =
        useState<Record<number, boolean>>({})
    // Additional license cards state
    const [additionalLicenses, setAdditionalLicenses] = useState<
        Array<{
            licenseType: string
            licenseNpi: string
            licenseState: string
            licenseNumber: string
            licenseExpirationDate: string
            issueDate: string
            licenseStatus: string
            licenseFileUrl: string
            licenseFileName?: string
            licenseFilePreview?: string
        }>
    >([])

    const prefildlicenseNpi = localStorage.getItem('providerNpi') || ''

    const handleAdditionalLicenseChange = (
        index: number,
        field:
            | 'licenseType'
            | 'licenseNpi'
            | 'licenseState'
            | 'licenseNumber'
            | 'ssn'
            | 'issueDate'
            | 'licenseExpirationDate'
            | 'licenseStatus',
        value: string
    ) => {
        setAdditionalLicenses(prev => {
            const next = [...prev]
            next[index] = { ...next[index], [field]: value }
            return next
        })
        // Clear NPI error when license type or NPI changes
        if (field === 'licenseType' || field === 'licenseNpi') {
            clearNpiFieldError(`additionalLicenseNpi_${index}`)
        }
    }

    const handleAdditionalLicenseFile = async (
        index: number,
        file: File | null
    ) => {
        if (!file) return
        try {
            // Set loading state
            setIsUploadingAdditionalLicenses(prev => ({ ...prev, [index]: true }))

            // Clean up previous preview URL if exists
            setAdditionalLicenses(prev => {
                const next = [...prev]
                if (next[index]?.licenseFilePreview) {
                    URL.revokeObjectURL(next[index].licenseFilePreview!)
                }
                return next
            })

            const uploadRes = await membershipApi.uploadLicenseFile(file)
            const url = uploadRes?.files?.[0]?.signedUrl || uploadRes?.fileUrl || ''
            if (!url) {
                setIsUploadingAdditionalLicenses(prev => ({ ...prev, [index]: false }))
                return
            }

            // Create preview URL for images
            let previewUrl = ''
            if (file.type.startsWith('image/')) {
                previewUrl = URL.createObjectURL(file)
            }

            setAdditionalLicenses(prev => {
                const next = [...prev]
                next[index] = {
                    ...next[index],
                    licenseFileUrl: url,
                    licenseFileName: file.name,
                    licenseFilePreview: previewUrl,
                }
                return next
            })
        } catch (e) {
            console.error('Error uploading additional license:', e)
        } finally {
            setIsUploadingAdditionalLicenses(prev => ({ ...prev, [index]: false }))
        }
    }

    // Payment flow state
    const [showPaymentModal, setShowPaymentModal] = useState(false)
    const [subscriptionDetails, setSubscriptionDetails] =
        useState<SubscriptionDetails | null>(null)
    const [therapistId, setTherapistId] = useState<string | null>(null)
    const [discountedPrice, setDiscountedPrice] = useState<number | null>(null)
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>(
        'monthly'
    )
    const [isProcessingPayment, setIsProcessingPayment] = useState(false)
    const [paymentError, setPaymentError] = useState<string | null>(null)
    const [isConnectingBank, setIsConnectingBank] = useState(false)
    const [subscriptionPaymentSuccess, setSubscriptionPaymentSuccess] =
        useState(false)

    // Specialties state
    const [specialties, setSpecialties] = useState<Specialty[]>([])
    const [isLoadingSpecialties, setIsLoadingSpecialties] = useState(false)

    // Provided Services state
    interface ProvidedService {
        id: number
        name: string
    }
    const [providedServices, setProvidedServices] = useState<ProvidedService[]>([])
    const [isLoadingProvidedServices, setIsLoadingProvidedServices] = useState(false)

    // Degrees state
    const [degrees, setDegrees] = useState<Degree[]>([])
    const [isLoadingDegrees, setIsLoadingDegrees] = useState(false)

    // Payers state
    const [payers, setPayers] = useState<Payer[]>([])
    const [isLoadingPayers, setIsLoadingPayers] = useState(false)
    const [payerSearchInput, setPayerSearchInput] = useState('')
    const [payerSearchOptions, setPayerSearchOptions] = useState<Payer[]>([])
    const [isSearchingPayers, setIsSearchingPayers] = useState(false)
    const [selectedPayers, setSelectedPayers] = useState<Payer[]>([])

    // Board certification search state
    const [boardNameSearch, setBoardNameSearch] = useState('')
    const [boardSpecialitySearch, setBoardSpecialitySearch] = useState('')
    const [boardSubSpecialitySearch, setBoardSubSpecialitySearch] = useState('')
    const [boardNameOptions, setBoardNameOptions] = useState<BoardOption[]>([])
    const [boardSpecialityOptions, setBoardSpecialityOptions] = useState<
        BoardOption[]
    >([])
    const [boardSubSpecialityOptions, setBoardSubSpecialityOptions] = useState<
        BoardOption[]
    >([])
    const [isSearchingBoardName, setIsSearchingBoardName] = useState(false)
    const [isSearchingBoardSpeciality, setIsSearchingBoardSpeciality] =
        useState(false)
    const [isSearchingBoardSubSpeciality, setIsSearchingBoardSubSpeciality] =
        useState(false)

    // States state
    const [states, setStates] = useState<Array<{ _id: string; name: string }>>([])
    const [isLoadingStates, setIsLoadingStates] = useState(false)

    // License types state
    const [licenseTypes, setLicenseTypes] = useState<LicenseType[]>([])
    const [isLoadingLicenseTypes, setIsLoadingLicenseTypes] = useState(false)

    // Profile photo upload state
    const [isUploadingProfilePhoto, setIsUploadingProfilePhoto] = useState(false)

    const [onboardingData, setOnboardingData] = useState<OnboardingData>({
        services: [],
        degrees: [],
        genderIdentity: 'Male',
        sessionFee: '',
        sessionLength: '',
        insuranceAccepted: [],
        yearsOfExperience: '',
        pronouns: '',
        boardCertificationNo: '',
        boardSpeciality: '',
        boardName: '',
        boardSubSpeciality: '',
        profilePicture: null,
        professionalBio: '',
        npiNumber: '',
        noOfProvider: '',
        ssn: '',
        socialSecurityNumber: '',
        licenseNpi: localStorage.getItem('providerNpi') || '',
        licenseStatus: 'Active',
        licenseType: '',
        licenseState: '',
        licenseNumber: '',
        licenseExpirationYear: '',
        licenseExpirationDate: '',
        issueDate: '',
        licenseFileUrl: '',
        licenseFileName: '',
        licenseFilePreview: '',
    })

    const restoreDraftOnceRef = useRef(false)
    const draftPersistenceEnabledRef = useRef(true)

    const draftSnapshot = useMemo<TherapistCreateAccountDraftV1>(() => {
        const {
            password: _password,
            confirmPassword: _confirmPassword,
            ssn: _ssn,
            taxId: _taxId,
            licenseDocument: _licenseDocument,
            dateOfBirth,
            ...safeFormData
        } = formData as any

        const {
            ssn: _onboardingSsn,
            socialSecurityNumber: _socialSecurityNumber,
            licenseFilePreview: _licenseFilePreview,
            ...safeOnboardingData
        } = onboardingData as any

        const safeAdditionalLicenses = (additionalLicenses || []).map(l => {
            const { licenseFilePreview: _preview, ...rest } = l as any
            return rest
        })

        return {
            v: 1,
            savedAt: Date.now(),
            currentStep,
            showOnboarding,
            therapistId,
            selectedPlan,
            billingCycle,
            discountedPrice,
            subscriptionDetails,
            subscriptionPaymentSuccess,
            formData: {
                ...safeFormData,
                dateOfBirth: dateOfBirth ? (dateOfBirth as Date).toISOString() : null,
            },
            onboardingData: safeOnboardingData,
            additionalLicenses: safeAdditionalLicenses,
        }
    }, [
        formData,
        onboardingData,
        additionalLicenses,
        currentStep,
        showOnboarding,
        therapistId,
        selectedPlan,
        billingCycle,
        discountedPrice,
        subscriptionDetails,
        subscriptionPaymentSuccess,
    ])

    const debouncedDraftSnapshot = useDebounce(draftSnapshot, 500)

    useEffect(() => {
        // Restore draft once on mount (if present).
        if (restoreDraftOnceRef.current) return
        restoreDraftOnceRef.current = true

            ; (async () => {
                try {
                    const raw = localStorage.getItem(THERAPIST_CREATE_ACCOUNT_DRAFT_KEY)
                    if (!raw) return

                    const parsed = JSON.parse(raw) as Partial<TherapistCreateAccountDraftV1>
                    if (!parsed || parsed.v !== 1) return

                    let restoredPassword = ''
                    let restoredConfirmPassword = ''
                    let restoredProviderSsn = ''
                    if (parsed.encryptedPassword) {
                        restoredPassword = await decryptDraftText(parsed.encryptedPassword)
                    }
                    if (parsed.encryptedConfirmPassword) {
                        restoredConfirmPassword = await decryptDraftText(
                            parsed.encryptedConfirmPassword
                        )
                    }
                    if (parsed.encryptedProviderSsn) {
                        restoredProviderSsn = await decryptDraftText(parsed.encryptedProviderSsn)
                    }

                    if (typeof parsed.showOnboarding === 'boolean') {
                        setShowOnboarding(parsed.showOnboarding)
                    }

                    if (typeof parsed.currentStep === 'number' && parsed.currentStep >= 0) {
                        setCurrentStep(parsed.currentStep)
                    }

                    if (typeof parsed.therapistId === 'string' || parsed.therapistId === null) {
                        setTherapistId(parsed.therapistId)
                    }
                    if (parsed.selectedPlan) {
                        setSelectedPlan(parsed.selectedPlan as any)
                    }
                    if (parsed.billingCycle === 'monthly' || parsed.billingCycle === 'yearly') {
                        setBillingCycle(parsed.billingCycle)
                    }
                    if (typeof parsed.discountedPrice === 'number' || parsed.discountedPrice === null) {
                        setDiscountedPrice(parsed.discountedPrice ?? null)
                    }
                    if (parsed.subscriptionDetails) {
                        setSubscriptionDetails(parsed.subscriptionDetails as any)
                    }
                    if (typeof parsed.subscriptionPaymentSuccess === 'boolean') {
                        setSubscriptionPaymentSuccess(parsed.subscriptionPaymentSuccess)
                    }

                    if (parsed.formData && typeof parsed.formData === 'object') {
                        setFormData(prev => {
                            const restoredDob =
                                parsed.formData?.dateOfBirth && typeof parsed.formData.dateOfBirth === 'string'
                                    ? new Date(parsed.formData.dateOfBirth)
                                    : null

                            return {
                                ...prev,
                                ...parsed.formData,
                                dateOfBirth: restoredDob,
                                password: restoredPassword,
                                confirmPassword: restoredConfirmPassword,
                                ssn: restoredProviderSsn,
                                taxId: '',
                                licenseDocument: null,
                            }
                        })
                    }

                    if (parsed.onboardingData && typeof parsed.onboardingData === 'object') {
                        setOnboardingData(prev => ({
                            ...prev,
                            ...parsed.onboardingData,
                            // Never restore sensitive / non-serializable fields
                            ssn: '',
                            socialSecurityNumber: '',
                            licenseFilePreview: '',
                        }))
                    }

                    if (Array.isArray(parsed.additionalLicenses)) {
                        setAdditionalLicenses(
                            parsed.additionalLicenses.map(l => ({
                                ...l,
                                licenseFilePreview: '',
                            })) as any
                        )
                    }
                    // Keep password meter consistent with restored password.
                    setPasswordStrength(calculatePasswordStrength(restoredPassword))
                } catch (e) {
                    console.warn('Failed to restore therapist create account draft:', e)
                }
            })()
    }, [])

    useEffect(() => {
        // Persist draft (sanitized) so users can resume after closing the browser.
        if (!draftPersistenceEnabledRef.current) return
        if (subscriptionPaymentSuccess) return
            ; (async () => {
                try {
                    const encryptedPassword = await encryptDraftText(formData.password || '')
                    const encryptedConfirmPassword = await encryptDraftText(
                        formData.confirmPassword || ''
                    )
                    const encryptedProviderSsn = await encryptDraftText(formData.ssn || '')

                    localStorage.setItem(
                        THERAPIST_CREATE_ACCOUNT_DRAFT_KEY,
                        JSON.stringify({
                            ...debouncedDraftSnapshot,
                            encryptedPassword,
                            encryptedConfirmPassword,
                            encryptedProviderSsn,
                        } satisfies TherapistCreateAccountDraftV1)
                    )
                } catch (e) {
                    console.warn('Failed to persist therapist create account draft:', e)
                }
            })()
    }, [debouncedDraftSnapshot])

    const steps = [
        'About Agency',
        'Service Information',
        'Agency Credentials & Verification',
        'Choose Subscription',
        'Connect Bank',
    ]

    const hasServiceInfoData = useMemo(() => {
        return (
            onboardingData.services.length > 0 ||
            onboardingData.degrees.length > 0 ||
            onboardingData.insuranceAccepted.length > 0 ||
            onboardingData.yearsOfExperience.trim() !== '' ||
            onboardingData.noOfProvider.trim() !== ''
        )
    }, [
        onboardingData.services,
        onboardingData.degrees,
        onboardingData.insuranceAccepted,
        onboardingData.yearsOfExperience,
        onboardingData.noOfProvider,
    ])

    const hasAboutYouData = useMemo(() => {
        return (
            !!onboardingData.profilePicture?.trim() ||
            onboardingData.professionalBio.trim().length > 0
        )
    }, [onboardingData.profilePicture, onboardingData.professionalBio])

    const handleInputChange =
        (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value
            setFormData(prev => ({
                ...prev,
                [field]: value,
            }))

            // Calculate password strength when password field changes
            if (field === 'password') {
                setPasswordStrength(calculatePasswordStrength(value))
                // Re-validate confirm password when password changes
                if (formData.confirmPassword) {
                    if (value !== formData.confirmPassword) {
                        setConfirmPasswordError('Passwords do not match')
                    } else {
                        setConfirmPasswordError('')
                    }
                }
            }

            // Real-time validation for confirm password
            if (field === 'confirmPassword') {
                if (value && formData.password) {
                    if (value !== formData.password) {
                        setConfirmPasswordError('Passwords do not match')
                    } else {
                        setConfirmPasswordError('')
                    }
                } else if (!value) {
                    setConfirmPasswordError('')
                }
            }
        }

    const handleSelectChange = (field: string) => (event: any) => {
        const value = event.target.value
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }))
        if (field === 'gender') {
            setGenderError('')
        }
    }

    const verifyEmail = async (email: string): Promise<boolean> => {
        if (!email || !email.trim()) {
            setEmailVerificationError('Please enter an email address')
            return false
        }

        // Validate email format using validation utility
        if (!isValidEmail(email)) {
            setEmailVerificationError(ValidationRules.email.message)
            return false
        }

        setIsVerifyingEmail(true)
        setEmailVerificationError('')

        try {
            const response = await api.post('/agency/verify-email', {
                email: email.trim(),
            })

            // If email is not found (available), proceed
            if (response.data.message === 'Email not found in database') {
                setEmailVerificationError('')
                return true
            }

            // Otherwise, email exists
            setEmailVerificationError(
                'Email already exists. Please use a different email address.'
            )
            return false
        } catch (error) {
            if (axios.isAxiosError(error)) {
                if (error.response) {
                    const { status, data } = error.response
                    // 404 means email already exists (based on user's description)
                    if (status === 404 && data.message === 'Email id already exist') {
                        setEmailVerificationError(
                            'Email already exists. Please use a different email address.'
                        )
                        return false
                    }
                    // Other error cases
                    setEmailVerificationError(
                        data.message || 'Failed to verify email. Please try again.'
                    )
                    return false
                } else if (error.request) {
                    setEmailVerificationError(
                        'Unable to verify email. Please check your connection.'
                    )
                    return false
                } else {
                    setEmailVerificationError('Failed to verify email. Please try again.')
                    return false
                }
            } else {
                setEmailVerificationError('Failed to verify email. Please try again.')
                return false
            }
        } finally {
            setIsVerifyingEmail(false)
        }
    }

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault()

        // Validate DBA Name
        if (!formData.dbaName.trim()) {
            setDbaNameError('DBA Name is required')
            return
        }
        setDbaNameError('')

        // Validate EIN
        if (!formData.ein.trim()) {
            setEinError('EIN is required')
            return
        }
        if (!isValidEIN(formData.ein)) {
            setEinError(ValidationRules.ein.message)
            return
        }
        setEinError('')

        // Validate State of Operations
        if (formData.stateOfOperations.length === 0) {
            setStateOfOperationsError('Please select at least one state')
            return
        }
        setStateOfOperationsError('')

        // Validate Business Email
        if (!formData.businessEmail.trim()) {
            setBusinessEmailError('Business email is required')
            return
        }
        if (!isValidEmail(formData.businessEmail)) {
            setBusinessEmailError(ValidationRules.email.message)
            return
        }
        setBusinessEmailError('')

        // Validate Business Phone Number (optional, but must be valid if provided)
        if (formData.businessPhoneNumber.trim()) {
            const businessPhoneErr = getUSPhoneErrorMessage(formData.businessPhoneNumber)
            if (businessPhoneErr) {
                setBusinessPhoneError(businessPhoneErr)
                setIsBusinessPhoneTouched(true)
                return
            }
        }
        setBusinessPhoneError('')

        // Validate phone number before proceeding
        const phoneError = getUSPhoneErrorMessage(formData.phoneNumber)
        if (phoneError) {
            setPhoneNumberError(phoneError)
            setIsPhoneNumberTouched(true)
            return
        }

        // Validate passwords match before proceeding
        if (formData.password !== formData.confirmPassword) {
            setConfirmPasswordError('Passwords do not match')
            return
        }

        // if (!formData.gender) {
        //     setGenderError('Please select your gender')
        //     scrollToErrorField('gender-field')
        //     return
        // }
        // setGenderError('')

        // if (!formData.dateOfBirth) {
        //     setDateOfBirthError('Date of birth is required')
        //     scrollToErrorField('gender-field')
        //     return
        // }
        // setDateOfBirthError('')

        // Verify email before proceeding
        const isEmailAvailable = await verifyEmail(formData.email)

        if (!isEmailAvailable) {
            // Error is already set in emailVerificationError state
            return
        }

        // Handle account creation logic here
        console.log('Account creation attempt:', formData)
        // Show onboarding form instead of navigating
        setShowOnboarding(true)
    }

    const handleLoginClick = () => {
        navigate('/agency-login')
    }

    const handleOnboardingInputChange =
        (field: keyof OnboardingData) => (event: any) => {
            const value = event.target.value
            setOnboardingData(prev => ({
                ...prev,
                [field]: value,
            }))
            if (
                field === 'licenseType' ||
                field === 'licenseNpi' ||
                field === 'licenseState' ||
                field === 'licenseNumber' ||
                field === 'ssn'
            ) {
                clearNpiFieldError(field)
            }
        }

    const handleYearsOfExperienceChange = (event: any) => {
        const value = event.target.value

        // Allow empty value
        if (value === '') {
            setOnboardingData(prev => ({
                ...prev,
                yearsOfExperience: '',
            }))
            setYearsOfExperienceError('Years of Experience is required')
            return
        }

        setYearsOfExperienceError('')

        // Check if value contains more than one decimal point
        if ((value.match(/\./g) || []).length > 1) {
            return // Reject if more than one decimal point
        }

        // Check if value has more than 1 decimal place
        const decimalIndex = value.indexOf('.')
        if (decimalIndex !== -1 && value.length - decimalIndex - 1 > 1) {
            return // Reject if more than 1 decimal place
        }

        // Parse the number
        const numValue = parseFloat(value)

        // Don't allow negative numbers or NaN
        if (isNaN(numValue) || numValue < 0) {
            return
        }

        // Check if decimal part is valid (must be 0 or 5, i.e., .0 or .5)
        if (decimalIndex !== -1) {
            const decimalPart = value.substring(decimalIndex + 1)
            // Only allow .0 or .5 (or just . which means user is typing)
            if (
                decimalPart.length === 1 &&
                decimalPart !== '0' &&
                decimalPart !== '5'
            ) {
                return // Reject invalid decimal digit
            }
        }

        setOnboardingData(prev => ({
            ...prev,
            yearsOfExperience: value,
        }))
    }

    const formatSSN = (value: string) => {
        if (!value) return ''
        return value
            .replace(/^(\d{3})(\d)/, '$1-$2')
            .replace(/^(\d{3}-\d{2})(\d)/, '$1-$2')
            .substring(0, 11)
    }

    const formatEIN = (value: string) => {
        if (!value) return ''
        const digitsOnly = value.replace(/\D/g, '')
        if (digitsOnly.length <= 2) return digitsOnly
        // Format as XX-XXXXXXX (2 digits, hyphen, up to 7 digits)
        return digitsOnly.substring(0, 2) + '-' + digitsOnly.substring(2, 9)
    }

    const handleNpiChange = (event: any) => {
        const value = event.target.value
        setOnboardingData(prev => ({
            ...prev,
            npiNumber: value,
        }))
        // Reset verification when NPI changes
        setIsNpiVerified(false)
        setNpiVerificationError('')
    }

    const handleVerifyNpi = async () => {
        const npiNumber = onboardingData.npiNumber.trim()
        localStorage.setItem('providerNpi', npiNumber)
        if (!npiNumber) {
            setNpiVerificationError('Please enter an NPI number')
            return
        }

        setIsNpiVerifying(true)
        setNpiVerificationError('')

        try {
            const response = await api.get(`/npi/number`, {
                params: { npi: npiNumber },
            })

            const data = response.data

            // Check if the API response indicates a valid NPI
            if (data && (data.result_count > 0 || data.results?.length > 0)) {
                setIsNpiVerified(true)
                setNpiVerificationError('')

                // Pre-populate other NPI fields
                setOnboardingData(prev => ({
                    ...prev,
                    licenseNpi: npiNumber,
                }))
                setFormData(prev => ({
                    ...prev,
                    billingNpi: npiNumber,
                }))
                // If there are additional licenses, pre-populate them as well
                if (additionalLicenses.length > 0) {
                    setAdditionalLicenses(prev =>
                        prev.map(license => ({
                            ...license,
                            licenseNpi: npiNumber,
                        }))
                    )
                }
            } else {
                setNpiVerificationError('Invalid NPI number')
                setIsNpiVerified(false)
            }
        } catch (error) {
            if (axios.isAxiosError(error)) {
                if (error.response) {
                    // Server responded with error status
                    setNpiVerificationError(
                        error.response.data?.message || 'Invalid NPI number'
                    )
                } else if (error.request) {
                    // Request made but no response received
                    setNpiVerificationError(
                        'Unable to verify NPI. Please check your connection.'
                    )
                } else {
                    // Something else happened
                    setNpiVerificationError('Failed to verify NPI. Please try again.')
                }
            } else {
                setNpiVerificationError('Failed to verify NPI. Please try again.')
            }
            setIsNpiVerified(false)
        } finally {
            setIsNpiVerifying(false)
        }
    }

    const handleVerifyBillingNpi = async () => {
        const billingNpi = formData.billingNpi.trim()

        if (!billingNpi) {
            setBillingNpiVerificationError('Please enter a billing NPI number')
            return
        }

        setIsBillingNpiVerifying(true)
        setBillingNpiVerificationError('')

        try {
            const response = await api.get(`/npi/number`, {
                params: { npi: billingNpi },
            })

            const data = response.data

            // Check if the API response indicates a valid NPI
            if (data && (data.result_count > 0 || data.results?.length > 0)) {
                setIsBillingNpiVerified(true)
                setBillingNpiVerificationError('')
            } else {
                setBillingNpiVerificationError('Invalid billing NPI number')
                setIsBillingNpiVerified(false)
            }
        } catch (error) {
            if (axios.isAxiosError(error)) {
                if (error.response) {
                    // Server responded with error status
                    setBillingNpiVerificationError(
                        error.response.data?.message || 'Invalid billing NPI number'
                    )
                } else if (error.request) {
                    // Request made but no response received
                    setBillingNpiVerificationError(
                        'Unable to verify billing NPI. Please check your connection.'
                    )
                } else {
                    // Something else happened
                    setBillingNpiVerificationError(
                        'Failed to verify billing NPI. Please try again.'
                    )
                }
            } else {
                setBillingNpiVerificationError(
                    'Failed to verify billing NPI. Please try again.'
                )
            }
            setIsBillingNpiVerified(false)
        } finally {
            setIsBillingNpiVerifying(false)
        }
    }

    const handleOnboardingMultiSelectChange =
        (field: keyof OnboardingData) => (event: any) => {
            setOnboardingData(prev => ({
                ...prev,
                [field]: event.target.value,
            }))
            if (field === 'services') setServicesError('')
            if (field === 'degrees') setDegreesError('')
        }

    const handleRemoveChip = (
        field: keyof OnboardingData,
        valueToRemove: string
    ) => {
        setIsDeletingChip(true)
        if (field === 'services') {
            setServicesSelectOpen(false)
            setProvidedServicesSelectOpen(false)
        } else if (field === 'degrees') {
            setDegreesSelectOpen(false)
        }
        setOnboardingData(prev => ({
            ...prev,
            [field]: Array.isArray(prev[field])
                ? (prev[field] as string[]).filter(item => item !== valueToRemove)
                : prev[field],
        }))
        // Reset the flag after a short delay
        setTimeout(() => {
            setIsDeletingChip(false)
        }, 100)
    }

    // State of Operations multi-select handlers
    const handleStateOfOperationsChange = (event: any) => {
        setFormData(prev => ({
            ...prev,
            stateOfOperations: event.target.value as string[],
        }))
        setStateOfOperationsError('')
    }

    const handleRemoveStateOfOperations = (valueToRemove: string) => {
        setIsDeletingChip(true)
        setStateOfOperationsSelectOpen(false)
        setFormData(prev => ({
            ...prev,
            stateOfOperations: prev.stateOfOperations.filter(
                item => item !== valueToRemove
            ),
        }))
        setTimeout(() => {
            setIsDeletingChip(false)
        }, 100)
    }

    // Secondary administrators handlers
    const handleOpenAddAdminDialog = () => {
        setNewAdminDraft({ firstName: '', lastName: '', role: '', email: '' })
        setNewAdminErrors({})
        setShowAddAdminDialog(true)
    }

    const handleCloseAddAdminDialog = () => {
        setShowAddAdminDialog(false)
    }

    const handleAddSecondaryAdministrator = () => {
        const errors: Record<string, string> = {}
        if (!newAdminDraft.firstName.trim()) {
            errors.firstName = 'First name is required'
        }
        if (!newAdminDraft.lastName.trim()) {
            errors.lastName = 'Last name is required'
        }
        if (!newAdminDraft.role.trim()) {
            errors.role = 'Role is required'
        }
        if (!newAdminDraft.email.trim()) {
            errors.email = 'Email is required'
        } else if (!isValidEmail(newAdminDraft.email)) {
            errors.email = ValidationRules.email.message
        }
        if (Object.keys(errors).length > 0) {
            setNewAdminErrors(errors)
            return
        }
        setSecondaryAdministrators(prev => [
            ...prev,
            {
                id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                firstName: newAdminDraft.firstName.trim(),
                lastName: newAdminDraft.lastName.trim(),
                role: newAdminDraft.role.trim(),
                email: newAdminDraft.email.trim(),
            },
        ])
        setShowAddAdminDialog(false)
    }

    const handleRemoveSecondaryAdministrator = (id: string) => {
        setSecondaryAdministrators(prev => prev.filter(admin => admin.id !== id))
    }

    // Handle Autocomplete change for insurance accepted
    const handleInsuranceAcceptedChange = (
        event: React.SyntheticEvent,
        newValue: Payer[]
    ) => {
        setSelectedPayers(newValue)
        setOnboardingData(prev => ({
            ...prev,
            insuranceAccepted: newValue.map(payer => payer._id),
        }))
        setInsuranceError('')
        // Clear input after selection so user can search for next option
        setTimeout(() => {
            setPayerSearchInput('')
        }, 0)
    }

    // Update selectedPayers when search options include previously selected IDs
    useEffect(() => {
        if (
            payerSearchOptions.length > 0 &&
            onboardingData.insuranceAccepted.length > 0
        ) {
            setSelectedPayers(prevSelected => {
                const currentSelectedIds = prevSelected.map(p => p._id)
                const missingIds = onboardingData.insuranceAccepted.filter(
                    id => !currentSelectedIds.includes(id)
                )

                if (missingIds.length === 0) return prevSelected

                const newPayers = missingIds
                    .map(id => payerSearchOptions.find(p => p._id === id))
                    .filter((p): p is Payer => p !== undefined)

                return [...prevSelected, ...newPayers]
            })
        }
    }, [payerSearchOptions, onboardingData.insuranceAccepted])

    // Helper function to scroll to and focus an element
    const scrollToErrorField = (elementId: string) => {
        setTimeout(() => {
            const element = document.getElementById(elementId)
            if (element) {
                // Scroll to the element
                element.scrollIntoView({ behavior: 'smooth', block: 'center' })
                // Find the input element within the TextField
                const input =
                    element instanceof HTMLInputElement
                        ? element
                        : (element.querySelector('input') as HTMLInputElement | null)
                // Focus the input after scrolling
                setTimeout(() => {
                    if (input) {
                        input.focus()
                        // Highlight the field by triggering focus state
                        input.blur()
                        input.focus()
                    }
                }, 300)
            }
        }, 100)
    }

    const clearNpiFieldError = (field: string) => {
        setNpiStepErrors(prev => {
            if (!prev[field]) return prev
            const { [field]: _removed, ...rest } = prev
            return rest
        })
    }

    // Helper function to check if NPI is required for a license type
    const isNpiRequired = (licenseTypeId: string): boolean => {
        if (licenseTypeId === '69aacb7782693bdedf50db43') return
        const licenseType = licenseTypes.find(lt => lt._id === licenseTypeId)
        return licenseType?.required === 'mandatory'
    }

    const validateNpiAdditionalFields = (): boolean => {
        const errors: Record<string, string> = {}
        let isValid = true
        let firstErrorId: string | null = null

        const assignError = (key: string, message: string, elementId: string) => {
            errors[key] = message
            if (!firstErrorId) {
                firstErrorId = elementId
            }
            isValid = false
        }

        if (!formData.billingAddressLine.trim()) {
            assignError(
                'billingAddressLine',
                'Billing address is required',
                'billing-address-field'
            )
        }

        if (!formData.billingState.trim()) {
            assignError(
                'billingState',
                'Billing state is required',
                'billing-state-field'
            )
        }

        if (!formData.billingCity.trim()) {
            assignError(
                'billingCity',
                'Billing city is required',
                'billing-city-field'
            )
        }

        if (!formData.billingZipCode.trim()) {
            assignError(
                'billingZipCode',
                'Billing zip code is required',
                'billing-zip-field'
            )
        }

        if (!formData.billingCountry.trim()) {
            assignError(
                'billingCountry',
                'Billing country is required',
                'billing-country-field'
            )
        }

        if (!onboardingData.licenseType.trim()) {
            assignError(
                'licenseType',
                'Specialty License Type is required',
                'license-type-field'
            )
        }

        // Validate NPI if required for the selected license type
        if (
            onboardingData.licenseType &&
            isNpiRequired(onboardingData.licenseType) &&
            !onboardingData.licenseNpi?.trim()
        ) {
            assignError(
                'licenseNpi',
                'NPI Number is required for this license type',
                'license-name-field'
            )
        }

        // Validate additional licenses
        additionalLicenses.forEach((license, index) => {
            if (
                license.licenseType &&
                isNpiRequired(license.licenseType) &&
                !license.licenseNpi?.trim()
            ) {
                assignError(
                    `additionalLicenseNpi_${index}`,
                    'NPI Number is required for this license type',
                    `additional-license-npi-${index}`
                )
            }
        })

        if (onboardingData.licenseType !== '69aacb7782693bdedf50db43') {
            if (!onboardingData.licenseState.trim()) {
                assignError(
                    'licenseState',
                    'License state is required',
                    'license-state-field'
                )
            }

            if (!onboardingData.licenseNumber.trim()) {
                assignError(
                    'licenseNumber',
                    'License number is required',
                    'license-number-field'
                )
            }

            if (!onboardingData.issueDate.trim()) {
                assignError(
                    'issueDate',
                    'License issue date is required',
                    'license-issue-field'
                )
            }

            if (!onboardingData.licenseExpirationDate.trim()) {
                assignError(
                    'licenseExpirationDate',
                    'License expiration date is required',
                    'license-expiration-field'
                )
            }
        }

        // License file is optional for '69aacb7782693bdedf50db43'
        if (
            onboardingData.licenseType !== '69aacb7782693bdedf50db43' &&
            !onboardingData.licenseFileUrl.trim()
        ) {
            assignError(
                'licenseFileUrl',
                'License document is required',
                'license-file-field'
            )
        }

        let phoneMessage = ''
        const billingPhoneMessage = getBillingPhoneValidationMessage(
            formData.billingPhone
        )
        if (billingPhoneMessage) {
            phoneMessage = billingPhoneMessage
            if (!firstErrorId) {
                firstErrorId = 'billing-phone-field'
            }
            isValid = false
        }
        setBillingPhoneError(phoneMessage)
        if (phoneMessage) {
            setIsBillingPhoneTouched(true)
        }

        setNpiStepErrors(errors)

        if (!isValid && firstErrorId) {
            scrollToErrorField(firstErrorId)
        }

        return isValid
    }

    const moveToStep = (nextStep: number) => {
        setCurrentStep(nextStep)
        window.scrollTo(0, 0)
        if (document.documentElement) {
            document.documentElement.scrollTop = 0
        }
        if (document.body) {
            document.body.scrollTop = 0
        }
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' })
            if (document.documentElement) {
                document.documentElement.scrollTo({ top: 0, behavior: 'smooth' })
            }
            if (document.body) {
                document.body.scrollTo({ top: 0, behavior: 'smooth' })
            }
        }, 100)
    }

    // console.log('formData', formData)

    const handleNext = async (action: 'skip' | 'save' = 'save') => {
        switch (currentStep) {

            case 0: {
                if (action === 'skip' || !hasAboutYouData) {
                    moveToStep(1)
                    return
                }
                moveToStep(1)
                return
            }
            case 1: {
                let hasError = false
                if (!onboardingData.services || onboardingData.services.length === 0) {
                    setServicesError('Please select at least one specialty')
                    hasError = true
                }
                if (!onboardingData.degrees || onboardingData.degrees.length === 0) {
                    setDegreesError('Please select at least one degree')
                    hasError = true
                }
                if (!onboardingData.insuranceAccepted || onboardingData.insuranceAccepted.length === 0) {
                    setInsuranceError('Please select at least one insurance provider')
                    hasError = true
                }
                if (!onboardingData.noOfProvider || onboardingData.noOfProvider.trim() === '') {
                    setNoOfProviderError('Number of providers is required')
                    hasError = true
                }

                if (hasError) {
                    if (!onboardingData.services || onboardingData.services.length === 0) {
                        scrollToErrorField('services-field')
                    } else if (!onboardingData.degrees || onboardingData.degrees.length === 0) {
                        scrollToErrorField('degrees-field')
                    } else if (!onboardingData.insuranceAccepted || onboardingData.insuranceAccepted.length === 0) {
                        scrollToErrorField('insurance-field')
                    } else if (!onboardingData.noOfProvider || onboardingData.noOfProvider.trim() === '') {
                        scrollToErrorField('no-of-providers-field')
                    }
                    return
                }

                moveToStep(2)
                return
            }

            case 2: {
                const isBusiness = formData.billingType === 'business'

                // Only require Provider NPI verification if user has entered a value
                // const hasProviderNpi =
                //     onboardingData.npiNumber && onboardingData.npiNumber.trim().length > 0
                // if (hasProviderNpi && !isNpiVerified) {
                //     setNpiVerificationError(
                //         'Please verify your Provider NPI before proceeding.'
                //     )
                //     scrollToErrorField('provider-npi-field')
                //     return
                // }

                // Only require Billing NPI verification for business type
                // if (isBusiness) {
                const hasBillingNpi =
                    formData.billingNpi && formData.billingNpi.trim().length > 0
                if (hasBillingNpi && !isBillingNpiVerified) {
                    setBillingNpiVerificationError(
                        'Please verify your Billing NPI before proceeding.'
                    )
                    scrollToErrorField('billing-npi-field')
                    return
                }
                // }

                // Validate Company Name and EIN for business type only
                // if (isBusiness) {
                // if (!formData.companyName || !formData.companyName.trim()) {
                //     setCompanyNameError('Company Legal Name is required')
                //     scrollToErrorField('company-name-field')
                //     return
                // }

                if (!formData.taxId || !formData.taxId.trim()) {
                    setTaxIdError('Tax Id / EIN is required')
                    scrollToErrorField('tax-id-field')
                    return
                }

                // if (!isValidEIN(formData.taxId)) {
                //     setTaxIdError(ValidationRules.ein.message)
                //     scrollToErrorField('tax-id-field')
                //     return
                // }
                // }

                // Validate SSN for individual type only
                // if (!isBusiness) {
                //     if (!formData.ssn || !formData.ssn.trim()) {
                //         setSsnError('SSN is required')
                //         scrollToErrorField('ssn-number-field')
                //         return
                //     }

                //     if (!isValidSSN(formData.ssn)) {
                //         setSsnError(ValidationRules.ssn.message)
                //         scrollToErrorField('ssn-number-field')
                //         return
                //     }
                // }

                if (!formData.billingPhone || !formData.billingPhone.trim()) {
                    setBillingPhoneError('Billing phone number is required')
                    scrollToErrorField('billing-phone-field')
                    return
                }

                const extraFieldsValid = validateNpiAdditionalFields()
                if (!extraFieldsValid) {
                    return
                }

                // Create therapist account before moving to Step 4
                try {
                    setIsProcessingPayment(true)
                    setPaymentError(null)

                    if (!therapistId) {
                        // Normalize licenses: include primary license + additional licenses
                        const normalizedLicenses = [
                            {
                                licenseType: onboardingData.licenseType,
                                licenseNpi: onboardingData.licenseNpi,
                                licenseState: onboardingData.licenseState,
                                licenseNumber: onboardingData.licenseNumber,
                                issueDate: onboardingData.issueDate,
                                expiryDate: onboardingData.licenseExpirationDate,
                                licenseStatus: onboardingData.licenseStatus,
                                licenseFileUrl: onboardingData.licenseFileUrl || '',
                            },
                            ...additionalLicenses.map(l => ({
                                licenseType: l.licenseType,
                                licenseNpi: l.licenseNpi,
                                licenseState: l.licenseState,
                                licenseNumber: l.licenseNumber,
                                issueDate: l.issueDate,
                                expiryDate: l.licenseExpirationDate,
                                licenseStatus: l.licenseStatus,
                                licenseFileUrl: l.licenseFileUrl || '',
                            })),
                        ].filter(l => !!l.licenseType || !!l.licenseNumber)

                        const therapistData = {
                            agencyName: formData.agencyName,
                            dbaName: formData.dbaName,
                            companyEin: formData.ein,
                            companyNpi: formData.npiOrganization || null,
                            licenseNumber: formData.agencyLicenseNumber || null,
                            licenseExpiration: formData.agencyLicenseExpiration
                                ? formatDate(formData.agencyLicenseExpiration, 'MM/dd/yyyy')
                                : null,
                            companyStates: formData.stateOfOperations,
                            companyEmail: formData.businessEmail,
                            companyPhone: formData.businessPhoneNumber || null,
                            website: formData.website || null,
                            professionalLiabilityInsurance:
                                formData.professionalLiabilityInsurance || null,
                            generalLiabilityInsurance: formData.generalLiabilityInsurance || null,
                            businessLicense: formData.businessLicenseUrl || null,
                            agencyLicense: formData.agencyLicenseUrl || null,
                            npiCertificate: formData.npiCertificateUrl || null,
                            wnineForm: formData.w9FormUrl || null,
                            certificateInsurance: formData.certificateOfInsuranceUrl || null,
                            clinicalDoc: formData.clinicalAccreditationUrl || null,
                            // gender: formData.gender,
                            // dob: formData.dateOfBirth
                            //     ? formatDate(formData.dateOfBirth, 'yyyy-MM-dd')
                            //     : undefined,





                            addressLine1: formData.address,
                            addressLine2: formData.addressLine2,

                            city: formData.city,
                            country: formData.country,
                            state: formData.state,
                            zipCode: formData.zipCode,
                            latitude: formData.latitude,
                            longitude: formData.longitude,

                            serviceProvideType: onboardingData.services,
                            insuranceAccepted: onboardingData.insuranceAccepted,
                            yearsOfExperience:
                                parseFloat(onboardingData.yearsOfExperience) || 0,
                            noOfProvider: onboardingData.noOfProvider,









                            bio: onboardingData.professionalBio,
                            specialization: onboardingData.degrees,


                            // boardCertificationNo:
                            //     onboardingData.boardCertificationNo?.trim() || null,
                            // boardSpeciality: onboardingData.boardSpeciality?.trim() || null,
                            // boardName: onboardingData.boardName?.trim() || null,
                            // boardSubSpeciality:
                            //     onboardingData.boardSubSpeciality?.trim() || null,


                            // billingType: formData.billingType,
                            // providerNpi: onboardingData.npiNumber?.trim() || null,
                            ssn: formData.ssn,
                            billingNpi: formData.billingNpi?.trim() || null,
                            taxId: formData.taxId,
                            companyName: formData.companyName || null,
                            billingPhone: formData.billingPhone,
                            billingAddressLine1: formData.billingAddressLine,
                            billingState: formData.billingState,
                            billingCountry: formData.billingCountry,
                            billingCity: formData.billingCity,
                            billingZipCode: formData.billingZipCode,
                            profilePhotoUrl: onboardingData.profilePicture || undefined,

                            licenses: normalizedLicenses,

                            users: [
                                {
                                    firstName: formData.firstName,
                                    lastName: formData.lastName,
                                    email: formData.email,
                                    phone: formData.phoneNumber,
                                    password: formData.password,
                                },
                                ...secondaryAdministrators.map(admin => ({
                                    firstName: admin.firstName,
                                    lastName: admin.lastName,
                                    email: admin.email,
                                    role: admin.role,
                                })),
                            ],
                        }

                        const accountResponse =
                            await membershipApi.agencySignup(therapistData)

                        console.log('Therapist signup response:', accountResponse)

                        if (!accountResponse.id) {
                            throw new Error(
                                accountResponse.message || 'Failed to create provider account'
                            )
                        }

                        const newTherapistId = accountResponse.id
                        setTherapistId(newTherapistId)

                        // Show success message from response
                        if (accountResponse.message) {
                            setSuccessMessage(accountResponse.message)
                            setShowSuccessAlert(true)
                        }

                        // Clean localStorage after successful account creation
                        try {
                            draftPersistenceEnabledRef.current = false
                            localStorage.removeItem(THERAPIST_CREATE_ACCOUNT_DRAFT_KEY)
                        } catch (e) {
                            console.warn('Failed to clear draft after signup:', e)
                        }
                    }

                    moveToStep(3)
                } catch (error) {
                    console.error('Error in therapist signup:', error)
                    const errorMessage =
                        error instanceof Error
                            ? error.message
                            : 'An error occurred during account creation'
                    setPaymentError(errorMessage)
                } finally {
                    setIsProcessingPayment(false)
                }
                return
            }
            case 3: {
                setPaymentError(null)
                if (!subscriptionPaymentSuccess) {
                    setPaymentError(
                        'Please complete your subscription payment before proceeding to connect your bank account.'
                    )
                    return
                }
                moveToStep(4)
                return
            }
            default: {
                if (currentStep < steps.length - 1) {
                    moveToStep(currentStep + 1)
                } else {
                    handleCompleteProfile()
                }
            }
        }
    }

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1)
        } else {
            setShowOnboarding(false)
        }
    }

    const handleProfilePictureChange = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0]
        event.target.value = '' // Reset to allow re-uploading same file
        if (file) {
            try {
                setIsUploadingProfilePhoto(true)

                // Upload file to server
                const uploadResponse = await membershipApi.uploadProfilePhoto(file)

                // Store the fileUrl returned from the API
                const imageUrl = uploadResponse?.fileUrl || uploadResponse?.profilePhotoUrl || uploadResponse?.url || uploadResponse?.data?.profilePhotoUrl || uploadResponse?.data?.url
                if (imageUrl) {
                    setOnboardingData(prev => ({
                        ...prev,
                        profilePicture: imageUrl,
                    }))
                }
            } catch (error) {
                console.error('Error uploading profile photo:', error)
                // You could add an error notification here if needed
            } finally {
                setIsUploadingProfilePhoto(false)
            }
        }
    }

    const handleRemoveProfilePicture = () => {
        setOnboardingData(prev => ({
            ...prev,
            profilePicture: null,
        }))
    }

    const handleBioChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setOnboardingData(prev => ({
            ...prev,
            professionalBio: event.target.value,
        }))
    }

    const handleLicenseDocumentChange = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0]
        event.target.value = '' // Reset value to allow selecting the same file again
        if (!file) return
        try {
            // Clean up previous preview URL if exists
            if (onboardingData.licenseFilePreview) {
                URL.revokeObjectURL(onboardingData.licenseFilePreview)
            }

            const uploadRes = await membershipApi.uploadLicenseFile(file)
            const url = uploadRes?.files?.[0]?.signedUrl || uploadRes?.fileUrl || ''
            if (url) {
                // Create preview URL for images
                let previewUrl = ''
                if (file.type.startsWith('image/')) {
                    previewUrl = URL.createObjectURL(file)
                }

                setOnboardingData(prev => ({
                    ...prev,
                    licenseFileUrl: url,
                    licenseFileName: file.name,
                    licenseFilePreview: previewUrl,
                }))
                clearNpiFieldError('licenseFileUrl')
            }
        } catch (e) {
            console.error('Error uploading license:', e)
        }
    }

    const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        const file = event.dataTransfer.files[0]
        if (
            file &&
            (file.type === 'application/pdf' || file.type.startsWith('image/'))
        ) {
            try {
                // Clean up previous preview URL if exists
                if (onboardingData.licenseFilePreview) {
                    URL.revokeObjectURL(onboardingData.licenseFilePreview)
                }

                const uploadRes = await membershipApi.uploadLicenseFile(file)
                const url = uploadRes?.files?.[0]?.signedUrl || uploadRes?.fileUrl || ''
                if (url) {
                    // Create preview URL for images
                    let previewUrl = ''
                    if (file.type.startsWith('image/')) {
                        previewUrl = URL.createObjectURL(file)
                    }

                    setOnboardingData(prev => ({
                        ...prev,
                        licenseFileUrl: url,
                        licenseFileName: file.name,
                        licenseFilePreview: previewUrl,
                    }))
                    clearNpiFieldError('licenseFileUrl')
                }
            } catch (e) {
                console.error('Error uploading dropped license:', e)
            }
        }
    }

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault()
    }

    // Corporate documents / clinical accreditation upload (Agency Credentials & Verification step)
    const handleDocumentUpload = (fieldPrefix: string) => async (file: File) => {
        setUploadingDocumentField(fieldPrefix)
        try {
            const uploadRes = await membershipApi.uploadLicenseFile(file)
            const url = uploadRes?.files?.[0]?.signedUrl || uploadRes?.fileUrl || ''
            if (url) {
                setFormData(prev => ({
                    ...prev,
                    [`${fieldPrefix}Url`]: url,
                    [`${fieldPrefix}Name`]: file.name,
                }))
            }
        } catch (e) {
            console.error('Error uploading document:', e)
        } finally {
            setUploadingDocumentField(null)
        }
    }

    const handleRemoveMainLicense = (e?: React.MouseEvent) => {
        e?.stopPropagation()
        if (onboardingData.licenseFilePreview) {
            URL.revokeObjectURL(onboardingData.licenseFilePreview)
        }
        setOnboardingData(prev => ({
            ...prev,
            licenseFileUrl: '',
            licenseFileName: '',
            licenseFilePreview: '',
        }))
    }

    const handleRemoveAdditionalLicenseFile = (
        index: number,
        e?: React.MouseEvent
    ) => {
        e?.stopPropagation()
        setAdditionalLicenses(prev => {
            const next = [...prev]
            if (next[index]?.licenseFilePreview) {
                URL.revokeObjectURL(next[index].licenseFilePreview!)
            }
            next[index] = {
                ...next[index],
                licenseFileUrl: '',
                licenseFileName: '',
                licenseFilePreview: '',
            }
            return next
        })
    }

    const handleStripeConnect = async () => {
        setIsConnectingBank(true)
        setPaymentError(null)

        try {
            console.log('Connecting bank account for:', formData.email)

            // Call the API to get Stripe hosted page URL
            const response = await membershipApi.connectAgencyBank(
                formData.email,
                '/agency-login'
            )

            console.log('Full API response:', response)

            // Check if response contains the URL directly or in a data property
            let stripeUrl = null
            if (typeof response === 'string') {
                stripeUrl = response
            } else if (response && response.data) {
                stripeUrl = response.data
            } else if (response && response.stripe_hosted_page_url) {
                stripeUrl = response.stripe_hosted_page_url
            }

            if (stripeUrl) {
                console.log('Redirecting to Stripe hosted page:', stripeUrl)

                // Redirect to Stripe hosted page
                window.location.href = stripeUrl
            } else {
                console.error('No Stripe hosted page URL received')
                setPaymentError(
                    'Failed to initialize bank connection. Please try again.'
                )
            }
        } catch (error) {
            console.error('Error connecting bank account:', error)
            console.error('Error details:', error.response?.data)
            console.error('Error status:', error.response?.status)

            // Check if it's a 401 error (unauthorized)
            if (error.response?.status === 401) {
                setPaymentError(
                    'Authentication required. Please ensure you are logged in.'
                )
            } else if (error.response?.status === 404) {
                setPaymentError(
                    'Bank connection service not found. Please contact support.'
                )
            } else if (error.code === 'NETWORK_ERROR' || !error.response) {
                setPaymentError(
                    'Network error. Please check your internet connection and try again.'
                )
            } else {
                setPaymentError(
                    `Failed to connect bank account: ${error.response?.data?.message || 'Please try again.'}`
                )
            }
        } finally {
            setIsConnectingBank(false)
        }
    }

    const handleCompleteProfile = async () => {
        setIsSubmitting(true)
        try {

            if (true) {
                setShowSuccessAlert(true)
                try {
                    localStorage.removeItem(THERAPIST_CREATE_ACCOUNT_DRAFT_KEY)
                } catch { }
                // Redirect to login after 2 seconds
                setTimeout(() => {
                    navigate('/agency-login')
                }, 2000)
            } else {
                throw new Error('Failed to create account')
            }
        } catch (error) {
            console.error('Error creating account:', error)
            // TODO: Show error message to user
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleLoginLinkClick = () => {
        try {
            localStorage.removeItem(THERAPIST_CREATE_ACCOUNT_DRAFT_KEY)
        } catch { }
        navigate('/agency-login')
    }

    // Auto-open subscription dialog when reaching step 3 (Choose Subscription) - only once
    useEffect(() => {
        if (
            currentStep === 3 &&
            showOnboarding &&
            !showSubscriptionDialog &&
            !hasAutoOpenedSubscriptionDialog
        ) {
            setShowSubscriptionDialog(true)
            setHasAutoOpenedSubscriptionDialog(true)
        }
    }, [
        currentStep,
        showOnboarding,
        showSubscriptionDialog,
        hasAutoOpenedSubscriptionDialog,
    ])

    // Fetch specialties on component mount
    useEffect(() => {
        const fetchSpecialties = async () => {
            try {
                setIsLoadingSpecialties(true)
                const response = await membershipApi.getSpecialties()
                setSpecialties(response.data || response)
            } catch (error) {
                console.error('Error fetching specialties:', error)
            } finally {
                setIsLoadingSpecialties(false)
            }
        }

        const fetchProvidedServices = async () => {
            try {
                setIsLoadingProvidedServices(true)
                const response = await api.get('/agency/provided-services')
                setProvidedServices(response.data?.data || response.data || response)
            } catch (error) {
                console.error('Error fetching provided services:', error)
            } finally {
                setIsLoadingProvidedServices(false)
            }
        }

        fetchSpecialties()
        fetchProvidedServices()
    }, [])

    // Cleanup object URLs on unmount
    useEffect(() => {
        return () => {
            // Cleanup main license preview
            if (onboardingData.licenseFilePreview) {
                URL.revokeObjectURL(onboardingData.licenseFilePreview)
            }
            // Cleanup additional license previews
            additionalLicenses.forEach(license => {
                if (license.licenseFilePreview) {
                    URL.revokeObjectURL(license.licenseFilePreview)
                }
            })
        }
    }, [onboardingData.licenseFilePreview, additionalLicenses])

    // Debounce search input for payer search
    const debouncedPayerSearch = useDebounce(payerSearchInput, 300)
    const debouncedBoardNameSearch = useDebounce(boardNameSearch, 300)
    const debouncedBoardSpecialitySearch = useDebounce(boardSpecialitySearch, 300)
    const debouncedBoardSubSpecialitySearch = useDebounce(
        boardSubSpecialitySearch,
        300
    )

    // Search board options when debounced input changes
    useEffect(() => {
        const searchBoardNames = async () => {
            setIsSearchingBoardName(true)
            try {
                const response = await membershipApi.getBoardNames(
                    debouncedBoardNameSearch
                )
                const data = response?.data || response
                setBoardNameOptions(Array.isArray(data) ? data : [])
            } catch (error) {
                console.error('Error fetching board names:', error)
                setBoardNameOptions([])
            } finally {
                setIsSearchingBoardName(false)
            }
        }
        searchBoardNames()
    }, [debouncedBoardNameSearch])

    useEffect(() => {
        const searchBoardSpecialities = async () => {
            // Only fetch if a board is selected
            if (!onboardingData.boardName) {
                setBoardSpecialityOptions([])
                return
            }
            setIsSearchingBoardSpeciality(true)
            try {
                const response = await membershipApi.getBoardSpecialities(
                    debouncedBoardSpecialitySearch,
                    onboardingData.boardName
                )
                const data = response?.data || response
                setBoardSpecialityOptions(Array.isArray(data) ? data : [])
            } catch (error) {
                console.error('Error fetching board specialities:', error)
                setBoardSpecialityOptions([])
            } finally {
                setIsSearchingBoardSpeciality(false)
            }
        }
        searchBoardSpecialities()
    }, [debouncedBoardSpecialitySearch, onboardingData.boardName])

    useEffect(() => {
        const searchBoardSubSpecialities = async () => {
            // Only fetch if a speciality is selected
            if (!onboardingData.boardSpeciality) {
                setBoardSubSpecialityOptions([])
                return
            }
            setIsSearchingBoardSubSpeciality(true)
            try {
                const response = await membershipApi.getBoardSubspecialities(
                    debouncedBoardSubSpecialitySearch,
                    onboardingData.boardSpeciality
                )
                const data = response?.data || response
                setBoardSubSpecialityOptions(Array.isArray(data) ? data : [])
            } catch (error) {
                console.error('Error fetching board subspecialities:', error)
                setBoardSubSpecialityOptions([])
            } finally {
                setIsSearchingBoardSubSpeciality(false)
            }
        }
        searchBoardSubSpecialities()
    }, [debouncedBoardSubSpecialitySearch, onboardingData.boardSpeciality])

    // Search payers when debounced input changes
    useEffect(() => {
        const searchPayers = async () => {
            if (debouncedPayerSearch.trim().length === 0) {
                setPayerSearchOptions([])
                setIsSearchingPayers(false)
                return
            }

            // Set loading state but keep previous results visible
            setIsSearchingPayers(true)

            try {
                const response = await membershipApi.searchPayers(debouncedPayerSearch)
                const payersData = response.data || response
                // Update options with new results (even if empty - user searched and got no results)
                if (Array.isArray(payersData)) {
                    setPayerSearchOptions(payersData)
                }
            } catch (error) {
                console.error('Error searching payers:', error)
                // Don't clear options on error, keep previous results visible
            } finally {
                setIsSearchingPayers(false)
            }
        }

        searchPayers()
    }, [debouncedPayerSearch])

    // Fetch states on component mount
    useEffect(() => {
        const fetchStates = async () => {
            try {
                setIsLoadingStates(true)
                const response = await api.get('/state/get-states')
                setStates(response.data || response)
            } catch (error) {
                console.error('Error fetching states:', error)
            } finally {
                setIsLoadingStates(false)
            }
        }

        fetchStates()
    }, [])

    // Fetch license types on component mount
    useEffect(() => {
        const fetchLicenseTypes = async () => {
            try {
                setIsLoadingLicenseTypes(true)
                const response = await membershipApi.getSpecialtyRoles()
                const licenseTypesData = response.data || response
                if (Array.isArray(licenseTypesData)) {
                    setLicenseTypes(
                        licenseTypesData.filter((lt: LicenseType) => !lt.isDeleted)
                    )
                }
            } catch (error) {
                console.error('Error fetching specialty license types:', error)
            } finally {
                setIsLoadingLicenseTypes(false)
            }
        }

        fetchLicenseTypes()
    }, [])

    // Scroll to top when step changes
    useEffect(() => {
        // Skip scroll on initial mount
        if (currentStep === 0 && !showOnboarding) {
            return
        }

        // Delay to allow animations to start, then scroll
        const timer = setTimeout(() => {
            // Try multiple scroll methods for better compatibility
            const scrollToTop = () => {
                // Method 1: Scroll window to top
                window.scrollTo({ top: 0, behavior: 'smooth' })

                // Method 2: Also try scrolling the document element
                if (document.documentElement) {
                    document.documentElement.scrollTo({ top: 0, behavior: 'smooth' })
                }

                // Method 3: Try scrolling body as well
                if (document.body) {
                    document.body.scrollTo({ top: 0, behavior: 'smooth' })
                }
            }

            scrollToTop()
        }, 300) // Increased delay to account for animations

        return () => clearTimeout(timer)
    }, [currentStep, showOnboarding])

    // Handle plan selection from subscription dialog
    const handlePlanSelect = async (
        plan: MembershipPlan,
        cycle: 'monthly' | 'yearly',
        amountPaid: string,
        totalPrice: string,
        couponApplied: string
    ) => {
        try {
            setIsProcessingPayment(true)
            setPaymentError(null)
            setBillingCycle(cycle)
            setSelectedPlan(plan)
            setDiscountedPrice(parseFloat(amountPaid))

            // Close subscription dialog immediately
            setShowSubscriptionDialog(false)
            // Open payment modal immediately with loading state
            setShowPaymentModal(true)

            // Disable draft persistence - will be removed from local storage when user clicks handlePayment
            draftPersistenceEnabledRef.current = false

            console.log('Step 1: Fetching price information for plan...')

            // Step 1: Fetch price IDs for the selected plan
            const priceResponse = await membershipApi.getMembershipSubscriptionPrice(
                plan._id
            )
            const prices = priceResponse.data

            // Find the appropriate price ID based on billing cycle
            const selectedPrice = prices.find(
                p => p.billing_frequency.toLowerCase() === cycle
            )

            if (!selectedPrice) {
                throw new Error(`No price found for ${cycle} billing`)
            }

            console.log('Price found:', selectedPrice)
            console.log('couponApplied:', couponApplied)
            console.log('amountPaid (discounted):', amountPaid)
            console.log('totalPrice (discounted):', totalPrice)

            // Step 2: Ensure therapist account already exists
            let currentTherapistId = therapistId

            if (!currentTherapistId) {
                throw new Error('Agency account not found. Please complete the previous steps.')
            }

            // Step 4: Prepare billing address
            const billingAddress = {
                address1: formData.billingAddressLine,
                address2: '',
                city: formData.billingCity,
                country: formData.country,
                state: formData.billingState,
                postal_code: formData.billingZipCode,
            }

            console.log('Step 3: Creating subscription with payment intent...')

            // Step 4: Create subscription
            const subscriptionResponse = await membershipApi.createAgencySubscription({
                agency_id: currentTherapistId,
                members_price: [{ price_id: selectedPrice.stripe_price_id }],
                total_price: parseFloat(totalPrice) ?? parseFloat(selectedPrice.price),
                amount_paid: parseFloat(amountPaid) ?? parseFloat(selectedPrice.price),
                // total_price: parseFloat(selectedPrice.price),
                // amount_paid: parseFloat(selectedPrice.price),
                billing_address: billingAddress,
                // coupon: couponApplied,
                // Add coupon only if applied
                ...(couponApplied !== '' && {
                    coupon: couponApplied,
                }),
            })

            console.log('Subscription created:', subscriptionResponse)

            if (!subscriptionResponse.client_secret) {
                throw new Error('Failed to create subscription payment intent')
            }

            // Step 6: Store subscription details and show payment modal
            setSubscriptionDetails({
                orderId: subscriptionResponse.orderId,
                client_secret: subscriptionResponse.client_secret,
                customerId: subscriptionResponse.customerId,
                ephemeralKey: subscriptionResponse.ephemeralKey,
            })

            console.log('Ready for payment confirmation')

            // Payment modal is already open from the beginning, it will show loading until clientSecret is ready
        } catch (error) {
            console.error('Error in plan selection flow:', error)
            const errorMessage =
                error instanceof Error ? error.message : 'An error occurred'
            setPaymentError(errorMessage)
            // Keep payment modal open to show error
            // The modal will display the error state
        } finally {
            setIsProcessingPayment(false)
        }
    }

    // Handle payment success
    const handlePaymentSuccess = async (paymentIntentId: string) => {
        try {
            console.log('Payment successful! Intent ID:', paymentIntentId)

            // Mark subscription payment as successful
            setSubscriptionPaymentSuccess(true)

            // User purchased a plan — clear saved draft from localStorage
            try {
                draftPersistenceEnabledRef.current = false
                localStorage.removeItem(THERAPIST_CREATE_ACCOUNT_DRAFT_KEY)
            } catch { }

            // Close payment modal and navigate to step 5 immediately
            setShowPaymentModal(false)
            setCurrentStep(4) // Step 5 is index 4 (0-based)
        } catch (error) {
            console.error('Error after payment:', error)
            setPaymentError('Error processing your payment. Please contact support.')
        }
    }















    return (
        <Box
            sx={{
                minHeight: '100vh',
                bgcolor: 'white',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* Header */}
            <Fade in timeout={800}>
                <AppBar
                    position="static"
                    elevation={0}
                    sx={{
                        bgcolor: 'white',
                        borderBottom: '1px solid',
                        borderColor: 'grey.200',
                        height: '80px',
                        flexShrink: 0,
                    }}
                >
                    <Toolbar
                        sx={{ justifyContent: 'space-between', py: 1, height: '100%' }}
                    >
                        <Slide direction="right" in timeout={1000}>
                            <Box
                                component="img"
                                src="/logo.svg"
                                alt="PillarThera Logo"
                                sx={{
                                    height: { xs: 32, sm: 60 },
                                    width: 'auto',
                                    cursor: 'pointer',
                                    transition: 'opacity 0.3s ease',
                                    '&:hover': {
                                        opacity: 0.8,
                                    },
                                }}
                            />
                        </Slide>

                        {/* <Slide
              direction="left"
              in
              timeout={1000}
              style={{ transitionDelay: '200ms' }}
            >
              <Button
                variant="outlined"
                startIcon={<Person />}
                onClick={handleLoginClick}
                sx={{
                  color: 'grey.600',
                  borderColor: 'grey.300',
                  borderRadius: 2,
                  px: { sm: 2, md: 3 },
                  py: 1,
                  fontSize: { sm: '0.75rem', md: '0.875rem' },
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'grey.400',
                    bgcolor: 'grey.50',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  },
                }}
              >
                Sign Up / Login
              </Button>
            </Slide> */}
                    </Toolbar>
                </AppBar>
            </Fade>

            {/* Main Content */}
            <Box
                ref={formContainerRef}
                sx={{
                    display: 'flex',
                    flex: 1,
                    minHeight: 0,
                    justifyContent: 'center',
                    alignItems: 'center',
                    px: { xs: 2, sm: 3, md: 4 }, // Reduced horizontal padding
                    py: { xs: 3, sm: 4 }, // Reduced vertical padding
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        width: '100%',
                        maxWidth: '1600px', // Increased max width
                        gap: { xs: 0, md: 3, lg: 4 }, // Reduced gap
                        alignItems: 'flex-start',
                    }}
                >
                    {/* Left Section - Image */}
                    <Zoom in timeout={1200} style={{ transitionDelay: '300ms' }}>
                        <Box
                            sx={{
                                flex: { xs: 0, md: 1 },
                                position: 'sticky',
                                top: { md: 24, lg: 32 }, // Sticky positioning with top offset
                                display: { xs: 'none', md: 'block' },
                                overflow: 'hidden',
                                bgcolor: 'white',
                                borderRadius: 3,
                                minHeight: { md: '600px', lg: '700px' },
                                maxHeight: {
                                    md: 'calc(100vh - 48px)',
                                    lg: 'calc(100vh - 64px)',
                                }, // Constrain to viewport height
                                alignSelf: 'flex-start', // Align to top
                            }}
                        >
                            <Box
                                component="img"
                                src="/agencyImage/createAccount.jpg"
                                alt="Agency create account illustration"
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    objectPosition: 'center',
                                    transition: 'transform 0.3s ease',
                                    '&:hover': {
                                        transform: 'scale(1.02)',
                                    },
                                }}
                            />
                        </Box>
                    </Zoom>

                    {/* Right Section - Create Account Form or Onboarding */}
                    <Slide
                        direction="left"
                        in
                        timeout={1000}
                        style={{ transitionDelay: '500ms' }}
                    >
                        <Box
                            sx={{
                                flex: { xs: 1, md: 1.5 }, // Increased flex for more width
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                            }}
                        >
                            {/* Form Card */}
                            <Grow in timeout={1200} style={{ transitionDelay: '700ms' }}>
                                <Paper
                                    ref={formPaperRef}
                                    elevation={2}
                                    sx={{
                                        p: { xs: 3, sm: 4, md: 5 }, // Reduced padding for more space
                                        borderRadius: 4,
                                        width: '100%',
                                        maxWidth: {
                                            xs: '100%',
                                            sm: '700px', // Increased max width
                                            md: '800px',
                                            lg: '900px',
                                            xl: '1000px',
                                        },
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                        transition: 'box-shadow 0.3s ease, transform 0.3s ease',
                                        '&:hover': {
                                            boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                                            transform: 'translateY(-2px)',
                                        },
                                    }}
                                >
                                    {!showOnboarding ? (
                                        <>
                                            {/* Header */}
                                            <Fade
                                                in
                                                timeout={1000}
                                                style={{ transitionDelay: '900ms' }}
                                            >
                                                <Box sx={{ textAlign: 'center', mb: 4 }}>
                                                    <Typography
                                                        variant="h4"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: '#574F75',
                                                            mb: 1,
                                                            fontSize: {
                                                                xs: '1.75rem',
                                                                sm: '2rem',
                                                                md: '2.25rem',
                                                            },
                                                        }}
                                                    >
                                                        Create your account
                                                    </Typography>
                                                </Box>
                                            </Fade>

                                            {/* Create Account Form */}
                                            <Box
                                                component="form"
                                                onSubmit={handleSubmit}
                                                autoComplete="off"
                                            >
                                                {/* Legal Business Name */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1100ms' }}
                                                >
                                                    <Box sx={{ mb: 3 }}>
                                                        <Typography variant="body2" sx={accountFieldLabelSx}>
                                                            Legal Business Name *
                                                        </Typography>
                                                        <TextField
                                                            type="text"
                                                            placeholder="Enter your agency's legal business name"
                                                            name="agencyName"
                                                            value={formData.agencyName}
                                                            onChange={handleInputChange('agencyName')}
                                                            required
                                                            fullWidth
                                                            variant="outlined"
                                                            sx={accountFieldSx}
                                                        />
                                                    </Box>
                                                </Fade>

                                                {/* DBA Name */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1150ms' }}
                                                >
                                                    <Box sx={{ mb: 3 }}>
                                                        <Typography variant="body2" sx={accountFieldLabelSx}>
                                                            DBA Name *
                                                        </Typography>
                                                        <TextField
                                                            fullWidth
                                                            placeholder="Enter DBA (Doing Business As) name"
                                                            value={formData.dbaName}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                                const value = e.target.value
                                                                setFormData(prev => ({ ...prev, dbaName: value }))
                                                                if (dbaNameError) setDbaNameError('')
                                                            }}
                                                            required
                                                            error={!!dbaNameError}
                                                            helperText={dbaNameError}
                                                            sx={accountFieldSx}
                                                        />
                                                    </Box>
                                                </Fade>

                                                {/* EIN & NPI Row */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1200ms' }}
                                                >
                                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                                        <Grid item xs={6}>
                                                            <Typography variant="body2" sx={accountFieldLabelSx}>
                                                                EIN *
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                placeholder="00-0000000"
                                                                value={formData.ein}
                                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                                    const value = e.target.value
                                                                    setFormData(prev => ({ ...prev, ein: value }))
                                                                    if (einError) setEinError('')
                                                                }}
                                                                onBlur={() => {
                                                                    if (formData.ein.trim() && !isValidEIN(formData.ein)) {
                                                                        setEinError(ValidationRules.ein.message)
                                                                    } else {
                                                                        setEinError('')
                                                                    }
                                                                }}
                                                                required
                                                                error={!!einError}
                                                                helperText={einError}
                                                                sx={accountFieldSx}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography variant="body2" sx={accountFieldLabelSx}>
                                                                NPI (Organization NPI if Applicable)
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                placeholder="10-digit NPI number"
                                                                value={formData.npiOrganization}
                                                                onChange={handleInputChange('npiOrganization')}
                                                                sx={accountFieldSx}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Fade>

                                                {/* License Number & License Expiration Row */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1250ms' }}
                                                >
                                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                                        <Grid item xs={6}>
                                                            <Typography variant="body2" sx={accountFieldLabelSx}>
                                                                License Number
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                placeholder="Enter License Number"
                                                                value={formData.agencyLicenseNumber}
                                                                onChange={handleInputChange('agencyLicenseNumber')}
                                                                sx={accountFieldSx}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography variant="body2" sx={accountFieldLabelSx}>
                                                                License Expiration
                                                            </Typography>
                                                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                                <DatePicker
                                                                    value={formData.agencyLicenseExpiration}
                                                                    onChange={date => {
                                                                        const dateObj =
                                                                            date instanceof Date
                                                                                ? date
                                                                                : (date as any)?.toDate
                                                                                    ? (date as any).toDate()
                                                                                    : date
                                                                                        ? new Date(date as any)
                                                                                        : null
                                                                        setFormData(prev => ({
                                                                            ...prev,
                                                                            agencyLicenseExpiration: dateObj,
                                                                        }))
                                                                    }}
                                                                    slotProps={{
                                                                        textField: {
                                                                            fullWidth: true,
                                                                            placeholder: 'mm/dd/yyyy',
                                                                            sx: accountDatePickerSx,
                                                                        },
                                                                    }}
                                                                />
                                                            </LocalizationProvider>
                                                        </Grid>
                                                    </Grid>
                                                </Fade>

                                                {/* State of Operations */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1300ms' }}
                                                >
                                                    <Box sx={{ mb: 3 }}>
                                                        <Typography variant="body2" sx={accountFieldLabelSx}>
                                                            State of Operations *
                                                        </Typography>
                                                        <FormControl fullWidth error={!!stateOfOperationsError}>
                                                            <Select
                                                                multiple
                                                                displayEmpty
                                                                value={formData.stateOfOperations}
                                                                onChange={handleStateOfOperationsChange}
                                                                open={stateOfOperationsSelectOpen}
                                                                onOpen={() => {
                                                                    if (!isDeletingChip) setStateOfOperationsSelectOpen(true)
                                                                }}
                                                                onClose={() => setStateOfOperationsSelectOpen(false)}
                                                                sx={{
                                                                    borderRadius: 2,
                                                                    bgcolor: 'white',
                                                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'grey.300' },
                                                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'grey.400' },
                                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: '#574F75',
                                                                        borderWidth: 2,
                                                                    },
                                                                }}
                                                                renderValue={selected => {
                                                                    const selectedValues = selected as string[]
                                                                    if (selectedValues.length === 0) {
                                                                        return (
                                                                            <Typography sx={{ color: 'grey.500', fontSize: '0.875rem' }}>
                                                                                Select states where your agency operates
                                                                            </Typography>
                                                                        )
                                                                    }
                                                                    return (
                                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                            {selectedValues.map(name => (
                                                                                <Chip
                                                                                    key={name}
                                                                                    label={name}
                                                                                    size="small"
                                                                                    onDelete={() => handleRemoveStateOfOperations(name)}
                                                                                    onMouseDown={e => e.stopPropagation()}
                                                                                    sx={{
                                                                                        bgcolor: '#EFEBFD',
                                                                                        color: '#574F75',
                                                                                        '& .MuiChip-deleteIcon': {
                                                                                            color: '#574F75',
                                                                                            '&:hover': { color: '#3f3a5c' },
                                                                                        },
                                                                                    }}
                                                                                />
                                                                            ))}
                                                                        </Box>
                                                                    )
                                                                }}
                                                            >
                                                                {states.map(s => (
                                                                    <MenuItem key={s._id} value={s.name}>
                                                                        {s.name}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                            {stateOfOperationsError && (
                                                                <FormHelperText>{stateOfOperationsError}</FormHelperText>
                                                            )}
                                                        </FormControl>
                                                    </Box>
                                                </Fade>

                                                {/* Business Email */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1350ms' }}
                                                >
                                                    <Box sx={{ mb: 3 }}>
                                                        <Typography variant="body2" sx={accountFieldLabelSx}>
                                                            Business Email *
                                                        </Typography>
                                                        <TextField
                                                            fullWidth
                                                            type="email"
                                                            placeholder="agency@example.com"
                                                            value={formData.businessEmail}
                                                            inputProps={{ autoComplete: 'off' }}
                                                            onChange={e => {
                                                                const value = e.target.value
                                                                setFormData(prev => ({ ...prev, businessEmail: value }))
                                                                if (value && value.trim()) {
                                                                    setBusinessEmailError(
                                                                        isValidEmail(value) ? '' : ValidationRules.email.message
                                                                    )
                                                                } else {
                                                                    setBusinessEmailError('')
                                                                }
                                                            }}
                                                            onBlur={e => {
                                                                const value = e.target.value
                                                                if (value && value.trim() && !isValidEmail(value)) {
                                                                    setBusinessEmailError(ValidationRules.email.message)
                                                                }
                                                            }}
                                                            required
                                                            error={!!businessEmailError}
                                                            helperText={businessEmailError}
                                                            sx={accountFieldSx}
                                                        />
                                                    </Box>
                                                </Fade>

                                                {/* Business Phone Number & Website Row */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1400ms' }}
                                                >
                                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                                        <Grid item xs={6}>
                                                            <Typography variant="body2" sx={accountFieldLabelSx}>
                                                                Business Phone Number
                                                            </Typography>
                                                            <PhoneInput
                                                                fullWidth
                                                                value={formData.businessPhoneNumber}
                                                                onChange={value => {
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        businessPhoneNumber: value,
                                                                    }))
                                                                    if (isBusinessPhoneTouched) {
                                                                        setBusinessPhoneError(
                                                                            value.trim() ? getUSPhoneErrorMessage(value) : ''
                                                                        )
                                                                    }
                                                                }}
                                                                onBlur={() => {
                                                                    setIsBusinessPhoneTouched(true)
                                                                    setBusinessPhoneError(
                                                                        formData.businessPhoneNumber.trim()
                                                                            ? getUSPhoneErrorMessage(formData.businessPhoneNumber)
                                                                            : ''
                                                                    )
                                                                }}
                                                                onValidationChange={isValid => {
                                                                    if (!isBusinessPhoneTouched) return
                                                                    if (!formData.businessPhoneNumber.trim()) {
                                                                        setBusinessPhoneError('')
                                                                        return
                                                                    }
                                                                    setBusinessPhoneError(
                                                                        isValid ? '' : getUSPhoneErrorMessage(formData.businessPhoneNumber)
                                                                    )
                                                                }}
                                                                error={!!businessPhoneError}
                                                                helperText={businessPhoneError}
                                                                sx={accountFieldSx}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography variant="body2" sx={accountFieldLabelSx}>
                                                                Website
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                placeholder="https://www.youragency.com"
                                                                value={formData.website}
                                                                onChange={handleInputChange('website')}
                                                                sx={accountFieldSx}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Fade>

                                                {/* Business Address Section Heading */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1450ms' }}
                                                >
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography
                                                            variant="h6"
                                                            sx={{ color: 'grey.800', fontWeight: 600, fontSize: '1.05rem' }}
                                                        >
                                                            Business Address
                                                        </Typography>
                                                    </Box>
                                                </Fade>

                                                {/* Address Field */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1500ms' }}
                                                >
                                                    <Box sx={{ mb: 3 }}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: 'grey.700',
                                                                mb: 1,
                                                                fontWeight: 500,
                                                                fontSize: '0.875rem',
                                                            }}
                                                        >
                                                            Street Address 1*
                                                        </Typography>
                                                        <AddressAutocomplete
                                                            value={formData.address}
                                                            onChange={(address, components) => {
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    address,
                                                                    // Only update city, state, zip if components are provided and have values
                                                                    // Don't clear them when address is cleared (components will have empty strings)
                                                                    ...(components && {
                                                                        ...(components.city && {
                                                                            city: components.city,
                                                                        }),
                                                                        ...(components.country && {
                                                                            country: components.country,
                                                                        }),
                                                                        ...(components.state && {
                                                                            state: components.state,
                                                                        }),
                                                                        ...((components.zipCode ||
                                                                            components.postalCode) && {
                                                                            zipCode:
                                                                                components.zipCode ||
                                                                                components.postalCode,
                                                                        }),
                                                                        // Capture latitude and longitude
                                                                        ...(components.latitude !== undefined && {
                                                                            latitude: components.latitude,
                                                                        }),
                                                                        ...(components.longitude !== undefined && {
                                                                            longitude: components.longitude,
                                                                        }),
                                                                    }),
                                                                }))
                                                            }}
                                                            placeholder="Start typing an Street Address 1..."
                                                            addressType="address"
                                                            borderColor='#574F75'
                                                        />

                                                    </Box>

                                                </Fade>
                                                <Box sx={{ mb: 3 }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: 'grey.700',
                                                            mb: 1,
                                                            fontWeight: 500,
                                                            fontSize: '0.875rem',
                                                        }}
                                                    >
                                                        Street Address 2
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        placeholder="Enter Street Address 2"
                                                        value={formData.addressLine2 || ''}
                                                        onChange={handleInputChange('addressLine2')}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                bgcolor: 'white',
                                                                borderRadius: 2,
                                                                transition: 'all 0.3s ease',
                                                                '& fieldset': { borderColor: 'grey.300' },
                                                                '&:hover fieldset': { borderColor: 'grey.400' },
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: '#574F75',
                                                                    borderWidth: 2,
                                                                },
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                py: 1.5,
                                                                fontSize: '0.875rem',
                                                            },
                                                        }}
                                                    />

                                                </Box>

                                                {/* Country → State cascading dropdowns */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1600ms' }}
                                                >
                                                    <Box sx={{ mb: 3 }}>
                                                        <LocationSelector
                                                            country={formData.country}
                                                            state={formData.state}
                                                            onCountryChange={name =>
                                                                setFormData(prev => ({ ...prev, country: name, state: '' }))
                                                            }
                                                            onStateChange={name =>
                                                                setFormData(prev => ({ ...prev, state: name }))
                                                            }
                                                            accentColor="#574F75"
                                                            required
                                                        />
                                                    </Box>
                                                </Fade>

                                                {/* City & Zip Code Row */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1650ms' }}
                                                >
                                                    <Grid container spacing={2} sx={{ mb: 4 }}>
                                                        <Grid item xs={6}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: 'grey.700',
                                                                    mb: 1,
                                                                    fontWeight: 500,
                                                                    fontSize: '0.875rem',
                                                                }}
                                                            >
                                                                City *
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                placeholder="Enter City"
                                                                value={formData.city}
                                                                onChange={handleInputChange('city')}
                                                                required
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        bgcolor: 'white',
                                                                        borderRadius: 2,
                                                                        transition: 'all 0.3s ease',
                                                                        '& fieldset': { borderColor: 'grey.300' },
                                                                        '&:hover fieldset': { borderColor: 'grey.400' },
                                                                        '&.Mui-focused fieldset': { borderColor: '#574F75', borderWidth: 2 },
                                                                    },
                                                                    '& .MuiInputBase-input': { py: 1.5, fontSize: '0.875rem' },
                                                                }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: 'grey.700',
                                                                    mb: 1,
                                                                    fontWeight: 500,
                                                                    fontSize: '0.875rem',
                                                                }}
                                                            >
                                                                Zip Code *
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                placeholder="Enter Zip Code"
                                                                value={formData.zipCode}
                                                                onChange={handleInputChange('zipCode')}
                                                                required
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        bgcolor: 'white',
                                                                        borderRadius: 2,
                                                                        transition: 'all 0.3s ease',
                                                                        '& fieldset': { borderColor: 'grey.300' },
                                                                        '&:hover fieldset': { borderColor: 'grey.400' },
                                                                        '&.Mui-focused fieldset': { borderColor: '#574F75', borderWidth: 2 },
                                                                    },
                                                                    '& .MuiInputBase-input': { py: 1.5, fontSize: '0.875rem' },
                                                                }}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Fade>

                                                {/* Primary Administrator Section Heading */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1700ms' }}
                                                >
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography
                                                            variant="h6"
                                                            sx={{ color: 'grey.800', fontWeight: 600, fontSize: '1.05rem' }}
                                                        >
                                                            Primary Administrator
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: 'grey.500', mt: 0.5 }}>
                                                            The person who will be the primary contact and administrator for this account.
                                                        </Typography>
                                                    </Box>
                                                </Fade>

                                                {/* First Name & Last Name Row */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1750ms' }}
                                                >
                                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                                        <Grid item xs={6}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: 'grey.700',
                                                                    mb: 1,
                                                                    fontWeight: 500,
                                                                    fontSize: '0.875rem',
                                                                }}
                                                            >
                                                                First Name *
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                value={formData.firstName}
                                                                onChange={handleInputChange('firstName')}
                                                                placeholder="Enter First Name"
                                                                required
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        bgcolor: 'white',
                                                                        borderRadius: 2,
                                                                        transition: 'all 0.3s ease',
                                                                        '& fieldset': {
                                                                            borderColor: 'grey.300',
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor: 'grey.400',
                                                                            transform: 'scale(1.02)',
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: '#574F75',
                                                                            borderWidth: 2,
                                                                        },
                                                                    },
                                                                    '& .MuiInputBase-input': {
                                                                        py: 1.5,
                                                                        fontSize: '0.875rem',
                                                                    },
                                                                }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: 'grey.700',
                                                                    mb: 1,
                                                                    fontWeight: 500,
                                                                    fontSize: '0.875rem',
                                                                }}
                                                            >
                                                                Last Name *
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                placeholder="Enter Last Name"
                                                                value={formData.lastName}
                                                                onChange={handleInputChange('lastName')}
                                                                required
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        bgcolor: 'white',
                                                                        borderRadius: 2,
                                                                        transition: 'all 0.3s ease',
                                                                        '& fieldset': {
                                                                            borderColor: 'grey.300',
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor: 'grey.400',
                                                                            transform: 'scale(1.02)',
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: '#574F75',
                                                                            borderWidth: 2,
                                                                        },
                                                                    },
                                                                    '& .MuiInputBase-input': {
                                                                        py: 1.5,
                                                                        fontSize: '0.875rem',
                                                                    },
                                                                }}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Fade>

                                                {/* Email Address & Phone Number Row */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1800ms' }}
                                                >
                                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                                        <Grid item xs={6}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: 'grey.700',
                                                                    mb: 1,
                                                                    fontWeight: 500,
                                                                    fontSize: '0.875rem',
                                                                }}
                                                            >
                                                                Email Address *
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                type="email"
                                                                placeholder="Enter Email"
                                                                value={formData.email}
                                                                inputProps={{ autoComplete: 'off' }}
                                                                onChange={e => {
                                                                    const emailValue = e.target.value
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        email: emailValue,
                                                                    }))
                                                                    // Validate email format in real-time using validation utility
                                                                    if (emailValue && emailValue.trim()) {
                                                                        if (!isValidEmail(emailValue)) {
                                                                            setEmailVerificationError(
                                                                                ValidationRules.email.message
                                                                            )
                                                                        } else {
                                                                            // Clear error if format is valid
                                                                            setEmailVerificationError('')
                                                                        }
                                                                    } else {
                                                                        // Clear error when field is empty
                                                                        setEmailVerificationError('')
                                                                    }
                                                                }}
                                                                onBlur={e => {
                                                                    // Validate on blur as well
                                                                    const emailValue = e.target.value
                                                                    if (emailValue && emailValue.trim()) {
                                                                        if (!isValidEmail(emailValue)) {
                                                                            setEmailVerificationError(
                                                                                ValidationRules.email.message
                                                                            )
                                                                        }
                                                                    }
                                                                }}
                                                                required
                                                                error={!!emailVerificationError}
                                                                helperText={emailVerificationError}
                                                                disabled={isVerifyingEmail}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        bgcolor: 'white',
                                                                        borderRadius: 2,
                                                                        transition: 'all 0.3s ease',
                                                                        '& fieldset': {
                                                                            borderColor: emailVerificationError
                                                                                ? 'error.main'
                                                                                : 'grey.300',
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor: emailVerificationError
                                                                                ? 'error.main'
                                                                                : 'grey.400',
                                                                            transform: 'scale(1.02)',
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: emailVerificationError
                                                                                ? 'error.main'
                                                                                : '#574F75',
                                                                            borderWidth: 2,
                                                                        },
                                                                    },
                                                                    '& .MuiInputBase-input': {
                                                                        py: 1.5,
                                                                        fontSize: '0.875rem',
                                                                    },
                                                                }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: 'grey.700',
                                                                    mb: 1,
                                                                    fontWeight: 500,
                                                                    fontSize: '0.875rem',
                                                                }}
                                                            >
                                                                Phone Number *
                                                            </Typography>
                                                            <PhoneInput
                                                                fullWidth
                                                                value={formData.phoneNumber}
                                                                onChange={value => {
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        phoneNumber: value,
                                                                    }))
                                                                    if (isPhoneNumberTouched) {
                                                                        setPhoneNumberError(
                                                                            getUSPhoneErrorMessage(value)
                                                                        )
                                                                    }
                                                                }}
                                                                onBlur={() => {
                                                                    setIsPhoneNumberTouched(true)
                                                                    setPhoneNumberError(
                                                                        getUSPhoneErrorMessage(formData.phoneNumber)
                                                                    )
                                                                }}
                                                                onValidationChange={isValid => {
                                                                    if (!isPhoneNumberTouched) {
                                                                        return
                                                                    }
                                                                    setPhoneNumberError(
                                                                        isValid
                                                                            ? ''
                                                                            : getUSPhoneErrorMessage(
                                                                                formData.phoneNumber
                                                                            )
                                                                    )
                                                                }}
                                                                required
                                                                error={!!phoneNumberError}
                                                                helperText={phoneNumberError}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        bgcolor: 'white',
                                                                        borderRadius: 2,
                                                                        transition: 'all 0.3s ease',
                                                                        '& fieldset': {
                                                                            borderColor: phoneNumberError
                                                                                ? 'error.main'
                                                                                : 'grey.300',
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor: phoneNumberError
                                                                                ? 'error.main'
                                                                                : 'grey.400',
                                                                            transform: 'scale(1.02)',
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: phoneNumberError
                                                                                ? 'error.main'
                                                                                : '#574F75',
                                                                            borderWidth: 2,
                                                                        },
                                                                    },
                                                                    '& .MuiInputBase-input': {
                                                                        py: 1.5,
                                                                        fontSize: '0.875rem',
                                                                    },
                                                                }}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Fade>

                                                {/* Password & Confirm Password Row */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1850ms' }}
                                                >
                                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                                        <Grid item xs={6}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: 'grey.700',
                                                                    mb: 1,
                                                                    fontWeight: 500,
                                                                    fontSize: '0.875rem',
                                                                }}
                                                            >
                                                                Password *
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                type={showPassword ? 'text' : 'password'}
                                                                placeholder="Enter Password"
                                                                value={formData.password}
                                                                onChange={handleInputChange('password')}
                                                                inputProps={{ autoComplete: 'new-password' }}
                                                                required
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        bgcolor: 'white',
                                                                        borderRadius: 2,
                                                                        transition: 'all 0.3s ease',
                                                                        '& fieldset': {
                                                                            borderColor:
                                                                                formData.password.length > 0
                                                                                    ? passwordStrength.borderColor
                                                                                    : 'grey.300',
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor:
                                                                                formData.password.length > 0
                                                                                    ? "#574F75"
                                                                                    : 'grey.400',
                                                                            transform: 'scale(1.02)',
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: "#574F75",
                                                                            borderWidth: 2,
                                                                        },
                                                                    },
                                                                    '& .MuiInputBase-input': {
                                                                        py: 1.5,
                                                                        fontSize: '0.875rem',
                                                                    },
                                                                }}
                                                                InputProps={{
                                                                    endAdornment: (
                                                                        <InputAdornment position="end">
                                                                            <IconButton
                                                                                onClick={() =>
                                                                                    setShowPassword(!showPassword)
                                                                                }
                                                                                edge="end"
                                                                                sx={{
                                                                                    color: 'grey.400',
                                                                                    transition: 'transform 0.2s ease',
                                                                                    '&:hover': {
                                                                                        transform: 'scale(1.1)',
                                                                                    },
                                                                                }}
                                                                            >
                                                                                {showPassword ? (
                                                                                    <VisibilityOff />
                                                                                ) : (
                                                                                    <Visibility />
                                                                                )}
                                                                            </IconButton>
                                                                        </InputAdornment>
                                                                    ),
                                                                }}
                                                            />
                                                            {formData.password.length > 0 && (
                                                                <PasswordStrengthMeter
                                                                    strength={passwordStrength}
                                                                />
                                                            )}
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: 'grey.700',
                                                                    mb: 1,
                                                                    fontWeight: 500,
                                                                    fontSize: '0.875rem',
                                                                }}
                                                            >
                                                                Confirm Password *
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                type={showConfirmPassword ? 'text' : 'password'}
                                                                placeholder="Enter Confirm Password"
                                                                value={formData.confirmPassword}
                                                                onChange={handleInputChange('confirmPassword')}
                                                                inputProps={{ autoComplete: 'new-password' }}
                                                                required
                                                                error={!!confirmPasswordError}
                                                                helperText={confirmPasswordError}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        bgcolor: 'white',
                                                                        borderRadius: 2,
                                                                        transition: 'all 0.3s ease',
                                                                        '& fieldset': {
                                                                            borderColor: confirmPasswordError
                                                                                ? '#F44336'
                                                                                : 'grey.300',
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor: confirmPasswordError
                                                                                ? '#F44336'
                                                                                : 'grey.400',
                                                                            transform: 'scale(1.02)',
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: confirmPasswordError
                                                                                ? '#F44336'
                                                                                : '#574F75',
                                                                            borderWidth: 2,
                                                                        },
                                                                        '&.Mui-error fieldset': {
                                                                            borderColor: '#F44336',
                                                                        },
                                                                    },
                                                                    '& .MuiInputBase-input': {
                                                                        py: 1.5,
                                                                        fontSize: '0.875rem',
                                                                    },
                                                                }}
                                                                InputProps={{
                                                                    endAdornment: (
                                                                        <InputAdornment position="end">
                                                                            <IconButton
                                                                                onClick={() =>
                                                                                    setShowConfirmPassword(
                                                                                        !showConfirmPassword
                                                                                    )
                                                                                }
                                                                                edge="end"
                                                                                sx={{
                                                                                    color: 'grey.400',
                                                                                    transition: 'transform 0.2s ease',
                                                                                    '&:hover': {
                                                                                        transform: 'scale(1.1)',
                                                                                    },
                                                                                }}
                                                                            >
                                                                                {showConfirmPassword ? (
                                                                                    <VisibilityOff />
                                                                                ) : (
                                                                                    <Visibility />
                                                                                )}
                                                                            </IconButton>
                                                                        </InputAdornment>
                                                                    ),
                                                                }}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Fade>

                                                {/* Secondary Administrators (Optional) */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '1900ms' }}
                                                >
                                                    <Box sx={{ mb: 4 }}>
                                                        {/* <Box
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                mb: 1,
                                                            }}
                                                        >
                                                            <Box>
                                                                <Typography
                                                                    variant="h6"
                                                                    sx={{ color: 'grey.800', fontWeight: 600, fontSize: '1.05rem' }}
                                                                >
                                                                    Secondary Administrators
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ color: 'grey.500' }}>
                                                                    (Optional)
                                                                </Typography>
                                                            </Box>
                                                            <Button
                                                                startIcon={<Add />}
                                                                onClick={handleOpenAddAdminDialog}
                                                                sx={{
                                                                    color: '#574F75',
                                                                    textTransform: 'none',
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                Add Administrator
                                                            </Button>
                                                        </Box> */}
                                                        {secondaryAdministrators.length > 0 && (
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                {secondaryAdministrators.map(admin => (
                                                                    <Box
                                                                        key={admin.id}
                                                                        sx={{
                                                                            display: 'flex',
                                                                            justifyContent: 'space-between',
                                                                            alignItems: 'center',
                                                                            p: 1.5,
                                                                            borderRadius: 2,
                                                                            bgcolor: '#f8f9fa',
                                                                            border: '1px solid #e9ecef',
                                                                        }}
                                                                    >
                                                                        <Box>
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{ fontWeight: 600, color: 'grey.800' }}
                                                                            >
                                                                                {admin.firstName} {admin.lastName}
                                                                                {' - '}
                                                                                <Typography
                                                                                    component="span"
                                                                                    variant="body2"
                                                                                    sx={{ color: 'grey.500', fontWeight: 400 }}
                                                                                >
                                                                                    {admin.role}
                                                                                </Typography>
                                                                            </Typography>
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{ color: 'grey.600', fontSize: '0.8rem' }}
                                                                            >
                                                                                {admin.email}
                                                                            </Typography>
                                                                        </Box>
                                                                        <Button
                                                                            size="small"
                                                                            onClick={() =>
                                                                                handleRemoveSecondaryAdministrator(admin.id)
                                                                            }
                                                                            sx={{
                                                                                color: '#d32f2f',
                                                                                textTransform: 'none',
                                                                                fontWeight: 600,
                                                                                fontSize: '0.75rem',
                                                                            }}
                                                                        >
                                                                            Remove
                                                                        </Button>
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Fade>

                                                {/* Add Secondary Administrator Dialog */}
                                                <Dialog
                                                    open={showAddAdminDialog}
                                                    onClose={handleCloseAddAdminDialog}
                                                    maxWidth="sm"
                                                    fullWidth
                                                >
                                                    <DialogTitle>Add Secondary Administrator</DialogTitle>
                                                    <DialogContent>
                                                        <Grid container spacing={2} sx={{ mt: 1 }}>
                                                            <Grid item xs={6}>
                                                                <Typography variant="body2" sx={accountFieldLabelSx}>
                                                                    First Name *
                                                                </Typography>
                                                                <TextField
                                                                    fullWidth
                                                                    value={newAdminDraft.firstName}
                                                                    onChange={e =>
                                                                        setNewAdminDraft(prev => ({
                                                                            ...prev,
                                                                            firstName: e.target.value,
                                                                        }))
                                                                    }
                                                                    error={!!newAdminErrors.firstName}
                                                                    helperText={newAdminErrors.firstName}
                                                                    sx={accountFieldSx}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <Typography variant="body2" sx={accountFieldLabelSx}>
                                                                    Last Name *
                                                                </Typography>
                                                                <TextField
                                                                    fullWidth
                                                                    value={newAdminDraft.lastName}
                                                                    onChange={e =>
                                                                        setNewAdminDraft(prev => ({
                                                                            ...prev,
                                                                            lastName: e.target.value,
                                                                        }))
                                                                    }
                                                                    error={!!newAdminErrors.lastName}
                                                                    helperText={newAdminErrors.lastName}
                                                                    sx={accountFieldSx}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <Typography variant="body2" sx={accountFieldLabelSx}>
                                                                    Role / Title *
                                                                </Typography>
                                                                <TextField
                                                                    fullWidth
                                                                    placeholder="e.g. Director of Operations"
                                                                    value={newAdminDraft.role}
                                                                    onChange={e =>
                                                                        setNewAdminDraft(prev => ({
                                                                            ...prev,
                                                                            role: e.target.value,
                                                                        }))
                                                                    }
                                                                    error={!!newAdminErrors.role}
                                                                    helperText={newAdminErrors.role}
                                                                    sx={accountFieldSx}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <Typography variant="body2" sx={accountFieldLabelSx}>
                                                                    Email Address *
                                                                </Typography>
                                                                <TextField
                                                                    fullWidth
                                                                    type="email"
                                                                    value={newAdminDraft.email}
                                                                    onChange={e =>
                                                                        setNewAdminDraft(prev => ({
                                                                            ...prev,
                                                                            email: e.target.value,
                                                                        }))
                                                                    }
                                                                    error={!!newAdminErrors.email}
                                                                    helperText={newAdminErrors.email}
                                                                    sx={accountFieldSx}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </DialogContent>
                                                    <DialogActions>
                                                        <Button
                                                            onClick={handleCloseAddAdminDialog}
                                                            sx={{ color: 'grey.600', textTransform: 'none' }}
                                                        >
                                                            Cancel
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            onClick={handleAddSecondaryAdministrator}
                                                            sx={{
                                                                bgcolor: '#574F75',
                                                                textTransform: 'none',
                                                                '&:hover': { bgcolor: '#473f63' },
                                                            }}
                                                        >
                                                            Add
                                                        </Button>
                                                    </DialogActions>
                                                </Dialog>

                                                {/* Create Account Button */}
                                                <Fade
                                                    in
                                                    timeout={800}
                                                    style={{ transitionDelay: '2300ms' }}
                                                >
                                                    <Button
                                                        type="submit"
                                                        fullWidth
                                                        variant="contained"
                                                        disabled={isVerifyingEmail}
                                                        sx={{
                                                            bgcolor: '#574F75',
                                                            py: 1.5,
                                                            fontSize: '1rem',
                                                            fontWeight: 600,
                                                            borderRadius: 2,
                                                            mb: 3,
                                                            textTransform: 'none',
                                                            transition: 'all 0.3s ease',
                                                            '&:hover': {
                                                                bgcolor: '#574F75',
                                                                transform: 'translateY(-2px)',
                                                                boxShadow: '0 4px 12px rgba(46, 125, 175, 0.3)',
                                                            },
                                                            '&:disabled': {
                                                                bgcolor: '#9e9e9e',
                                                                cursor: 'not-allowed',
                                                            },
                                                        }}
                                                    >
                                                        {isVerifyingEmail ? (
                                                            <>
                                                                <CircularProgress
                                                                    size={20}
                                                                    sx={{ mr: 1, color: 'white' }}
                                                                />
                                                                Verifying Email...
                                                            </>
                                                        ) : (
                                                            'Create Agency Account'
                                                        )}
                                                    </Button>
                                                </Fade>
                                            </Box>


                                            <Fade
                                                in
                                                timeout={800}
                                                style={{ transitionDelay: '2500ms' }}
                                            >
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: 'grey.600',
                                                            fontSize: '0.875rem',

                                                        }}
                                                    >
                                                        By creating an account, you agree to our
                                                        <Link
                                                            component="button"
                                                            onClick={handleLoginLinkClick}
                                                            sx={{
                                                                color: '#574F75',
                                                                textDecoration: 'none',
                                                                fontWeight: 600,
                                                                margin: '0px 5px',
                                                                transition: 'all 0.3s ease',
                                                                '&:hover': {
                                                                    textDecoration: 'underline',
                                                                    transform: 'translateY(-1px)',
                                                                },
                                                            }}
                                                        >
                                                            Terms of Service
                                                        </Link>
                                                        and
                                                        <Link
                                                            component="button"
                                                            onClick={handleLoginLinkClick}
                                                            sx={{
                                                                color: '#574F75',
                                                                textDecoration: 'none',
                                                                fontWeight: 600,
                                                                margin: '0px 5px',
                                                                transition: 'all 0.3s ease',
                                                                '&:hover': {
                                                                    textDecoration: 'underline',
                                                                    transform: 'translateY(-1px)',
                                                                },
                                                            }}
                                                        >
                                                            Privacy Policy
                                                        </Link>
                                                    </Typography>
                                                </Box>
                                            </Fade>









                                            {/* Login Link */}
                                            <Fade
                                                in
                                                timeout={800}
                                                style={{ transitionDelay: '2500ms' }}
                                            >
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: 'grey.600',
                                                            fontSize: '0.875rem',
                                                        }}
                                                    >
                                                        Already have an account?{' '}
                                                        <Link
                                                            component="button"
                                                            onClick={handleLoginLinkClick}
                                                            sx={{
                                                                color: '#574F75',
                                                                textDecoration: 'none',
                                                                fontWeight: 600,
                                                                transition: 'all 0.3s ease',
                                                                '&:hover': {
                                                                    textDecoration: 'underline',
                                                                    transform: 'translateY(-1px)',
                                                                },
                                                            }}
                                                        >
                                                            Login
                                                        </Link>
                                                    </Typography>
                                                </Box>
                                            </Fade>
                                        </>
                                    ) : (
                                        // Onboarding Form
                                        <>
                                            {/* Onboarding Header */}
                                            <Fade in timeout={800}>
                                                <Box sx={{ mb: 4 }}>
                                                    <Typography
                                                        variant="h3"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: '#574F75',
                                                            mb: 1,
                                                            fontSize: {
                                                                xs: '1.75rem',
                                                                sm: '2.25rem',
                                                                md: '2.5rem',
                                                            },
                                                        }}
                                                    >
                                                        Hello, {formData.firstName || ''}!
                                                    </Typography>
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            color: '#574F75',
                                                            fontWeight: 400,
                                                            fontSize: { xs: '1rem', sm: '1.125rem' },
                                                        }}
                                                    >
                                                        We're excited to build <span style={{ color: '#574F75', fontWeight: 600 }}>{formData.agencyName || ''}</span> Agency profile with you.
                                                    </Typography>
                                                </Box>
                                            </Fade>

                                            {/* Progress Stepper */}
                                            <Fade
                                                in
                                                timeout={800}
                                                style={{ transitionDelay: '200ms' }}
                                            >
                                                <Box>
                                                    <ProgressStepper
                                                        steps={steps}
                                                        currentStep={currentStep}
                                                        onStepClick={stepIndex => moveToStep(stepIndex)}
                                                    />
                                                </Box>
                                            </Fade>

                                            {/* Step 2: About you */}
                                            {currentStep === 0 && (
                                                <AboutYouStep
                                                    profilePicture={onboardingData.profilePicture}
                                                    professionalBio={onboardingData.professionalBio}
                                                    isUploadingProfilePhoto={isUploadingProfilePhoto}
                                                    onProfilePictureChange={handleProfilePictureChange}
                                                    onRemoveProfilePicture={handleRemoveProfilePicture}
                                                    onBioChange={handleBioChange}
                                                />
                                            )}

                                            {/* Onboarding Form Fields */}
                                            {currentStep === 1 && (
                                                <>
                                                    <Grid container spacing={3}>
                                                        {/* Left Column */}
                                                        <Grid item xs={12} md={6}>
                                                            {/* Services Offered */}
                                                            <Fade
                                                                in
                                                                timeout={800}
                                                                style={{ transitionDelay: '400ms' }}
                                                            >
                                                                <Box id="services-field" sx={{ mb: 3 }}>
                                                                    <Typography
                                                                        variant="body1"
                                                                        sx={{
                                                                            color: 'grey.700',
                                                                            mb: 1.5,
                                                                            fontWeight: 500,
                                                                            fontSize: '0.875rem',
                                                                        }}
                                                                    >
                                                                        Services Offered <span style={{ color: '#d32f2f' }}>*</span>
                                                                    </Typography>
                                                                    <FormControl fullWidth error={!!degreesError}>
                                                                        <Select
                                                                            multiple
                                                                            value={onboardingData.degrees}
                                                                            onChange={handleOnboardingMultiSelectChange(
                                                                                'degrees'
                                                                            )}
                                                                            open={degreesSelectOpen}
                                                                            onOpen={() => {
                                                                                if (!isDeletingChip) {
                                                                                    setDegreesSelectOpen(true)
                                                                                }
                                                                            }}
                                                                            onClose={() =>
                                                                                setDegreesSelectOpen(false)
                                                                            }
                                                                            displayEmpty
                                                                            disabled={isLoadingSpecialties}
                                                                            sx={{
                                                                                borderRadius: 2,
                                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                                    borderColor: '#7A6FA1',
                                                                                },
                                                                                '&:hover .MuiOutlinedInput-notchedOutline':
                                                                                {
                                                                                    borderColor: '#7A6FA1',
                                                                                },
                                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                                                                {
                                                                                    borderColor: '#7A6FA1',
                                                                                    borderWidth: 2,
                                                                                },
                                                                            }}

                                                                            renderValue={selected => {
                                                                                if (
                                                                                    (selected as string[]).length === 0
                                                                                ) {
                                                                                    return (
                                                                                        <em style={{ color: 'grey.400' }}>
                                                                                            Select service
                                                                                        </em>
                                                                                    )
                                                                                }
                                                                                return (
                                                                                    <Box
                                                                                        sx={{
                                                                                            display: 'flex',
                                                                                            flexWrap: 'wrap',
                                                                                            gap: 0.5,
                                                                                        }}
                                                                                    >
                                                                                        {(selected as string[]).map(id => {
                                                                                            const specialty =
                                                                                                specialties.find(
                                                                                                    s => s._id === id
                                                                                                )
                                                                                            return (
                                                                                                <Chip
                                                                                                    key={id}
                                                                                                    label={specialty?.name || id}
                                                                                                    size="small"
                                                                                                    onDelete={e => {
                                                                                                        e.preventDefault()
                                                                                                        e.stopPropagation()
                                                                                                        handleRemoveChip(
                                                                                                            'degrees',
                                                                                                            id
                                                                                                        )
                                                                                                    }}
                                                                                                    onMouseDown={e => {
                                                                                                        // Only prevent if clicking on the chip itself, not the delete icon
                                                                                                        if (
                                                                                                            e.target ===
                                                                                                            e.currentTarget ||
                                                                                                            (
                                                                                                                e.target as HTMLElement
                                                                                                            ).closest(
                                                                                                                '.MuiChip-deleteIcon'
                                                                                                            )
                                                                                                        ) {
                                                                                                            e.preventDefault()
                                                                                                            e.stopPropagation()
                                                                                                        }
                                                                                                    }}
                                                                                                    onClick={e => {
                                                                                                        e.preventDefault()
                                                                                                        e.stopPropagation()
                                                                                                    }}
                                                                                                    sx={{
                                                                                                        bgcolor: '#efebfdff',
                                                                                                        color: '#7A6FA1',
                                                                                                        cursor: 'default',
                                                                                                        '& .MuiChip-deleteIcon': {
                                                                                                            color: '#7A6FA1',
                                                                                                            cursor: 'pointer',
                                                                                                            '&:hover': {
                                                                                                                color: '#7A6FA1',
                                                                                                            },
                                                                                                        },
                                                                                                    }}
                                                                                                />
                                                                                            )
                                                                                        })}
                                                                                    </Box>
                                                                                )
                                                                            }}
                                                                        >
                                                                            {specialties.map(specialty => (
                                                                                <MenuItem
                                                                                    key={specialty._id}
                                                                                    value={specialty._id}
                                                                                >
                                                                                    {specialty.name}
                                                                                </MenuItem>
                                                                            ))}
                                                                        </Select>
                                                                        {degreesError && (
                                                                            <FormHelperText>{degreesError}</FormHelperText>
                                                                        )}
                                                                    </FormControl>
                                                                </Box>
                                                            </Fade>

                                                            {/* How Do You Provide Therapy Services? */}
                                                            <Fade
                                                                in
                                                                timeout={800}
                                                                style={{ transitionDelay: '500ms' }}
                                                            >
                                                                <Box id="services-provided-field" sx={{ mb: 3 }}>
                                                                    <Typography
                                                                        variant="body1"
                                                                        sx={{
                                                                            color: 'grey.700',
                                                                            mb: 1.5,
                                                                            fontWeight: 500,
                                                                            fontSize: '0.875rem',
                                                                        }}
                                                                    >
                                                                        How Do You Provide Therapy Services? <span style={{ color: '#d32f2f' }}>*</span>
                                                                    </Typography>
                                                                    <FormControl fullWidth error={!!servicesError}>
                                                                        <Select
                                                                            multiple
                                                                            value={onboardingData.services}
                                                                            onChange={handleOnboardingMultiSelectChange(
                                                                                'services'
                                                                            )}
                                                                            open={providedServicesSelectOpen}
                                                                            onOpen={() => {
                                                                                if (!isDeletingChip) {
                                                                                    setProvidedServicesSelectOpen(true)
                                                                                }
                                                                            }}
                                                                            onClose={() =>
                                                                                setProvidedServicesSelectOpen(false)
                                                                            }
                                                                            displayEmpty
                                                                            disabled={isLoadingProvidedServices}
                                                                            sx={{
                                                                                borderRadius: 2,
                                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                                    borderColor: '#7A6FA1',
                                                                                },
                                                                                '&:hover .MuiOutlinedInput-notchedOutline':
                                                                                {
                                                                                    borderColor: '#7A6FA1',
                                                                                },
                                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                                                                {
                                                                                    borderColor: '#7A6FA1',
                                                                                    borderWidth: 2,
                                                                                },
                                                                            }}
                                                                            renderValue={selected => {
                                                                                if (
                                                                                    (selected as string[]).length === 0
                                                                                ) {
                                                                                    return (
                                                                                        <em style={{ color: 'grey.400' }}>
                                                                                            Select service type
                                                                                        </em>
                                                                                    )
                                                                                }
                                                                                return (
                                                                                    <Box
                                                                                        sx={{
                                                                                            display: 'flex',
                                                                                            flexWrap: 'wrap',
                                                                                            gap: 0.5,
                                                                                        }}
                                                                                    >
                                                                                        {(selected as string[]).map(
                                                                                            value => {
                                                                                                return (
                                                                                                    <Chip
                                                                                                        key={value}
                                                                                                        label={value}
                                                                                                        size="small"
                                                                                                        onDelete={e => {
                                                                                                            e.preventDefault()
                                                                                                            e.stopPropagation()
                                                                                                            handleRemoveChip(
                                                                                                                'services',
                                                                                                                value
                                                                                                            )
                                                                                                        }}
                                                                                                        onMouseDown={e => {
                                                                                                            // Only prevent if clicking on the chip itself, not the delete icon
                                                                                                            if (
                                                                                                                e.target ===
                                                                                                                e.currentTarget ||
                                                                                                                (
                                                                                                                    e.target as HTMLElement
                                                                                                                ).closest(
                                                                                                                    '.MuiChip-deleteIcon'
                                                                                                                )
                                                                                                            ) {
                                                                                                                e.preventDefault()
                                                                                                                e.stopPropagation()
                                                                                                            }
                                                                                                        }}
                                                                                                        onClick={e => {
                                                                                                            e.preventDefault()
                                                                                                            e.stopPropagation()
                                                                                                        }}
                                                                                                        sx={{
                                                                                                            bgcolor: '#efebfdff',
                                                                                                            color: '#7A6FA1',
                                                                                                            cursor: 'default',
                                                                                                            '& .MuiChip-deleteIcon': {
                                                                                                                color: '#7A6FA1',
                                                                                                                cursor: 'pointer',
                                                                                                                '&:hover': {
                                                                                                                    color: '#7A6FA1',
                                                                                                                },
                                                                                                            },
                                                                                                        }}
                                                                                                    />
                                                                                                )
                                                                                            }
                                                                                        )}
                                                                                    </Box>
                                                                                )
                                                                            }}
                                                                        >
                                                                            {isLoadingProvidedServices ? (
                                                                                <MenuItem disabled>
                                                                                    <CircularProgress
                                                                                        size={20}
                                                                                        sx={{ mr: 1 }}
                                                                                    />
                                                                                    Loading services...
                                                                                </MenuItem>
                                                                            ) : providedServices.length === 0 ? (
                                                                                <MenuItem disabled>
                                                                                    No services available.
                                                                                </MenuItem>
                                                                            ) : (
                                                                                providedServices.map(service => (
                                                                                    <MenuItem
                                                                                        key={service.id}
                                                                                        value={service.name}
                                                                                    >
                                                                                        {service.name}
                                                                                    </MenuItem>
                                                                                ))
                                                                            )}
                                                                        </Select>
                                                                        {servicesError && (
                                                                            <FormHelperText>{servicesError}</FormHelperText>
                                                                        )}
                                                                    </FormControl>
                                                                </Box>
                                                            </Fade>
                                                        </Grid>

                                                        {/* Right Column */}
                                                        <Grid item xs={12} md={6}>
                                                            {/* Insurance Accepted */}
                                                            <Fade
                                                                in
                                                                timeout={800}
                                                                style={{ transitionDelay: '800ms' }}
                                                            >
                                                                <Box id="insurance-field" sx={{ mb: 3 }}>
                                                                    <Typography
                                                                        variant="body1"
                                                                        sx={{
                                                                            color: 'grey.700',
                                                                            mb: 1.5,
                                                                            fontWeight: 500,
                                                                            fontSize: '0.875rem',
                                                                        }}
                                                                    >
                                                                        Insurance Accepted <span style={{ color: '#d32f2f' }}>*</span>
                                                                    </Typography>
                                                                    <FormControl fullWidth error={!!insuranceError}>
                                                                        <Autocomplete
                                                                            multiple
                                                                            options={payerSearchOptions}
                                                                            value={selectedPayers}
                                                                            inputValue={payerSearchInput}
                                                                            onChange={handleInsuranceAcceptedChange}
                                                                            onInputChange={(
                                                                                event,
                                                                                newInputValue,
                                                                                reason
                                                                            ) => {
                                                                                // Only update input when user is typing, not when clearing/resetting
                                                                                if (reason === 'input') {
                                                                                    setPayerSearchInput(newInputValue)
                                                                                } else if (reason === 'clear') {
                                                                                    setPayerSearchInput('')
                                                                                }
                                                                            }}
                                                                            getOptionLabel={option => option.payer_name}
                                                                            isOptionEqualToValue={(option, value) =>
                                                                                option._id === value._id
                                                                            }
                                                                            loading={isSearchingPayers}
                                                                            filterOptions={x => x} // Disable client-side filtering since we're using server-side search
                                                                            noOptionsText={
                                                                                payerSearchInput.trim().length === 0
                                                                                    ? 'Start typing to search for insurance providers'
                                                                                    : 'No insurance providers found'
                                                                            }
                                                                            onClose={(event, reason) => {
                                                                                // Clear input when an option is selected
                                                                                if (reason === 'selectOption') {
                                                                                    setPayerSearchInput('')
                                                                                }
                                                                            }}
                                                                            renderInput={params => (
                                                                                <TextField
                                                                                    {...params}
                                                                                    placeholder="Search and select insurance providers"
                                                                                    sx={{
                                                                                        '& .MuiOutlinedInput-root': {
                                                                                            borderRadius: 2,
                                                                                            '& fieldset': {
                                                                                                borderColor: '#7A6FA1',
                                                                                            },
                                                                                            '&:hover fieldset': {
                                                                                                borderColor: '#7A6FA1',
                                                                                            },
                                                                                            '&.Mui-focused fieldset': {
                                                                                                borderColor: '#7A6FA1',
                                                                                                borderWidth: 2,
                                                                                            },
                                                                                        },
                                                                                    }}
                                                                                    InputProps={{
                                                                                        ...params.InputProps,
                                                                                        endAdornment: (
                                                                                            <>
                                                                                                {isSearchingPayers ? (
                                                                                                    <CircularProgress
                                                                                                        color="inherit"
                                                                                                        size={20}
                                                                                                    />
                                                                                                ) : null}
                                                                                                {params.InputProps.endAdornment}
                                                                                            </>
                                                                                        ),
                                                                                    }}
                                                                                />
                                                                            )}
                                                                            renderTags={(value, getTagProps) =>
                                                                                value.map((option, index) => (
                                                                                    <Chip
                                                                                        {...getTagProps({ index })}
                                                                                        key={option._id}
                                                                                        label={option.payer_name}
                                                                                        size="small"
                                                                                        sx={{
                                                                                            bgcolor: '#efebfdff',
                                                                                            color: '#7A6FA1',
                                                                                            cursor: 'default',
                                                                                            '& .MuiChip-deleteIcon': {
                                                                                                color: '#7A6FA1',
                                                                                                cursor: 'pointer',
                                                                                                '&:hover': {
                                                                                                    color: '#7A6FA1',
                                                                                                },
                                                                                            },
                                                                                        }}
                                                                                    />
                                                                                ))
                                                                            }
                                                                        />
                                                                        {insuranceError && (
                                                                            <FormHelperText>{insuranceError}</FormHelperText>
                                                                        )}
                                                                    </FormControl>
                                                                </Box>
                                                            </Fade>

                                                            {/* Years of Experience */}
                                                            <Fade
                                                                in
                                                                timeout={800}
                                                                style={{ transitionDelay: '900ms' }}
                                                            >
                                                                <Box id="years-experience-field" sx={{ mb: 3 }}>
                                                                    <Typography
                                                                        variant="body1"
                                                                        sx={{
                                                                            color: 'grey.700',
                                                                            mb: 1.5,
                                                                            fontWeight: 500,
                                                                            fontSize: '0.875rem',
                                                                        }}
                                                                    >
                                                                        Year of Establishment (optional)
                                                                    </Typography>
                                                                    <FormControl fullWidth error={!!yearsOfExperienceError}>
                                                                        <TextField
                                                                            value={onboardingData.yearsOfExperience}
                                                                            onChange={(e) => {
                                                                                const val = e.target.value.replace(/\D/g, '').slice(0, 4)
                                                                                setOnboardingData(prev => ({
                                                                                    ...prev,
                                                                                    yearsOfExperience: val,
                                                                                }))
                                                                                setYearsOfExperienceError('')
                                                                            }}
                                                                            placeholder="Enter year (e.g. 2018)"
                                                                            error={!!yearsOfExperienceError}
                                                                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                                                            onKeyDown={(e) => {
                                                                                if (['e', 'E', '+', '-', '.', ','].includes(e.key)) {
                                                                                    e.preventDefault()
                                                                                }
                                                                            }}
                                                                            sx={{
                                                                                '& .MuiOutlinedInput-root': {
                                                                                    borderRadius: 2,
                                                                                    bgcolor: 'white',
                                                                                    height: '48px',
                                                                                    '& fieldset': {
                                                                                        borderColor: '#D5D1E1',
                                                                                    },
                                                                                    '&:hover fieldset': {
                                                                                        borderColor: '#B8B3C8',
                                                                                    },
                                                                                    '&.Mui-focused fieldset': {
                                                                                        borderColor: '#7A6FA1',
                                                                                        borderWidth: 2,
                                                                                    },
                                                                                },
                                                                                '& .MuiOutlinedInput-input': {
                                                                                    fontSize: '0.875rem',
                                                                                    color: '#4E4861',
                                                                                    fontWeight: 500,
                                                                                    pl: 1,
                                                                                }
                                                                            }}
                                                                        />
                                                                        {yearsOfExperienceError && (
                                                                            <FormHelperText>{yearsOfExperienceError}</FormHelperText>
                                                                        )}
                                                                    </FormControl>
                                                                </Box>
                                                            </Fade>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid container spacing={3}>

                                                        <Grid item xs={12} md={6}>

                                                            <Fade
                                                                in
                                                                timeout={800}
                                                                style={{ transitionDelay: '900ms' }}
                                                            >
                                                                <Box id="no-of-providers-field" sx={{ mb: 3 }}>
                                                                    <Typography
                                                                        variant="body1"
                                                                        sx={{
                                                                            color: 'grey.700',
                                                                            mb: 1.5,
                                                                            fontWeight: 500,
                                                                            fontSize: '0.875rem',
                                                                        }}
                                                                    >
                                                                        Number of Providers
                                                                    </Typography>
                                                                    <FormControl fullWidth error={!!noOfProviderError}>
                                                                        <TextField
                                                                            value={onboardingData.noOfProvider}
                                                                            onChange={(e) => {
                                                                                const val = e.target.value.replace(/\D/g, '').slice(0, 5)
                                                                                setOnboardingData(prev => ({
                                                                                    ...prev,
                                                                                    noOfProvider: val,
                                                                                }))
                                                                                setNoOfProviderError('')
                                                                            }}
                                                                            placeholder="Enter number of providers"
                                                                            error={!!noOfProviderError}
                                                                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                                                            onKeyDown={(e) => {
                                                                                if (['e', 'E', '+', '-', '.', ','].includes(e.key)) {
                                                                                    e.preventDefault()
                                                                                }
                                                                            }}
                                                                            sx={{
                                                                                '& .MuiOutlinedInput-root': {
                                                                                    borderRadius: 2,
                                                                                    bgcolor: 'white',
                                                                                    height: '48px',
                                                                                    '& fieldset': {
                                                                                        borderColor: '#D5D1E1',
                                                                                    },
                                                                                    '&:hover fieldset': {
                                                                                        borderColor: '#B8B3C8',
                                                                                    },
                                                                                    '&.Mui-focused fieldset': {
                                                                                        borderColor: '#7A6FA1',
                                                                                        borderWidth: 2,
                                                                                    },
                                                                                },
                                                                                '& .MuiOutlinedInput-input': {
                                                                                    fontSize: '0.875rem',
                                                                                    color: '#4E4861',
                                                                                    fontWeight: 500,
                                                                                    pl: 1,
                                                                                }
                                                                            }}
                                                                        />
                                                                        {noOfProviderError && (
                                                                            <FormHelperText>{noOfProviderError}</FormHelperText>
                                                                        )}
                                                                    </FormControl>
                                                                </Box>
                                                            </Fade>
                                                        </Grid>
                                                    </Grid>

                                                    {/* Board Certification Section */}
                                                    {/* <Box id="board-certification-section" sx={{ mt: 4 }}>
                                                        <Divider sx={{ mb: 3 }} />
                                                        <Typography
                                                            variant="h6"
                                                            sx={{
                                                                color: 'grey.800',
                                                                mb: 2.5,
                                                                fontWeight: 600,
                                                                fontSize: '1rem',
                                                            }}
                                                        >
                                                            Board Certification (Optional)
                                                        </Typography>
                                                        <Grid container spacing={3}>
                                                            <Grid item xs={12} md={6}>
                                                                <Fade
                                                                    in
                                                                    timeout={800}
                                                                    style={{ transitionDelay: '200ms' }}
                                                                >
                                                                    <Box sx={{ mb: 3 }} id="board-number-field">
                                                                        <Typography
                                                                            variant="body1"
                                                                            sx={{
                                                                                color: 'grey.700',
                                                                                mb: 1.5,
                                                                                fontWeight: 500,
                                                                                fontSize: '0.875rem',
                                                                            }}
                                                                        >
                                                                            Board Certification No
                                                                        </Typography>
                                                                        <TextField
                                                                            fullWidth
                                                                            value={
                                                                                onboardingData.boardCertificationNo
                                                                            }
                                                                            onChange={e => {
                                                                                setOnboardingData(prev => ({
                                                                                    ...prev,
                                                                                    boardCertificationNo: e.target.value,
                                                                                }))
                                                                                if (boardCertificationNoError) {
                                                                                    setBoardCertificationNoError('')
                                                                                }
                                                                            }}
                                                                            placeholder="e.g. 3342434234"
                                                                            error={!!boardCertificationNoError}
                                                                            helperText={boardCertificationNoError}
                                                                            sx={{
                                                                                '& .MuiOutlinedInput-root': {
                                                                                    bgcolor: 'white',
                                                                                    borderRadius: 2,
                                                                                    '& fieldset': {
                                                                                        borderColor: 'grey.300',
                                                                                    },
                                                                                    '&:hover fieldset': {
                                                                                        borderColor: 'grey.400',
                                                                                    },
                                                                                    '&.Mui-focused fieldset': {
                                                                                        borderColor: primaryColor,
                                                                                        borderWidth: 2,
                                                                                    },
                                                                                },
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                </Fade>
                                                            </Grid>
                                                            <Grid item xs={12} md={6}>
                                                                <Fade
                                                                    in
                                                                    timeout={800}
                                                                    style={{ transitionDelay: '300ms' }}
                                                                >
                                                                    <Box sx={{ mb: 3 }} id="board-name-field">
                                                                        <Typography
                                                                            variant="body1"
                                                                            sx={{
                                                                                color: 'grey.700',
                                                                                mb: 1.5,
                                                                                fontWeight: 500,
                                                                                fontSize: '0.875rem',
                                                                            }}
                                                                        >
                                                                            Board Name
                                                                        </Typography>
                                                                        <Autocomplete
                                                                            options={boardNameOptions}
                                                                            value={
                                                                                boardNameOptions.find(
                                                                                    o =>
                                                                                        o._id === onboardingData.boardName
                                                                                ) || null
                                                                            }
                                                                            inputValue={boardNameSearch}
                                                                            onInputChange={(_, newValue) => {
                                                                                setBoardNameSearch(newValue)
                                                                            }}
                                                                            onChange={(_, newValue) => {
                                                                                setOnboardingData(prev => ({
                                                                                    ...prev,
                                                                                    boardName: newValue?._id || '',
                                                                                    boardSpeciality: '',
                                                                                    boardSubSpeciality: '',
                                                                                }))
                                                                                setBoardSpecialityOptions([])
                                                                                setBoardSubSpecialityOptions([])
                                                                                if (boardNameError) {
                                                                                    setBoardNameError('')
                                                                                }
                                                                            }}
                                                                            getOptionLabel={option => option.name}
                                                                            isOptionEqualToValue={(opt, val) =>
                                                                                opt._id === val._id
                                                                            }
                                                                            loading={isSearchingBoardName}
                                                                            filterOptions={x => x}
                                                                            noOptionsText={
                                                                                boardNameSearch.trim().length === 0
                                                                                    ? 'Start typing to search'
                                                                                    : 'No options found'
                                                                            }
                                                                            renderInput={params => (
                                                                                <TextField
                                                                                    {...params}
                                                                                    placeholder="Search and select board"
                                                                                    error={!!boardNameError}
                                                                                    helperText={boardNameError}
                                                                                    sx={{
                                                                                        '& .MuiOutlinedInput-root': {
                                                                                            borderRadius: 2,
                                                                                            bgcolor: 'white',
                                                                                            '& fieldset': {
                                                                                                borderColor: 'grey.300',
                                                                                            },
                                                                                            '&:hover fieldset': {
                                                                                                borderColor: 'grey.400',
                                                                                            },
                                                                                            '&.Mui-focused fieldset': {
                                                                                                borderColor: primaryColor,
                                                                                                borderWidth: 2,
                                                                                            },
                                                                                        },
                                                                                    }}
                                                                                    InputProps={{
                                                                                        ...params.InputProps,
                                                                                        endAdornment: (
                                                                                            <>
                                                                                                {isSearchingBoardName ? (
                                                                                                    <CircularProgress
                                                                                                        color="inherit"
                                                                                                        size={20}
                                                                                                    />
                                                                                                ) : null}
                                                                                                {params.InputProps.endAdornment}
                                                                                            </>
                                                                                        ),
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        />
                                                                    </Box>
                                                                </Fade>
                                                            </Grid>
                                                            <Grid item xs={12} md={6}>
                                                                <Fade
                                                                    in
                                                                    timeout={800}
                                                                    style={{ transitionDelay: '400ms' }}
                                                                >
                                                                    <Box sx={{ mb: 3 }} id="board-specialty-field">
                                                                        <Typography
                                                                            variant="body1"
                                                                            sx={{
                                                                                color: 'grey.700',
                                                                                mb: 1.5,
                                                                                fontWeight: 500,
                                                                                fontSize: '0.875rem',
                                                                            }}
                                                                        >
                                                                            Board Specialty
                                                                        </Typography>
                                                                        <Autocomplete
                                                                            disabled={!onboardingData.boardName}
                                                                            options={boardSpecialityOptions}
                                                                            value={
                                                                                boardSpecialityOptions.find(
                                                                                    o =>
                                                                                        o._id ===
                                                                                        onboardingData.boardSpeciality
                                                                                ) || null
                                                                            }
                                                                            inputValue={boardSpecialitySearch}
                                                                            onInputChange={(_, newValue) => {
                                                                                setBoardSpecialitySearch(newValue)
                                                                            }}
                                                                            onChange={(_, newValue) => {
                                                                                setOnboardingData(prev => ({
                                                                                    ...prev,
                                                                                    boardSpeciality: newValue?._id || '',
                                                                                    boardSubSpeciality: '',
                                                                                }))
                                                                                setBoardSubSpecialityOptions([])
                                                                                if (boardSpecialityError) {
                                                                                    setBoardSpecialityError('')
                                                                                }
                                                                            }}
                                                                            getOptionLabel={option => option.name}
                                                                            isOptionEqualToValue={(opt, val) =>
                                                                                opt._id === val._id
                                                                            }
                                                                            loading={isSearchingBoardSpeciality}
                                                                            filterOptions={x => x}
                                                                            noOptionsText={
                                                                                boardSpecialitySearch.trim().length ===
                                                                                    0
                                                                                    ? 'Start typing to search'
                                                                                    : 'No options found'
                                                                            }
                                                                            renderInput={params => (
                                                                                <TextField
                                                                                    {...params}
                                                                                    placeholder="Search and select speciality"
                                                                                    error={!!boardSpecialityError}
                                                                                    helperText={boardSpecialityError}
                                                                                    sx={{
                                                                                        '& .MuiOutlinedInput-root': {
                                                                                            borderRadius: 2,
                                                                                            bgcolor: 'white',
                                                                                            '& fieldset': {
                                                                                                borderColor: 'grey.300',
                                                                                            },
                                                                                            '&:hover fieldset': {
                                                                                                borderColor: 'grey.400',
                                                                                            },
                                                                                            '&.Mui-focused fieldset': {
                                                                                                borderColor: primaryColor,
                                                                                                borderWidth: 2,
                                                                                            },
                                                                                        },
                                                                                    }}
                                                                                    InputProps={{
                                                                                        ...params.InputProps,
                                                                                        endAdornment: (
                                                                                            <>
                                                                                                {isSearchingBoardSpeciality ? (
                                                                                                    <CircularProgress
                                                                                                        color="inherit"
                                                                                                        size={20}
                                                                                                    />
                                                                                                ) : null}
                                                                                                {params.InputProps.endAdornment}
                                                                                            </>
                                                                                        ),
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        />
                                                                    </Box>
                                                                </Fade>
                                                            </Grid>
                                                            <Grid item xs={12} md={6}>
                                                                <Fade
                                                                    in
                                                                    timeout={800}
                                                                    style={{ transitionDelay: '500ms' }}
                                                                >
                                                                    <Box sx={{ mb: 3 }} id="board-subspecialty-field">
                                                                        <Typography
                                                                            variant="body1"
                                                                            sx={{
                                                                                color: 'grey.700',
                                                                                mb: 1.5,
                                                                                fontWeight: 500,
                                                                                fontSize: '0.875rem',
                                                                            }}
                                                                        >
                                                                            Board Sub-Specialty
                                                                        </Typography>
                                                                        <Autocomplete
                                                                            disabled={!onboardingData.boardSpeciality}
                                                                            options={boardSubSpecialityOptions}
                                                                            value={
                                                                                boardSubSpecialityOptions.find(
                                                                                    o =>
                                                                                        o._id ===
                                                                                        onboardingData.boardSubSpeciality
                                                                                ) || null
                                                                            }
                                                                            inputValue={boardSubSpecialitySearch}
                                                                            onInputChange={(_, newValue) => {
                                                                                setBoardSubSpecialitySearch(newValue)
                                                                            }}
                                                                            onChange={(_, newValue) => {
                                                                                setOnboardingData(prev => ({
                                                                                    ...prev,
                                                                                    boardSubSpeciality:
                                                                                        newValue?._id || '',
                                                                                }))
                                                                            }}
                                                                            getOptionLabel={option => option.name}
                                                                            isOptionEqualToValue={(opt, val) =>
                                                                                opt._id === val._id
                                                                            }
                                                                            loading={isSearchingBoardSubSpeciality}
                                                                            filterOptions={x => x}
                                                                            noOptionsText={
                                                                                boardSubSpecialitySearch.trim()
                                                                                    .length === 0
                                                                                    ? 'Start typing to search'
                                                                                    : 'No options found'
                                                                            }
                                                                            renderInput={params => (
                                                                                <TextField
                                                                                    {...params}
                                                                                    placeholder="Search and select sub-speciality"
                                                                                    sx={{
                                                                                        '& .MuiOutlinedInput-root': {
                                                                                            borderRadius: 2,
                                                                                            bgcolor: 'white',
                                                                                            '& fieldset': {
                                                                                                borderColor: 'grey.300',
                                                                                            },
                                                                                            '&:hover fieldset': {
                                                                                                borderColor: 'grey.400',
                                                                                            },
                                                                                            '&.Mui-focused fieldset': {
                                                                                                borderColor: primaryColor,
                                                                                                borderWidth: 2,
                                                                                            },
                                                                                        },
                                                                                    }}
                                                                                    InputProps={{
                                                                                        ...params.InputProps,
                                                                                        endAdornment: (
                                                                                            <>
                                                                                                {isSearchingBoardSubSpeciality ? (
                                                                                                    <CircularProgress
                                                                                                        color="inherit"
                                                                                                        size={20}
                                                                                                    />
                                                                                                ) : null}
                                                                                                {params.InputProps.endAdornment}
                                                                                            </>
                                                                                        ),
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        />
                                                                    </Box>
                                                                </Fade>
                                                            </Grid>
                                                        </Grid>
                                                    </Box> */}
                                                </>
                                            )}



                                            {/* Step 3: NPI & License verification */}
                                            {currentStep === 2 && (
                                                <Box>
                                                    {paymentError && (
                                                        <Alert
                                                            severity="error"
                                                            sx={{ mb: 3, borderRadius: 2 }}
                                                            onClose={() => setPaymentError(null)}
                                                        >
                                                            {paymentError}
                                                        </Alert>
                                                    )}
                                                    <Fade
                                                        in
                                                        timeout={800}
                                                        style={{ transitionDelay: '200ms' }}
                                                    >
                                                        <Box sx={{ mb: 4 }}>
                                                            {/* How are you enrolled to bill payers? */}
                                                            {/* <Typography
                                                                variant="h6"
                                                                sx={{
                                                                    color: 'grey.800',
                                                                    mb: 3,
                                                                    fontWeight: 600,
                                                                    fontSize: '1.125rem',
                                                                }}
                                                            >
                                                                How are you enrolled to bill payers?
                                                            </Typography> */}

                                                            {/* <FormControl
                                                                component="fieldset"
                                                                sx={{
                                                                    mb: 4,
                                                                }}
                                                            >
                                                                <RadioGroup
                                                                    value={formData.billingType || 'individual'}
                                                                    onChange={e => {
                                                                        setFormData(prev => ({
                                                                            ...prev,
                                                                            billingType: e.target.value,
                                                                            taxId: '',
                                                                            companyName: '',
                                                                            ssn: '',
                                                                            billingNpi: '',
                                                                        }))
                                                                        // Clear errors when billing type changes
                                                                        setCompanyNameError('')
                                                                        setTaxIdError('')
                                                                        setSsnError('')
                                                                        // Clear errors when billing type changes
                                                                        setTaxIdError('')
                                                                        setSsnError('')
                                                                        setBillingNpiVerificationError('')
                                                                        setIsBillingNpiVerified(false)
                                                                    }}
                                                                    sx={{
                                                                        display: 'flex',
                                                                        flexDirection: 'row',
                                                                        gap: 2,
                                                                    }}
                                                                >
                                                                    <FormControlLabel
                                                                        value="individual"
                                                                        control={
                                                                            <Radio
                                                                                sx={{
                                                                                    color: primaryColor,
                                                                                    '&.Mui-checked': {
                                                                                        color: primaryColor,
                                                                                    },
                                                                                }}
                                                                            />
                                                                        }
                                                                        label="I bill as an individual provider"
                                                                        sx={{
                                                                            '& .MuiFormControlLabel-label': {
                                                                                fontSize: '0.875rem',
                                                                                fontWeight: 500,
                                                                                color: 'grey.700',
                                                                            },
                                                                        }}
                                                                    />
                                                                    <FormControlLabel
                                                                        value="business"
                                                                        control={
                                                                            <Radio
                                                                                sx={{
                                                                                    color: primaryColor,
                                                                                    '&.Mui-checked': {
                                                                                        color: primaryColor,
                                                                                    },
                                                                                }}
                                                                            />
                                                                        }
                                                                        label="I bill through a company (Business Entity)"
                                                                        sx={{
                                                                            '& .MuiFormControlLabel-label': {
                                                                                fontSize: '0.875rem',
                                                                                fontWeight: 500,
                                                                                color: 'grey.700',
                                                                            },
                                                                        }}
                                                                    />
                                                                </RadioGroup>
                                                            </FormControl> */}

                                                            {/* Provider and Billing Information */}
                                                            {/* <Typography
                                                                variant="h6"
                                                                sx={{
                                                                    color: 'grey.800',
                                                                    mb: 3,
                                                                    fontWeight: 600,
                                                                    fontSize: '1.125rem',
                                                                }}
                                                            >
                                                                Provider and Billing Information
                                                            </Typography> */}

                                                            <Grid container spacing={3} sx={{ mb: 4 }}>

                                                                {/* Billing NPI - Only show for business */}

                                                                <Grid item xs={12} md={6}>
                                                                    <Typography
                                                                        variant="body1"
                                                                        sx={{
                                                                            color: 'grey.700',
                                                                            mb: 1.5,
                                                                            fontWeight: 500,
                                                                            fontSize: '0.875rem',
                                                                        }}
                                                                    >
                                                                        Billing NPI *
                                                                    </Typography>
                                                                    <TextField
                                                                        id="billing-npi-field"
                                                                        fullWidth
                                                                        placeholder="Enter 10 digit NPI number"
                                                                        value={formData.billingNpi || ''}
                                                                        onChange={e => {
                                                                            setFormData(prev => ({
                                                                                ...prev,
                                                                                billingNpi: e.target.value,
                                                                            }))
                                                                            // Reset verification when billing NPI changes
                                                                            setIsBillingNpiVerified(false)
                                                                            setBillingNpiVerificationError('')
                                                                        }}
                                                                        error={!!billingNpiVerificationError}
                                                                        helperText={billingNpiVerificationError}
                                                                        InputProps={{
                                                                            endAdornment: (
                                                                                <InputAdornment position="end">
                                                                                    {isBillingNpiVerified ? (
                                                                                        <Box
                                                                                            sx={{
                                                                                                display: 'flex',
                                                                                                alignItems: 'center',
                                                                                                gap: 0.5,
                                                                                                mr: 1,
                                                                                            }}
                                                                                        >
                                                                                            <CheckCircle
                                                                                                sx={{
                                                                                                    color: 'success.main',
                                                                                                    fontSize: 20,
                                                                                                }}
                                                                                            />
                                                                                            <Typography
                                                                                                variant="caption"
                                                                                                sx={{
                                                                                                    color: 'success.main',
                                                                                                    fontWeight: 500,
                                                                                                }}
                                                                                            >
                                                                                                Verified
                                                                                            </Typography>
                                                                                        </Box>
                                                                                    ) : (
                                                                                        <Button
                                                                                            variant="contained"
                                                                                            size="small"
                                                                                            onClick={handleVerifyBillingNpi}
                                                                                            disabled={
                                                                                                isBillingNpiVerifying ||
                                                                                                !formData.billingNpi.trim()
                                                                                            }
                                                                                            sx={{
                                                                                                textTransform: 'none',
                                                                                                borderColor: primaryColor,
                                                                                                bgcolor: primaryColor,
                                                                                                color: 'white',
                                                                                                minWidth: 'auto',
                                                                                                px: 1.5,
                                                                                                py: 0.5,
                                                                                                fontSize: '0.75rem',
                                                                                                '&:hover': {
                                                                                                    borderColor: darken(
                                                                                                        primaryColor,
                                                                                                        0.2
                                                                                                    ),
                                                                                                    bgcolor: darken(
                                                                                                        primaryColor,
                                                                                                        0.2
                                                                                                    ),
                                                                                                    color: 'white',
                                                                                                },
                                                                                                '&.Mui-disabled': {
                                                                                                    borderColor: 'grey.300',
                                                                                                    color: 'grey.400',
                                                                                                },
                                                                                            }}
                                                                                        >
                                                                                            {isBillingNpiVerifying ? (
                                                                                                <>
                                                                                                    <CircularProgress
                                                                                                        size={14}
                                                                                                        sx={{ mr: 0.5 }}
                                                                                                    />
                                                                                                    Verifying
                                                                                                </>
                                                                                            ) : (
                                                                                                'Verify'
                                                                                            )}
                                                                                        </Button>
                                                                                    )}
                                                                                </InputAdornment>
                                                                            ),
                                                                        }}
                                                                        sx={{
                                                                            '& .MuiOutlinedInput-root': {
                                                                                bgcolor: 'white',
                                                                                borderRadius: 2,
                                                                                '& fieldset': {
                                                                                    borderColor: 'grey.300',
                                                                                },
                                                                                '&:hover fieldset': {
                                                                                    borderColor: 'grey.400',
                                                                                },
                                                                                '&.Mui-focused fieldset': {
                                                                                    borderColor: primaryColor,
                                                                                    borderWidth: 2,
                                                                                },
                                                                            },
                                                                        }}
                                                                    />
                                                                </Grid>


                                                                {/* EIN - Only show for business */}

                                                                <Grid item xs={12} md={6}>
                                                                    <Typography
                                                                        variant="body1"
                                                                        sx={{
                                                                            color: 'grey.700',
                                                                            mb: 1.5,
                                                                            fontWeight: 500,
                                                                            fontSize: '0.875rem',
                                                                        }}
                                                                    >
                                                                        Tax Id / EIN *
                                                                    </Typography>
                                                                    <TextField
                                                                        id="tax-id-field"
                                                                        fullWidth
                                                                        required
                                                                        placeholder="Enter EIN - Format: XX-XXXXXXX"
                                                                        value={formatEIN(formData.taxId || '')}
                                                                        onChange={e => {
                                                                            const raw = e.target.value.replace(
                                                                                /\D/g,
                                                                                ''
                                                                            ) // keep only digits
                                                                            setFormData(prev => ({
                                                                                ...prev,
                                                                                taxId: raw, // store plain text only
                                                                            }))
                                                                            if (taxIdError) {
                                                                                setTaxIdError('')
                                                                            }
                                                                        }}
                                                                        onBlur={() => {
                                                                            if (
                                                                                !formData.taxId ||
                                                                                !formData.taxId.trim()
                                                                            ) {
                                                                                setTaxIdError('EIN is required')
                                                                            } else if (
                                                                                !isValidEIN(formData.taxId)
                                                                            ) {
                                                                                setTaxIdError(
                                                                                    ValidationRules.ein.message
                                                                                )
                                                                            }
                                                                        }}
                                                                        inputProps={{
                                                                            inputMode: 'numeric',
                                                                            maxLength: 10,
                                                                        }}
                                                                        error={!!taxIdError}
                                                                        helperText={taxIdError}
                                                                        sx={{
                                                                            '& .MuiOutlinedInput-root': {
                                                                                bgcolor: 'white',
                                                                                borderRadius: 2,
                                                                                '& fieldset': {
                                                                                    borderColor: taxIdError
                                                                                        ? 'error.main'
                                                                                        : 'grey.300',
                                                                                },
                                                                                '&:hover fieldset': {
                                                                                    borderColor: taxIdError
                                                                                        ? 'error.main'
                                                                                        : 'grey.400',
                                                                                },
                                                                                '&.Mui-focused fieldset': {
                                                                                    borderColor: taxIdError
                                                                                        ? 'error.main'
                                                                                        : primaryColor,
                                                                                    borderWidth: 2,
                                                                                },
                                                                            },
                                                                        }}
                                                                    />
                                                                </Grid>


                                                                <Grid item xs={12} md={6}>
                                                                    <Typography
                                                                        variant="body1"
                                                                        sx={{
                                                                            color: 'grey.700',
                                                                            mb: 1.5,
                                                                            fontWeight: 500,
                                                                            fontSize: '0.875rem',
                                                                        }}
                                                                    >
                                                                        Billing Phone Number *
                                                                    </Typography>
                                                                    <PhoneInput
                                                                        id="billing-phone-field"
                                                                        fullWidth
                                                                        value={formData.billingPhone}
                                                                        onChange={value => {
                                                                            setFormData(prev => ({
                                                                                ...prev,
                                                                                billingPhone: value,
                                                                            }))
                                                                            if (isBillingPhoneTouched) {
                                                                                setBillingPhoneError(
                                                                                    getBillingPhoneValidationMessage(
                                                                                        value
                                                                                    )
                                                                                )
                                                                            }
                                                                        }}
                                                                        onBlur={(
                                                                            event: React.FocusEvent<HTMLInputElement>
                                                                        ) => {
                                                                            setIsBillingPhoneTouched(true)
                                                                            setBillingPhoneError(
                                                                                getBillingPhoneValidationMessage(
                                                                                    event.target.value
                                                                                )
                                                                            )
                                                                        }}
                                                                        onValidationChange={isValid => {
                                                                            if (!isBillingPhoneTouched) {
                                                                                return
                                                                            }
                                                                            setBillingPhoneError(
                                                                                isValid
                                                                                    ? ''
                                                                                    : getBillingPhoneValidationMessage(
                                                                                        formData.billingPhone || ''
                                                                                    )
                                                                            )
                                                                        }}
                                                                        required
                                                                        error={!!billingPhoneError}
                                                                        helperText={billingPhoneError}
                                                                        sx={{
                                                                            '& .MuiOutlinedInput-root': {
                                                                                bgcolor: 'white',
                                                                                borderRadius: 2,
                                                                                '& fieldset': {
                                                                                    borderColor: billingPhoneError
                                                                                        ? 'error.main'
                                                                                        : 'grey.300',
                                                                                },
                                                                                '&:hover fieldset': {
                                                                                    borderColor: billingPhoneError
                                                                                        ? 'error.main'
                                                                                        : 'grey.400',
                                                                                },
                                                                                '&.Mui-focused fieldset': {
                                                                                    borderColor: billingPhoneError
                                                                                        ? 'error.main'
                                                                                        : primaryColor,
                                                                                    borderWidth: 2,
                                                                                },
                                                                            },
                                                                        }}
                                                                    />
                                                                </Grid>
                                                            </Grid>

                                                            {/* Billing Address */}
                                                            <Typography
                                                                variant="h6"
                                                                sx={{
                                                                    color: 'grey.800',
                                                                    mb: 3,
                                                                    fontWeight: 600,
                                                                    fontSize: '1.125rem',
                                                                }}
                                                            >
                                                                Billing Address
                                                            </Typography>

                                                            <Grid container spacing={3} sx={{ mb: 4 }}>
                                                                <Grid item xs={12} id="billing-address-field">
                                                                    <Typography
                                                                        variant="body1"
                                                                        sx={{
                                                                            color: 'grey.700',
                                                                            mb: 1.5,
                                                                            fontWeight: 500,
                                                                            fontSize: '0.875rem',
                                                                        }}
                                                                    >
                                                                        Billing Address *
                                                                    </Typography>
                                                                    <AddressAutocomplete
                                                                        sx={{
                                                                            margin: 'normal',
                                                                            fullWidth: true,
                                                                            fontSize: '1rem',
                                                                            '& .MuiInputBase-root': {
                                                                                height: 56,
                                                                            },
                                                                            '& .MuiOutlinedInput-root': {
                                                                                borderRadius: '16px !important' as any,
                                                                                bgcolor: 'white',
                                                                                '& fieldset': {
                                                                                    borderColor: 'grey.300',
                                                                                },
                                                                                '&:hover fieldset': {
                                                                                    borderColor: 'grey.400',
                                                                                },
                                                                                '&.Mui-focused fieldset': {
                                                                                    borderColor: primaryColor,
                                                                                    borderWidth: 2,
                                                                                },
                                                                            },
                                                                            '& .MuiInputBase-input': {
                                                                                py: '1.5rem',
                                                                                fontSize: '1rem',
                                                                            },
                                                                        }}
                                                                        value={formData.billingAddressLine}
                                                                        onChange={(address, components) => {
                                                                            setFormData(prev => ({
                                                                                ...prev,
                                                                                billingAddressLine: address,
                                                                                // Only update city, state, zip, country if components are provided and have values
                                                                                // Don't clear them when address is cleared (components will have empty strings)
                                                                                ...(components && {
                                                                                    ...(components.city && {
                                                                                        billingCity: components.city,
                                                                                    }),
                                                                                    ...(components.state && {
                                                                                        billingState: components.state,
                                                                                    }),
                                                                                    ...((components.zipCode ||
                                                                                        components.postalCode) && {
                                                                                        billingZipCode:
                                                                                            components.zipCode ||
                                                                                            components.postalCode,
                                                                                    }),
                                                                                    ...(components.country && {
                                                                                        billingCountry: components.country,
                                                                                    }),
                                                                                }),
                                                                            }))
                                                                            clearNpiFieldError('billingAddressLine')
                                                                            if (components?.city) {
                                                                                clearNpiFieldError('billingCity')
                                                                            }
                                                                            if (components?.state) {
                                                                                clearNpiFieldError('billingState')
                                                                            }
                                                                            if (
                                                                                components?.zipCode ||
                                                                                components?.postalCode
                                                                            ) {
                                                                                clearNpiFieldError('billingZipCode')
                                                                            }
                                                                            if (components?.country) {
                                                                                clearNpiFieldError('billingCountry')
                                                                            }
                                                                        }}
                                                                        placeholder="Start typing billing address..."
                                                                        addressType="billingAddress"
                                                                        required
                                                                        error={!!npiStepErrors.billingAddressLine}
                                                                        helperText={
                                                                            npiStepErrors.billingAddressLine
                                                                        }
                                                                    />
                                                                </Grid>
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                    md={4}
                                                                    id="billing-city-field"
                                                                >
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            color: 'grey.700',
                                                                            mb: 0.5,
                                                                            fontWeight: 500,
                                                                            fontSize: '0.875rem',
                                                                        }}
                                                                    >
                                                                        City *
                                                                    </Typography>
                                                                    <TextField
                                                                        fullWidth
                                                                        size="small"
                                                                        placeholder="Enter billing city"
                                                                        value={formData.billingCity}
                                                                        onChange={e => {
                                                                            setFormData(prev => ({
                                                                                ...prev,
                                                                                billingCity: e.target.value,
                                                                            }))
                                                                            clearNpiFieldError('billingCity')
                                                                        }}
                                                                        error={!!npiStepErrors.billingCity}
                                                                        helperText={npiStepErrors.billingCity}
                                                                        sx={{
                                                                            '& .MuiOutlinedInput-root': {
                                                                                bgcolor: 'white',
                                                                                borderRadius: 2,
                                                                                '& fieldset': {
                                                                                    borderColor: 'grey.300',
                                                                                },
                                                                                '&:hover fieldset': {
                                                                                    borderColor: 'grey.400',
                                                                                },
                                                                                '&.Mui-focused fieldset': {
                                                                                    borderColor: '#574F75',
                                                                                    borderWidth: 2,
                                                                                },
                                                                            },
                                                                        }}
                                                                    />
                                                                </Grid>
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                    md={4}
                                                                    id="billing-state-field"
                                                                >
                                                                    <Box id="billing-country-field">
                                                                        <LocationSelector
                                                                            country={formData.billingCountry}
                                                                            state={formData.billingState}
                                                                            onCountryChange={name => {
                                                                                setFormData(prev => ({
                                                                                    ...prev,
                                                                                    billingCountry: name,
                                                                                    billingState: '',
                                                                                }))
                                                                                clearNpiFieldError('billingCountry')
                                                                            }}
                                                                            onStateChange={name => {
                                                                                setFormData(prev => ({
                                                                                    ...prev,
                                                                                    billingState: name,
                                                                                }))
                                                                                clearNpiFieldError('billingState')
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                    {(npiStepErrors.billingState ||
                                                                        npiStepErrors.billingCountry) && (
                                                                            <FormHelperText error sx={{ mt: 1 }}>
                                                                                {npiStepErrors.billingState ||
                                                                                    npiStepErrors.billingCountry}
                                                                            </FormHelperText>
                                                                        )}
                                                                </Grid>
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                    md={4}
                                                                    id="billing-zip-field"
                                                                >
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            color: 'grey.700',
                                                                            mb: 0.5,
                                                                            fontWeight: 500,
                                                                            fontSize: '0.875rem',
                                                                        }}
                                                                    >
                                                                        Zip code *
                                                                    </Typography>
                                                                    <TextField
                                                                        fullWidth
                                                                        size="small"
                                                                        placeholder="Enter billing zip code"
                                                                        value={formData.billingZipCode}
                                                                        onChange={e => {
                                                                            setFormData(prev => ({
                                                                                ...prev,
                                                                                billingZipCode: e.target.value,
                                                                            }))
                                                                            clearNpiFieldError('billingZipCode')
                                                                        }}
                                                                        error={!!npiStepErrors.billingZipCode}
                                                                        helperText={npiStepErrors.billingZipCode}
                                                                        sx={{
                                                                            '& .MuiOutlinedInput-root': {
                                                                                bgcolor: 'white',
                                                                                borderRadius: 2,
                                                                                '& fieldset': {
                                                                                    borderColor: 'grey.300',
                                                                                },
                                                                                '&:hover fieldset': {
                                                                                    borderColor: 'grey.400',
                                                                                },
                                                                                '&.Mui-focused fieldset': {
                                                                                    borderColor: primaryColor,
                                                                                    borderWidth: 2,
                                                                                },
                                                                            },
                                                                        }}
                                                                    />
                                                                </Grid>
                                                            </Grid>

                                                            {/* License Information Card */}
                                                            <Box
                                                                sx={{
                                                                    bgcolor: '#f8f9fa',
                                                                    borderRadius: 2,
                                                                    p: 3,
                                                                    mb: 3,
                                                                    border: '1px solid #e9ecef',
                                                                }}
                                                            >
                                                                {/* <Typography
                                                                    variant="h6"
                                                                    sx={{
                                                                        color: 'grey.800',
                                                                        mb: 3,
                                                                        fontWeight: 600,
                                                                        fontSize: '1.125rem',
                                                                    }}
                                                                >
                                                                    License Information aa
                                                                </Typography> */}

                                                                <Grid container spacing={3}>
                                                                    <Grid
                                                                        item
                                                                        xs={12}
                                                                        md={6}
                                                                        id="license-type-field"
                                                                    >
                                                                        <Typography
                                                                            variant="body1"
                                                                            sx={{
                                                                                color: 'grey.700',
                                                                                mb: 1.5,
                                                                                fontWeight: 500,
                                                                                fontSize: '0.875rem',
                                                                            }}
                                                                        >
                                                                            Specialty License Type *
                                                                        </Typography>
                                                                        <FormControl
                                                                            fullWidth
                                                                            error={!!npiStepErrors.licenseType}
                                                                            disabled={isLoadingLicenseTypes}
                                                                        >
                                                                            <Select
                                                                                value={onboardingData.licenseType}
                                                                                onChange={handleOnboardingInputChange(
                                                                                    'licenseType'
                                                                                )}
                                                                                displayEmpty
                                                                                sx={{
                                                                                    borderRadius: 2,
                                                                                    '& .MuiOutlinedInput-notchedOutline':
                                                                                    {
                                                                                        borderColor: 'grey.300',
                                                                                    },
                                                                                    '&:hover .MuiOutlinedInput-notchedOutline':
                                                                                    {
                                                                                        borderColor: 'grey.400',
                                                                                    },
                                                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                                                                    {
                                                                                        borderColor: primaryColor,
                                                                                        borderWidth: 2,
                                                                                    },
                                                                                }}
                                                                            >
                                                                                <MenuItem value="" disabled>
                                                                                    <em>
                                                                                        {isLoadingLicenseTypes
                                                                                            ? 'Loading Specialty License Types *...'
                                                                                            : 'Select Specialty License type'}
                                                                                    </em>
                                                                                </MenuItem>
                                                                                {licenseTypes.map(licenseType => (
                                                                                    <MenuItem
                                                                                        key={licenseType._id}
                                                                                        value={licenseType._id}
                                                                                    >
                                                                                        {licenseType.task}
                                                                                    </MenuItem>
                                                                                ))}
                                                                            </Select>
                                                                            {npiStepErrors.licenseType && (
                                                                                <FormHelperText>
                                                                                    {npiStepErrors.licenseType}
                                                                                </FormHelperText>
                                                                            )}
                                                                        </FormControl>
                                                                    </Grid>
                                                                    {onboardingData.licenseType !== '69aacb7782693bdedf50db43' && (
                                                                        <>
                                                                            <Grid
                                                                                item
                                                                                xs={12}
                                                                                md={6}
                                                                                id="license-name-field"
                                                                            >
                                                                                <Typography
                                                                                    variant="body1"
                                                                                    sx={{
                                                                                        color: 'grey.700',
                                                                                        mb: 1.5,
                                                                                        fontWeight: 500,
                                                                                        fontSize: '0.875rem',
                                                                                    }}
                                                                                >
                                                                                    NPI Number
                                                                                    {isNpiRequired(onboardingData.licenseType)
                                                                                        ? ' *'
                                                                                        : ''}
                                                                                </Typography>
                                                                                <TextField
                                                                                    fullWidth
                                                                                    placeholder="Enter NPI number"
                                                                                    value={onboardingData.licenseNpi}
                                                                                    onChange={handleOnboardingInputChange(
                                                                                        'licenseNpi'
                                                                                    )}
                                                                                    required={isNpiRequired(
                                                                                        onboardingData.licenseType
                                                                                    )}
                                                                                    error={!!npiStepErrors.licenseNpi}
                                                                                    helperText={npiStepErrors.licenseNpi}
                                                                                    sx={{
                                                                                        '& .MuiOutlinedInput-root': {
                                                                                            bgcolor: 'white',
                                                                                            borderRadius: 2,
                                                                                            '& fieldset': {
                                                                                                borderColor: 'grey.300',
                                                                                            },
                                                                                            '&:hover fieldset': {
                                                                                                borderColor: 'grey.400',
                                                                                            },
                                                                                            '&.Mui-focused fieldset': {
                                                                                                borderColor: primaryColor,
                                                                                                borderWidth: 2,
                                                                                            },
                                                                                        },
                                                                                    }}
                                                                                />
                                                                            </Grid>
                                                                            <Grid
                                                                                item
                                                                                xs={12}
                                                                                md={6}
                                                                                id="license-state-field"
                                                                            >
                                                                                <Typography
                                                                                    variant="body1"
                                                                                    sx={{
                                                                                        color: 'grey.700',
                                                                                        mb: 1.5,
                                                                                        fontWeight: 500,
                                                                                        fontSize: '0.875rem',
                                                                                    }}
                                                                                >
                                                                                    License State*
                                                                                </Typography>
                                                                                <FormControl
                                                                                    fullWidth
                                                                                    error={!!npiStepErrors.licenseState}
                                                                                >
                                                                                    <Select
                                                                                        value={onboardingData.licenseState}
                                                                                        onChange={handleOnboardingInputChange(
                                                                                            'licenseState'
                                                                                        )}
                                                                                        displayEmpty
                                                                                        sx={{
                                                                                            borderRadius: 2,
                                                                                            '& .MuiOutlinedInput-notchedOutline':
                                                                                            {
                                                                                                borderColor: 'grey.300',
                                                                                            },
                                                                                            '&:hover .MuiOutlinedInput-notchedOutline':
                                                                                            {
                                                                                                borderColor: 'grey.400',
                                                                                            },
                                                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                                                                            {
                                                                                                borderColor: primaryColor,
                                                                                                borderWidth: 2,
                                                                                            },
                                                                                        }}
                                                                                    >
                                                                                        <MenuItem value="" disabled>
                                                                                            <em>Select state</em>
                                                                                        </MenuItem>
                                                                                        {states.map(state => (
                                                                                            <MenuItem
                                                                                                key={state._id}
                                                                                                value={state.name}
                                                                                            >
                                                                                                {state.name}
                                                                                            </MenuItem>
                                                                                        ))}
                                                                                    </Select>
                                                                                    {npiStepErrors.licenseState && (
                                                                                        <FormHelperText>
                                                                                            {npiStepErrors.licenseState}
                                                                                        </FormHelperText>
                                                                                    )}
                                                                                </FormControl>
                                                                            </Grid>
                                                                            <Grid
                                                                                item
                                                                                xs={12}
                                                                                md={6}
                                                                                id="license-number-field"
                                                                            >
                                                                                <Typography
                                                                                    variant="body1"
                                                                                    sx={{
                                                                                        color: 'grey.700',
                                                                                        mb: 1.5,
                                                                                        fontWeight: 500,
                                                                                        fontSize: '0.875rem',
                                                                                    }}
                                                                                >
                                                                                    License Number*
                                                                                </Typography>
                                                                                <TextField
                                                                                    fullWidth
                                                                                    placeholder="Enter license number"
                                                                                    value={onboardingData.licenseNumber}
                                                                                    onChange={handleOnboardingInputChange(
                                                                                        'licenseNumber'
                                                                                    )}
                                                                                    error={!!npiStepErrors.licenseNumber}
                                                                                    helperText={npiStepErrors.licenseNumber}
                                                                                    sx={{
                                                                                        '& .MuiOutlinedInput-root': {
                                                                                            bgcolor: 'white',
                                                                                            borderRadius: 2,
                                                                                            '& fieldset': {
                                                                                                borderColor: 'grey.300',
                                                                                            },
                                                                                            '&:hover fieldset': {
                                                                                                borderColor: 'grey.400',
                                                                                            },
                                                                                            '&.Mui-focused fieldset': {
                                                                                                borderColor: primaryColor,
                                                                                                borderWidth: 2,
                                                                                            },
                                                                                        },
                                                                                    }}
                                                                                />
                                                                            </Grid>
                                                                            <Grid
                                                                                item
                                                                                xs={12}
                                                                                md={6}
                                                                                id="license-issue-field"
                                                                            >
                                                                                <Typography
                                                                                    variant="body1"
                                                                                    sx={{
                                                                                        color: 'grey.700',
                                                                                        mb: 1.5,
                                                                                        fontWeight: 500,
                                                                                        fontSize: '0.875rem',
                                                                                    }}
                                                                                >
                                                                                    License Issue Date*
                                                                                </Typography>
                                                                                <LocalizationProvider
                                                                                    dateAdapter={AdapterDateFns}
                                                                                >
                                                                                    <DatePicker
                                                                                        value={
                                                                                            onboardingData.issueDate
                                                                                                ? new Date(onboardingData.issueDate)
                                                                                                : null
                                                                                        }
                                                                                        onChange={value => {
                                                                                            const formatted = value
                                                                                                ? formatDate(
                                                                                                    value as Date,
                                                                                                    'MM/dd/yyyy'
                                                                                                )
                                                                                                : ''
                                                                                            setOnboardingData(prev => ({
                                                                                                ...prev,
                                                                                                issueDate: formatted,
                                                                                            }))
                                                                                            clearNpiFieldError('issueDate')
                                                                                        }}
                                                                                        format="MM/dd/yyyy"
                                                                                        views={['year', 'month', 'day']}
                                                                                        openTo="year"
                                                                                        maxDate={new Date()}
                                                                                        slotProps={{
                                                                                            textField: {
                                                                                                fullWidth: true,
                                                                                                placeholder: 'MM/DD/YYYY',
                                                                                                error: !!npiStepErrors.issueDate,
                                                                                                helperText: npiStepErrors.issueDate,
                                                                                                sx: {
                                                                                                    '& .MuiOutlinedInput-root': {
                                                                                                        bgcolor: 'white',
                                                                                                        borderRadius: 2,
                                                                                                        '& fieldset': {
                                                                                                            borderColor: 'grey.300',
                                                                                                        },
                                                                                                        '&:hover fieldset': {
                                                                                                            borderColor: 'grey.400',
                                                                                                        },
                                                                                                        '&.Mui-focused fieldset': {
                                                                                                            borderColor: primaryColor,
                                                                                                            borderWidth: 2,
                                                                                                        },
                                                                                                    },
                                                                                                },
                                                                                            },
                                                                                        }}
                                                                                    />
                                                                                </LocalizationProvider>
                                                                            </Grid>
                                                                            <Grid
                                                                                item
                                                                                xs={12}
                                                                                md={6}
                                                                                id="license-expiration-field"
                                                                            >
                                                                                <Typography
                                                                                    variant="body1"
                                                                                    sx={{
                                                                                        color: 'grey.700',
                                                                                        mb: 1.5,
                                                                                        fontWeight: 500,
                                                                                        fontSize: '0.875rem',
                                                                                    }}
                                                                                >
                                                                                    License Expiration Date*
                                                                                </Typography>
                                                                                <LocalizationProvider
                                                                                    dateAdapter={AdapterDateFns}
                                                                                >
                                                                                    <DatePicker
                                                                                        value={
                                                                                            onboardingData.licenseExpirationDate
                                                                                                ? new Date(
                                                                                                    onboardingData.licenseExpirationDate
                                                                                                )
                                                                                                : null
                                                                                        }
                                                                                        onChange={value => {
                                                                                            const formatted = value
                                                                                                ? formatDate(
                                                                                                    value as Date,
                                                                                                    'MM/dd/yyyy'
                                                                                                )
                                                                                                : ''
                                                                                            setOnboardingData(prev => ({
                                                                                                ...prev,
                                                                                                licenseExpirationDate: formatted,
                                                                                            }))
                                                                                            clearNpiFieldError(
                                                                                                'licenseExpirationDate'
                                                                                            )
                                                                                        }}
                                                                                        format="MM/dd/yyyy"
                                                                                        views={['year', 'month', 'day']}
                                                                                        openTo="year"
                                                                                        minDate={new Date()}
                                                                                        slotProps={{
                                                                                            textField: {
                                                                                                fullWidth: true,
                                                                                                placeholder: 'MM/DD/YYYY',
                                                                                                error:
                                                                                                    !!npiStepErrors.licenseExpirationDate,
                                                                                                helperText:
                                                                                                    npiStepErrors.licenseExpirationDate,
                                                                                                sx: {
                                                                                                    '& .MuiOutlinedInput-root': {
                                                                                                        bgcolor: 'white',
                                                                                                        borderRadius: 2,
                                                                                                        '& fieldset': {
                                                                                                            borderColor: 'grey.300',
                                                                                                        },
                                                                                                        '&:hover fieldset': {
                                                                                                            borderColor: 'grey.400',
                                                                                                        },
                                                                                                        '&.Mui-focused fieldset': {
                                                                                                            borderColor: primaryColor,
                                                                                                            borderWidth: 2,
                                                                                                        },
                                                                                                    },
                                                                                                },
                                                                                            },
                                                                                        }}
                                                                                    />
                                                                                </LocalizationProvider>
                                                                            </Grid>
                                                                            <Grid item xs={12} md={6}>
                                                                                <Typography
                                                                                    variant="body1"
                                                                                    sx={{
                                                                                        color: 'grey.700',
                                                                                        mb: 1.5,
                                                                                        fontWeight: 500,
                                                                                        fontSize: '0.875rem',
                                                                                    }}
                                                                                >
                                                                                    License Status*
                                                                                </Typography>
                                                                                <FormControl fullWidth>
                                                                                    <Select
                                                                                        value={
                                                                                            onboardingData.licenseStatus ||
                                                                                            'Active'
                                                                                        }
                                                                                        onChange={handleOnboardingInputChange(
                                                                                            'licenseStatus'
                                                                                        )}
                                                                                        sx={{
                                                                                            borderRadius: 2,
                                                                                            '& .MuiOutlinedInput-notchedOutline':
                                                                                            {
                                                                                                borderColor: 'grey.300',
                                                                                            },
                                                                                            '&:hover .MuiOutlinedInput-notchedOutline':
                                                                                            {
                                                                                                borderColor: 'grey.400',
                                                                                            },
                                                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                                                                            {
                                                                                                borderColor: primaryColor,
                                                                                                borderWidth: 2,
                                                                                            },
                                                                                        }}
                                                                                    >
                                                                                        <MenuItem value="Active">
                                                                                            Active
                                                                                        </MenuItem>
                                                                                        <MenuItem value="Inactive">
                                                                                            Inactive
                                                                                        </MenuItem>
                                                                                        <MenuItem value="Suspended">
                                                                                            Suspended
                                                                                        </MenuItem>
                                                                                        <MenuItem value="Expired">
                                                                                            Expired
                                                                                        </MenuItem>
                                                                                    </Select>
                                                                                </FormControl>
                                                                            </Grid>
                                                                        </>
                                                                    )}


                                                                </Grid>

                                                                {/* Upload License Document */}
                                                                <Box sx={{ mt: 3 }}>
                                                                    <Typography
                                                                        variant="body1"
                                                                        sx={{
                                                                            color: 'grey.700',
                                                                            mb: 1.5,
                                                                            fontWeight: 500,
                                                                            fontSize: '0.875rem',
                                                                        }}
                                                                    >
                                                                        Upload License Document {onboardingData.licenseType !== '69aacb7782693bdedf50db43' ? "*" : ""}
                                                                    </Typography>
                                                                    <Box
                                                                        id="license-file-field"
                                                                        sx={{
                                                                            border: '2px dashed #d1d5db',
                                                                            borderRadius: 2,
                                                                            p: 4,
                                                                            textAlign: 'center',
                                                                            bgcolor: 'white',
                                                                            cursor: isUploadingLicense
                                                                                ? 'wait'
                                                                                : 'pointer',
                                                                            opacity: isUploadingLicense ? 0.6 : 1,
                                                                            pointerEvents: isUploadingLicense
                                                                                ? 'none'
                                                                                : 'auto',
                                                                            '&:hover': {
                                                                                borderColor: isUploadingLicense
                                                                                    ? '#d1d5db'
                                                                                    : primaryColor,
                                                                                bgcolor: isUploadingLicense
                                                                                    ? 'white'
                                                                                    : '#f8f9ff',
                                                                            },
                                                                        }}
                                                                        onDrop={handleDrop}
                                                                        onDragOver={handleDragOver}
                                                                        onClick={() => {
                                                                            if (!isUploadingLicense) {
                                                                                document
                                                                                    .getElementById('license-upload')
                                                                                    ?.click()
                                                                            }
                                                                        }}
                                                                    >
                                                                        {isUploadingLicense ? (
                                                                            <>
                                                                                <CircularProgress
                                                                                    size={48}
                                                                                    sx={{ mb: 2, color: primaryColor }}
                                                                                />
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    sx={{
                                                                                        color: 'grey.600',
                                                                                        mb: 1,
                                                                                        fontWeight: 500,
                                                                                    }}
                                                                                >
                                                                                    Uploading license document...
                                                                                </Typography>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <CloudUpload
                                                                                    sx={{
                                                                                        fontSize: 48,
                                                                                        color: '#9ca3af',
                                                                                        mb: 2,
                                                                                    }}
                                                                                />
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    sx={{
                                                                                        color: 'grey.600',
                                                                                        mb: 1,
                                                                                        fontWeight: 500,
                                                                                    }}
                                                                                >
                                                                                    Drag and drop your license document,
                                                                                    or click to browse
                                                                                </Typography>
                                                                            </>
                                                                        )}
                                                                        <Typography
                                                                            variant="caption"
                                                                            sx={{
                                                                                color: 'grey.500',
                                                                                display: 'block',
                                                                                mb: 2,
                                                                            }}
                                                                        >
                                                                            Supported formats: PDF, JPG, PNG (Max
                                                                            10MB)
                                                                        </Typography>
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                color: primaryColor,
                                                                                fontWeight: 500,
                                                                                textDecoration: 'underline',
                                                                                cursor: 'pointer',
                                                                            }}
                                                                            onClick={e => {
                                                                                e.stopPropagation()
                                                                                document
                                                                                    .getElementById('license-upload')
                                                                                    ?.click()
                                                                            }}
                                                                        >
                                                                            Browse
                                                                        </Typography>
                                                                        <input
                                                                            id="license-upload"
                                                                            type="file"
                                                                            accept=".pdf,.jpg,.jpeg,.png"
                                                                            style={{ display: 'none' }}
                                                                            disabled={isUploadingLicense}
                                                                            onChange={handleLicenseDocumentChange}
                                                                        />
                                                                        {!!onboardingData.licenseFileUrl && (
                                                                            <Box sx={{ mt: 2, position: 'relative', display: 'inline-block' }}>
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={handleRemoveMainLicense}
                                                                                    sx={{
                                                                                        position: 'absolute',
                                                                                        top: -10,
                                                                                        right: -10,
                                                                                        bgcolor: 'white',
                                                                                        boxShadow: 2,
                                                                                        color: '#dc2626',
                                                                                        zIndex: 1,
                                                                                        '&:hover': {
                                                                                            bgcolor: '#fee2e2',
                                                                                        },
                                                                                    }}
                                                                                >
                                                                                    <Cancel fontSize="small" />
                                                                                </IconButton>
                                                                                {onboardingData.licenseFilePreview ? (
                                                                                    <Box
                                                                                        sx={{
                                                                                            mb: 1,
                                                                                            display: 'flex',
                                                                                            justifyContent: 'center',
                                                                                        }}
                                                                                    >
                                                                                        <img
                                                                                            src={
                                                                                                onboardingData.licenseFilePreview
                                                                                            }
                                                                                            alt="License preview"
                                                                                            style={{
                                                                                                maxWidth: '150px',
                                                                                                maxHeight: '150px',
                                                                                                borderRadius: '8px',
                                                                                                objectFit: 'contain',
                                                                                                border: '1px solid #e5e7eb',
                                                                                            }}
                                                                                        />
                                                                                    </Box>
                                                                                ) : onboardingData.licenseFileName
                                                                                    ?.toLowerCase()
                                                                                    .endsWith('.pdf') ? (
                                                                                    <Box
                                                                                        sx={{
                                                                                            mb: 1,
                                                                                            display: 'flex',
                                                                                            justifyContent: 'center',
                                                                                        }}
                                                                                    >
                                                                                        <PictureAsPdf
                                                                                            sx={{
                                                                                                fontSize: 64,
                                                                                                color: '#dc2626',
                                                                                            }}
                                                                                        />
                                                                                    </Box>
                                                                                ) : null}
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    sx={{
                                                                                        color: 'grey.600',
                                                                                        display: 'block',
                                                                                        fontWeight: 500,
                                                                                        textAlign: 'center',
                                                                                    }}
                                                                                >
                                                                                    ✓ Uploaded:{' '}
                                                                                    {onboardingData.licenseFileName ||
                                                                                        'License document'}
                                                                                </Typography>
                                                                            </Box>
                                                                        )}
                                                                        {npiStepErrors.licenseFileUrl && (
                                                                            <Typography
                                                                                variant="caption"
                                                                                sx={{
                                                                                    color: 'error.main',
                                                                                    display: 'block',
                                                                                    mt: 1.5,
                                                                                }}
                                                                            >
                                                                                {npiStepErrors.licenseFileUrl}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                            </Box>

                                                            {/* Render additional license cards */}
                                                            {additionalLicenses.map((lic, index) => (
                                                                <Box
                                                                    key={index}
                                                                    sx={{
                                                                        bgcolor: '#f8f9fa',
                                                                        borderRadius: 2,
                                                                        p: 3,
                                                                        mb: 3,
                                                                        border: '1px solid #e9ecef',
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',

                                                                            justifyContent: 'flex-end',
                                                                            mb: 3,
                                                                        }}
                                                                    >
                                                                        {/* <Typography
                                                                            variant="h6"
                                                                            sx={{
                                                                                color: 'grey.800',
                                                                                fontWeight: 600,
                                                                                fontSize: '1.125rem',
                                                                            }}
                                                                        >
                                                                            License Information dd
                                                                        </Typography> */}
                                                                        <IconButton
                                                                            aria-label="Remove license"
                                                                            onClick={() =>
                                                                                setAdditionalLicenses(prev =>
                                                                                    prev.filter((_, i) => i !== index)
                                                                                )
                                                                            }
                                                                            size="small"
                                                                            sx={{ color: 'grey.600' }}
                                                                        >
                                                                            <Delete />
                                                                        </IconButton>
                                                                    </Box>

                                                                    <Grid container spacing={3}>
                                                                        <Grid item xs={12} md={6}>
                                                                            <Typography
                                                                                variant="body1"
                                                                                sx={{
                                                                                    color: 'grey.700',
                                                                                    mb: 1.5,
                                                                                    fontWeight: 500,
                                                                                    fontSize: '0.875rem',
                                                                                }}
                                                                            >
                                                                                Specialty License Type*
                                                                            </Typography>
                                                                            <FormControl
                                                                                fullWidth
                                                                                disabled={isLoadingLicenseTypes}
                                                                            >
                                                                                <Select
                                                                                    value={lic.licenseType}
                                                                                    onChange={e =>
                                                                                        handleAdditionalLicenseChange(
                                                                                            index,
                                                                                            'licenseType',
                                                                                            e.target.value as string
                                                                                        )
                                                                                    }
                                                                                    displayEmpty
                                                                                    sx={{
                                                                                        borderRadius: 2,
                                                                                        '& .MuiOutlinedInput-notchedOutline':
                                                                                            { borderColor: 'grey.300' },
                                                                                        '&:hover .MuiOutlinedInput-notchedOutline':
                                                                                            { borderColor: 'grey.400' },
                                                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                                                                        {
                                                                                            borderColor: primaryColor,
                                                                                            borderWidth: 2,
                                                                                        },
                                                                                    }}
                                                                                >
                                                                                    <MenuItem value="" disabled>
                                                                                        <em>
                                                                                            {isLoadingLicenseTypes
                                                                                                ? 'Loading license types...'
                                                                                                : 'Select license type'}
                                                                                        </em>
                                                                                    </MenuItem>
                                                                                    {licenseTypes.map(licenseType => (
                                                                                        <MenuItem
                                                                                            key={licenseType._id}
                                                                                            value={licenseType._id}
                                                                                        >
                                                                                            {licenseType.task}
                                                                                        </MenuItem>
                                                                                    ))}
                                                                                </Select>
                                                                            </FormControl>
                                                                        </Grid>
                                                                        {lic.licenseType !== '69aacb7782693bdedf50db43' && (
                                                                            <>
                                                                                <Grid item xs={12} md={6}>
                                                                                    <Typography
                                                                                        variant="body1"
                                                                                        sx={{
                                                                                            color: 'grey.700',
                                                                                            mb: 1.5,
                                                                                            fontWeight: 500,
                                                                                            fontSize: '0.875rem',
                                                                                        }}
                                                                                    >
                                                                                        NPI Number
                                                                                        {isNpiRequired(lic.licenseType)
                                                                                            ? ' *'
                                                                                            : ''}
                                                                                    </Typography>
                                                                                    <TextField
                                                                                        fullWidth
                                                                                        placeholder="Enter NPI number"
                                                                                        value={lic.licenseNpi || prefildlicenseNpi}
                                                                                        onChange={e =>
                                                                                            handleAdditionalLicenseChange(
                                                                                                index,
                                                                                                'licenseNpi',
                                                                                                e.target.value
                                                                                            )
                                                                                        }
                                                                                        required={isNpiRequired(
                                                                                            lic.licenseType
                                                                                        )}
                                                                                        error={
                                                                                            !!npiStepErrors[
                                                                                            `additionalLicenseNpi_${index}`
                                                                                            ]
                                                                                        }
                                                                                        helperText={
                                                                                            npiStepErrors[
                                                                                            `additionalLicenseNpi_${index}`
                                                                                            ]
                                                                                        }
                                                                                        sx={{
                                                                                            '& .MuiOutlinedInput-root': {
                                                                                                bgcolor: 'white',
                                                                                                borderRadius: 2,
                                                                                                '& fieldset': {
                                                                                                    borderColor: 'grey.300',
                                                                                                },
                                                                                                '&:hover fieldset': {
                                                                                                    borderColor: 'grey.400',
                                                                                                },
                                                                                                '&.Mui-focused fieldset': {
                                                                                                    borderColor: primaryColor,
                                                                                                    borderWidth: 2,
                                                                                                },
                                                                                            },
                                                                                        }}
                                                                                    />
                                                                                </Grid>
                                                                                <Grid item xs={12} md={6}>
                                                                                    <Typography
                                                                                        variant="body1"
                                                                                        sx={{
                                                                                            color: 'grey.700',
                                                                                            mb: 1.5,
                                                                                            fontWeight: 500,
                                                                                            fontSize: '0.875rem',
                                                                                        }}
                                                                                    >
                                                                                        License State*
                                                                                    </Typography>
                                                                                    <FormControl fullWidth>
                                                                                        <Select
                                                                                            value={lic.licenseState}
                                                                                            onChange={e =>
                                                                                                handleAdditionalLicenseChange(
                                                                                                    index,
                                                                                                    'licenseState',
                                                                                                    e.target.value as string
                                                                                                )
                                                                                            }
                                                                                            displayEmpty
                                                                                            sx={{
                                                                                                borderRadius: 2,
                                                                                                '& .MuiOutlinedInput-notchedOutline':
                                                                                                    { borderColor: 'grey.300' },
                                                                                                '&:hover .MuiOutlinedInput-notchedOutline':
                                                                                                    { borderColor: 'grey.400' },
                                                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                                                                                {
                                                                                                    borderColor: primaryColor,
                                                                                                    borderWidth: 2,
                                                                                                },
                                                                                            }}
                                                                                        >
                                                                                            <MenuItem value="" disabled>
                                                                                                <em>Select state</em>
                                                                                            </MenuItem>
                                                                                            {states.map(state => (
                                                                                                <MenuItem
                                                                                                    key={state._id}
                                                                                                    value={state.name}
                                                                                                >
                                                                                                    {state.name}
                                                                                                </MenuItem>
                                                                                            ))}
                                                                                        </Select>
                                                                                    </FormControl>
                                                                                </Grid>
                                                                                <Grid item xs={12} md={6}>
                                                                                    <Typography
                                                                                        variant="body1"
                                                                                        sx={{
                                                                                            color: 'grey.700',
                                                                                            mb: 1.5,
                                                                                            fontWeight: 500,
                                                                                            fontSize: '0.875rem',
                                                                                        }}
                                                                                    >
                                                                                        License Number*
                                                                                    </Typography>
                                                                                    <TextField
                                                                                        fullWidth
                                                                                        placeholder="License number"
                                                                                        value={lic.licenseNumber}
                                                                                        onChange={e =>
                                                                                            handleAdditionalLicenseChange(
                                                                                                index,
                                                                                                'licenseNumber',
                                                                                                e.target.value
                                                                                            )
                                                                                        }
                                                                                        sx={{
                                                                                            '& .MuiOutlinedInput-root': {
                                                                                                bgcolor: 'white',
                                                                                                borderRadius: 2,
                                                                                                '& fieldset': {
                                                                                                    borderColor: 'grey.300',
                                                                                                },
                                                                                                '&:hover fieldset': {
                                                                                                    borderColor: 'grey.400',
                                                                                                },
                                                                                                '&.Mui-focused fieldset': {
                                                                                                    borderColor: primaryColor,
                                                                                                    borderWidth: 2,
                                                                                                },
                                                                                            },
                                                                                        }}
                                                                                    />
                                                                                </Grid>
                                                                                <Grid item xs={12} md={6}>
                                                                                    <Typography
                                                                                        variant="body1"
                                                                                        sx={{
                                                                                            color: 'grey.700',
                                                                                            mb: 1.5,
                                                                                            fontWeight: 500,
                                                                                            fontSize: '0.875rem',
                                                                                        }}
                                                                                    >
                                                                                        License Issue Date*
                                                                                    </Typography>
                                                                                    <LocalizationProvider
                                                                                        dateAdapter={AdapterDateFns}
                                                                                    >
                                                                                        <DatePicker
                                                                                            value={
                                                                                                lic.issueDate
                                                                                                    ? new Date(lic.issueDate)
                                                                                                    : null
                                                                                            }
                                                                                            onChange={value =>
                                                                                                handleAdditionalLicenseChange(
                                                                                                    index,
                                                                                                    'issueDate',
                                                                                                    value
                                                                                                        ? formatDate(
                                                                                                            value as Date,
                                                                                                            'MM/dd/yyyy'
                                                                                                        )
                                                                                                        : ''
                                                                                                )
                                                                                            }
                                                                                            format="MM/dd/yyyy"
                                                                                            views={['year', 'month', 'day']}
                                                                                            openTo="year"
                                                                                            maxDate={new Date()}
                                                                                            slotProps={{
                                                                                                textField: {
                                                                                                    fullWidth: true,
                                                                                                    placeholder: 'MM/DD/YYYY',
                                                                                                    sx: {
                                                                                                        '& .MuiOutlinedInput-root': {
                                                                                                            bgcolor: 'white',
                                                                                                            borderRadius: 2,
                                                                                                            '& fieldset': {
                                                                                                                borderColor: 'grey.300',
                                                                                                            },
                                                                                                            '&:hover fieldset': {
                                                                                                                borderColor: 'grey.400',
                                                                                                            },
                                                                                                            '&.Mui-focused fieldset': {
                                                                                                                borderColor: primaryColor,
                                                                                                                borderWidth: 2,
                                                                                                            },
                                                                                                        },
                                                                                                    },
                                                                                                },
                                                                                            }}
                                                                                        />
                                                                                    </LocalizationProvider>
                                                                                </Grid>
                                                                                <Grid item xs={12} md={6}>
                                                                                    <Typography
                                                                                        variant="body1"
                                                                                        sx={{
                                                                                            color: 'grey.700',
                                                                                            mb: 1.5,
                                                                                            fontWeight: 500,
                                                                                            fontSize: '0.875rem',
                                                                                        }}
                                                                                    >
                                                                                        License Expiration Date*
                                                                                    </Typography>
                                                                                    <LocalizationProvider
                                                                                        dateAdapter={AdapterDateFns}
                                                                                    >
                                                                                        <DatePicker
                                                                                            value={
                                                                                                lic.licenseExpirationDate
                                                                                                    ? new Date(
                                                                                                        lic.licenseExpirationDate
                                                                                                    )
                                                                                                    : null
                                                                                            }
                                                                                            onChange={value =>
                                                                                                handleAdditionalLicenseChange(
                                                                                                    index,
                                                                                                    'licenseExpirationDate',
                                                                                                    value
                                                                                                        ? formatDate(
                                                                                                            value as Date,
                                                                                                            'MM/dd/yyyy'
                                                                                                        )
                                                                                                        : ''
                                                                                                )
                                                                                            }
                                                                                            format="MM/dd/yyyy"
                                                                                            views={['year', 'month', 'day']}
                                                                                            openTo="year"
                                                                                            minDate={new Date()}
                                                                                            slotProps={{
                                                                                                textField: {
                                                                                                    fullWidth: true,
                                                                                                    placeholder: 'MM/DD/YYYY',
                                                                                                    sx: {
                                                                                                        '& .MuiOutlinedInput-root': {
                                                                                                            bgcolor: 'white',
                                                                                                            borderRadius: 2,
                                                                                                            '& fieldset': {
                                                                                                                borderColor: 'grey.300',
                                                                                                            },
                                                                                                            '&:hover fieldset': {
                                                                                                                borderColor: 'grey.400',
                                                                                                            },
                                                                                                            '&.Mui-focused fieldset': {
                                                                                                                borderColor: primaryColor,
                                                                                                                borderWidth: 2,
                                                                                                            },
                                                                                                        },
                                                                                                    },
                                                                                                },
                                                                                            }}
                                                                                        />
                                                                                    </LocalizationProvider>
                                                                                </Grid>
                                                                                <Grid item xs={12} md={6}>
                                                                                    <Typography
                                                                                        variant="body1"
                                                                                        sx={{
                                                                                            color: 'grey.700',
                                                                                            mb: 1.5,
                                                                                            fontWeight: 500,
                                                                                            fontSize: '0.875rem',
                                                                                        }}
                                                                                    >
                                                                                        License Status*
                                                                                    </Typography>
                                                                                    <FormControl fullWidth>
                                                                                        <Select
                                                                                            value={lic.licenseStatus || 'Active'}
                                                                                            onChange={e =>
                                                                                                handleAdditionalLicenseChange(
                                                                                                    index,
                                                                                                    'licenseStatus',
                                                                                                    e.target.value as string
                                                                                                )
                                                                                            }
                                                                                            sx={{
                                                                                                borderRadius: 2,
                                                                                                '& .MuiOutlinedInput-notchedOutline':
                                                                                                    { borderColor: 'grey.300' },
                                                                                                '&:hover .MuiOutlinedInput-notchedOutline':
                                                                                                    { borderColor: 'grey.400' },
                                                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                                                                                {
                                                                                                    borderColor: primaryColor,
                                                                                                    borderWidth: 2,
                                                                                                },
                                                                                            }}
                                                                                        >
                                                                                            <MenuItem value="Active">
                                                                                                Active
                                                                                            </MenuItem>
                                                                                            <MenuItem value="Inactive">
                                                                                                Inactive
                                                                                            </MenuItem>
                                                                                            <MenuItem value="Suspended">
                                                                                                Suspended
                                                                                            </MenuItem>
                                                                                            <MenuItem value="Expired">
                                                                                                Expired
                                                                                            </MenuItem>
                                                                                        </Select>
                                                                                    </FormControl>
                                                                                </Grid>
                                                                            </>)}
                                                                    </Grid>


                                                                    {/* Upload for additional license */}
                                                                    <Box sx={{ mt: 3 }}>
                                                                        <Typography
                                                                            variant="body1"
                                                                            sx={{
                                                                                color: 'grey.700',
                                                                                mb: 1.5,
                                                                                fontWeight: 500,
                                                                                fontSize: '0.875rem',
                                                                            }}
                                                                        >
                                                                            Upload License Document { } {onboardingData.licenseType !== '69aacb7782693bdedf50db43' ? "*" : ""}
                                                                        </Typography>
                                                                        <Box
                                                                            sx={{
                                                                                border: '2px dashed #d1d5db',
                                                                                borderRadius: 2,
                                                                                p: 4,
                                                                                textAlign: 'center',
                                                                                bgcolor: 'white',
                                                                                cursor: isUploadingAdditionalLicenses[
                                                                                    index
                                                                                ]
                                                                                    ? 'wait'
                                                                                    : 'pointer',
                                                                                opacity: isUploadingAdditionalLicenses[
                                                                                    index
                                                                                ]
                                                                                    ? 0.6
                                                                                    : 1,
                                                                                pointerEvents:
                                                                                    isUploadingAdditionalLicenses[index]
                                                                                        ? 'none'
                                                                                        : 'auto',
                                                                                '&:hover': {
                                                                                    borderColor:
                                                                                        isUploadingAdditionalLicenses[index]
                                                                                            ? '#d1d5db'
                                                                                            : primaryColor,
                                                                                    bgcolor:
                                                                                        isUploadingAdditionalLicenses[index]
                                                                                            ? 'white'
                                                                                            : '#f8f9ff',
                                                                                },
                                                                            }}
                                                                            onClick={() => {
                                                                                if (
                                                                                    !isUploadingAdditionalLicenses[index]
                                                                                ) {
                                                                                    document
                                                                                        .getElementById(
                                                                                            `license-upload-${index}`
                                                                                        )
                                                                                        ?.click()
                                                                                }
                                                                            }}
                                                                        >
                                                                            {isUploadingAdditionalLicenses[index] ? (
                                                                                <>
                                                                                    <CircularProgress
                                                                                        size={48}
                                                                                        sx={{ mb: 2, color: primaryColor }}
                                                                                    />
                                                                                    <Typography
                                                                                        variant="body2"
                                                                                        sx={{
                                                                                            color: 'grey.600',
                                                                                            mb: 1,
                                                                                            fontWeight: 500,
                                                                                        }}
                                                                                    >
                                                                                        Uploading license document...
                                                                                    </Typography>
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <CloudUpload
                                                                                        sx={{
                                                                                            fontSize: 48,
                                                                                            color: '#9ca3af',
                                                                                            mb: 2,
                                                                                        }}
                                                                                    />
                                                                                    <Typography
                                                                                        variant="body2"
                                                                                        sx={{
                                                                                            color: 'grey.600',
                                                                                            mb: 1,
                                                                                            fontWeight: 500,
                                                                                        }}
                                                                                    >
                                                                                        Drag and drop your license document,
                                                                                        or click to browse
                                                                                    </Typography>
                                                                                </>
                                                                            )}
                                                                            <Typography
                                                                                variant="caption"
                                                                                sx={{
                                                                                    color: 'grey.500',
                                                                                    display: 'block',
                                                                                    mb: 2,
                                                                                }}
                                                                            >
                                                                                Supported formats: PDF, JPG, PNG (Max
                                                                                10MB)
                                                                            </Typography>
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{
                                                                                    color: primaryColor,
                                                                                    fontWeight: 500,
                                                                                    textDecoration: 'underline',
                                                                                    cursor: 'pointer',
                                                                                }}
                                                                                onClick={e => {
                                                                                    e.stopPropagation()
                                                                                    document
                                                                                        .getElementById(
                                                                                            `license-upload-${index}`
                                                                                        )
                                                                                        ?.click()
                                                                                }}
                                                                            >
                                                                                Browse
                                                                            </Typography>
                                                                            <input
                                                                                id={`license-upload-${index}`}
                                                                                type="file"
                                                                                accept=".pdf,.jpg,.jpeg,.png"
                                                                                style={{ display: 'none' }}
                                                                                disabled={
                                                                                    isUploadingAdditionalLicenses[index]
                                                                                }
                                                                                onChange={e => {
                                                                                    handleAdditionalLicenseFile(
                                                                                        index,
                                                                                        e.target.files?.[0] ?? null
                                                                                    )
                                                                                    e.target.value = ''
                                                                                }}
                                                                            />
                                                                            {!!lic.licenseFileUrl && (
                                                                                <Box sx={{ mt: 2, position: 'relative', display: 'inline-block' }}>
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        onClick={(e) => handleRemoveAdditionalLicenseFile(index, e)}
                                                                                        sx={{
                                                                                            position: 'absolute',
                                                                                            top: -10,
                                                                                            right: -10,
                                                                                            bgcolor: 'white',
                                                                                            boxShadow: 2,
                                                                                            color: '#dc2626',
                                                                                            zIndex: 1,
                                                                                            '&:hover': {
                                                                                                bgcolor: '#fee2e2',
                                                                                            },
                                                                                        }}
                                                                                    >
                                                                                        <Cancel fontSize="small" />
                                                                                    </IconButton>
                                                                                    {lic.licenseFilePreview ? (
                                                                                        <Box
                                                                                            sx={{
                                                                                                mb: 1,
                                                                                                display: 'flex',
                                                                                                justifyContent: 'center',
                                                                                            }}
                                                                                        >
                                                                                            <img
                                                                                                src={lic.licenseFilePreview}
                                                                                                alt="License preview"
                                                                                                style={{
                                                                                                    maxWidth: '150px',
                                                                                                    maxHeight: '150px',
                                                                                                    borderRadius: '8px',
                                                                                                    objectFit: 'contain',
                                                                                                    border: '1px solid #e5e7eb',
                                                                                                }}
                                                                                            />
                                                                                        </Box>
                                                                                    ) : lic.licenseFileName
                                                                                        ?.toLowerCase()
                                                                                        .endsWith('.pdf') ? (
                                                                                        <Box
                                                                                            sx={{
                                                                                                mb: 1,
                                                                                                display: 'flex',
                                                                                                justifyContent: 'center',
                                                                                            }}
                                                                                        >
                                                                                            <PictureAsPdf
                                                                                                sx={{
                                                                                                    fontSize: 64,
                                                                                                    color: '#dc2626',
                                                                                                }}
                                                                                            />
                                                                                        </Box>
                                                                                    ) : null}
                                                                                    <Typography
                                                                                        variant="caption"
                                                                                        sx={{
                                                                                            color: 'grey.600',
                                                                                            display: 'block',
                                                                                            fontWeight: 500,
                                                                                            textAlign: 'center',
                                                                                        }}
                                                                                    >
                                                                                        ✓ Uploaded:{' '}
                                                                                        {lic.licenseFileName ||
                                                                                            'License document'}
                                                                                    </Typography>
                                                                                </Box>
                                                                            )}
                                                                        </Box>
                                                                    </Box>
                                                                </Box>
                                                            ))}

                                                            {/* Add more License button */}
                                                            <Button
                                                                variant="outlined"
                                                                startIcon={<Add />}
                                                                onClick={() =>
                                                                    setAdditionalLicenses(prev => [
                                                                        ...prev,
                                                                        {
                                                                            licenseType: '',
                                                                            licenseNpi: '',
                                                                            licenseState: '',
                                                                            licenseNumber: '',
                                                                            issueDate: '',
                                                                            licenseExpirationDate: '',
                                                                            licenseStatus: 'Active',
                                                                            licenseFileUrl: '',
                                                                        },
                                                                    ])
                                                                }
                                                                sx={{
                                                                    borderColor: '#d1d5db',
                                                                    color: 'grey.700',
                                                                    textTransform: 'none',
                                                                    fontWeight: 500,
                                                                    px: 3,
                                                                    py: 1.5,
                                                                    borderRadius: 2,
                                                                    '&:hover': {
                                                                        borderColor: primaryColor,
                                                                        color: primaryColor,
                                                                        bgcolor: '#f8f9ff',
                                                                    },
                                                                }}
                                                            >
                                                                Add more License
                                                            </Button>
                                                        </Box>
                                                    </Fade>

                                                    {/* Corporate Documents & Clinical Accreditation */}
                                                    <AgencyDocumentsSection
                                                        formData={formData}
                                                        uploadingField={uploadingDocumentField}
                                                        onUpload={(fieldPrefix, file) =>
                                                            handleDocumentUpload(fieldPrefix)(file)
                                                        }
                                                        accentColor={primaryColor}
                                                    />

                                                    {/* Insurance Coverage */}
                                                    <Fade
                                                        in
                                                        timeout={800}
                                                        style={{ transitionDelay: '400ms' }}
                                                    >
                                                        <Box sx={{ mb: 4 }}>
                                                            <Typography
                                                                variant="h6"
                                                                sx={{
                                                                    color: 'grey.800',
                                                                    fontWeight: 600,
                                                                    fontSize: '1.125rem',
                                                                    mb: 3,
                                                                }}
                                                            >
                                                                Insurance Coverage
                                                            </Typography>
                                                            <Grid container spacing={3}>
                                                                <Grid item xs={12} md={6}>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            color: 'grey.700',
                                                                            mb: 1,
                                                                            fontWeight: 500,
                                                                            fontSize: '0.875rem',
                                                                        }}
                                                                    >
                                                                        Professional Liability Insurance
                                                                    </Typography>
                                                                    <TextField
                                                                        fullWidth
                                                                        placeholder="Enter policy number"
                                                                        value={formData.professionalLiabilityInsurance}
                                                                        onChange={handleInputChange(
                                                                            'professionalLiabilityInsurance'
                                                                        )}
                                                                        sx={accountFieldSx}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={6}>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            color: 'grey.700',
                                                                            mb: 1,
                                                                            fontWeight: 500,
                                                                            fontSize: '0.875rem',
                                                                        }}
                                                                    >
                                                                        General Liability Insurance
                                                                    </Typography>
                                                                    <TextField
                                                                        fullWidth
                                                                        placeholder="Enter policy number"
                                                                        value={formData.generalLiabilityInsurance}
                                                                        onChange={handleInputChange(
                                                                            'generalLiabilityInsurance'
                                                                        )}
                                                                        sx={accountFieldSx}
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        </Box>
                                                    </Fade>
                                                </Box>
                                            )}

                                            {/* Step 4: Choose Subscription */}
                                            {currentStep === 3 && (
                                                <ChooseSubscriptionStep
                                                    showSubscriptionDialog={showSubscriptionDialog}
                                                    onCloseDialog={() => setShowSubscriptionDialog(false)}
                                                    onOpenDialog={() => setShowSubscriptionDialog(true)}
                                                    onPlanSelect={handlePlanSelect}
                                                    isProcessingPayment={isProcessingPayment}
                                                    paymentError={paymentError}
                                                    onClearPaymentError={() => setPaymentError(null)}
                                                    subscriptionPaymentSuccess={subscriptionPaymentSuccess}
                                                    onNextAfterPayment={() => moveToStep(4)}
                                                />
                                            )}

                                            {/* Step 5: Connect Bank */}
                                            {currentStep === 4 && (
                                                <ConnectBankStep
                                                    isConnectingBank={isConnectingBank}
                                                    paymentError={paymentError}
                                                    onClearPaymentError={() => setPaymentError(null)}
                                                    onStripeConnect={handleStripeConnect}
                                                />
                                            )}

                                            {/* Placeholder for other steps */}
                                            {currentStep > 4 && (
                                                <Box sx={{ textAlign: 'center', py: 8 }}>
                                                    <Typography variant="h4" sx={{ mb: 2 }}>
                                                        {steps[currentStep]}
                                                    </Typography>
                                                    <Typography variant="body1" color="text.secondary">
                                                        This step will be implemented soon...
                                                    </Typography>
                                                </Box>
                                            )}

                                            <OnboardingNavigation
                                                currentStep={currentStep}
                                                hasAboutYouData={hasAboutYouData}
                                                isProcessingPayment={isProcessingPayment}
                                                onBack={handleBack}
                                                onNext={handleNext}
                                            />
                                        </>
                                    )}
                                </Paper>
                            </Grow>
                        </Box>
                    </Slide>
                </Box>
            </Box>

            {/* Payment Modal */}
            {subscriptionDetails && (
                <AgencyPaymentModal
                    open={showPaymentModal}
                    onClose={() => {
                        setShowPaymentModal(false)
                        setPaymentError(null)
                    }}
                    onPaymentSuccess={handlePaymentSuccess}
                    plan={selectedPlan}
                    billingCycle={billingCycle}
                    discountedPrice={discountedPrice}
                    amount={
                        subscriptionDetails && subscriptionDetails
                            ? parseFloat(
                                subscriptionDetails.client_secret.split('_').pop() || '0'
                            ) ||
                            (selectedPlan
                                ? billingCycle === 'monthly'
                                    ? selectedPlan.monthly_fee
                                    : selectedPlan.anual_fee
                                : 0)
                            : 0
                    }
                    therapistFirstName={formData.firstName}
                    therapistLastName={formData.lastName}
                    therapistEmail={formData.email}
                    clientSecret={subscriptionDetails.client_secret}
                    billingAddress={formData.billingAddressLine}
                    billingCity={formData.billingCity}
                    billingState={formData.billingState}
                    billingZip={formData.billingZipCode}
                />
            )}

            {/* Success Alert */}
            <Snackbar
                open={showSuccessAlert}
                autoHideDuration={2000}
                onClose={() => setShowSuccessAlert(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setShowSuccessAlert(false)}
                    severity="success"
                    sx={{ width: '100%' }}
                >
                    {successMessage || 'Account created successfully!'}
                </Alert>
            </Snackbar>
        </Box>
    )
}

export default AgencySignUp
